/**
 * <xenon-set-builder> — the "Kendi Setini Oluştur" tray.
 *
 * A row of boxes, one per category ("Kedi Tuvaleti", "Mama Kabı", "Su Kabı").
 * Clicking an empty box opens a picker modal for that category; choosing fills
 * the box and it stays filled. A "+ Yeni ürün" tile appends more boxes. At the
 * end, ONE batched /cart/add sends every chosen product as its own cart line
 * carrying a shared `_set_id`, so the set is a real grouping in the cart, the
 * checkout and the order — and so a future discount can be a Shopify Function
 * reading that one key, with no change here.
 *
 * WHY BOXES AND NOT A GRID. The well-known build-your-own-bundle builders put a
 * product grid on the main surface and let slots fill in a side panel. They are
 * all solving "pick any N from one pool" — ten snacks, four coffees, mutually
 * interchangeable. This catalogue is categorical: a litter box, a bowl and a
 * fountain have different candidates and different specs and cannot share a
 * grid. Boxes-first is the right shape for a categorical kit, and the empty box
 * is the thing that says "not finished yet".
 *
 * TWO DELEGATION ROOTS, AND BOTH ARE NECESSARY.
 *   1. `this` — the tray.
 *   2. the picker — BasicModal sets requiresBodyAppended (theme.js:865), so
 *      show() re-parents it to document.body (theme.js:728). Bound to `this` it
 *      would leave the listening subtree the instant it opened and every click
 *      inside it would go nowhere.
 * Neither is `document`: two builders on one page (the theme editor does this)
 * must not answer for each other, and set-item-modal.js already owns a
 * document-level listener that we must not race.
 *
 * There used to be a third — a popover over a filled box offering Değiştir /
 * Detay / Çıkar. It was a menu whose first item was what everyone wanted, so
 * the box now opens the picker directly. Removing costs nothing: the × in the
 * corner already does that, and "detail" was always the comparison table the
 * picker opens onto.
 *
 * INTERACTIVE ROLES ARE ATTACHED HERE, NOT IN LIQUID — a card that announces
 * itself as a button before this file runs is a surface that lies.
 *
 * DATA COMES FROM THE MANIFEST, NOT THE DOM. The section embeds every eligible
 * candidate of every step as JSON (#xset-manifest-<section id>, built by
 * snippets/xenon-set-candidates.liquid). Selections, restore, the "…'den
 * başlıyor" floor, the chooser counts, the advance and the dice all read prices,
 * availability and names from there — so a product without a comparison column
 * on screen can still be restored and priced. The comparison cells are only the
 * CONTROLS (which product/colour/variant the shopper is pointing at); readCard()
 * turns that pointer into data through the manifest, falling back to the cell's
 * own data-* only when the manifest has no entry.
 *
 * SADE MOD. With the section's merdiven_aktif off (the default until a Discount
 * Function honours the ladder in the cart) data-discount-ladder is not emitted,
 * the ladder is empty, and every discount path — tier chips, struck prices,
 * "kazandın", the chooser's "%N indirimli" lead — collapses to "no discount"
 * through tierFor() returning 0. The total is then the plain sum of the
 * products' own prices, which is exactly what the cart will charge.
 *
 * THE STATUS LINE TELLS THE TRUTH (plan İ6). announce() records the tray state
 * its sentence described; renderStatus() replaces the sentence with one derived
 * from the current state the moment the tray moves on without a new sentence —
 * so "Set kuruldu." can no longer outlive the set it reported.
 *
 * URL, STORAGE AND HISTORY (plan §2.2). Restore order: the URL (?set= →
 * ?oneri= → ?bas=) → localStorage (7 days; sessionStorage is wiped when an
 * Instagram/WhatsApp in-app tab closes) → an empty tray. Every incoming value is
 * checked against the manifest and the set limits, and whatever was dropped is
 * said in the notice band in the plan's own words. The tray's shareable state
 * is written back as ?set=<adım>.<varyant>~… with replaceState — and ONLY onto
 * the base history entry: the picker pushes an entry of its own when it opens
 * and every way of closing it (pick, ✕, Esc, overlay) goes back() over it, so
 * five open/pick cycles still leave one entry and one back press leaves the
 * page. A back press with the picker open closes it without a pick. A write
 * that comes due while the picker's entry is on top waits for the popstate.
 *
 * THE CTA IS A STATE MACHINE (plan §4.1): BOŞ / EKSİK open the step that is
 * missing (never a dead "En az 2 ürün seçin"), HAZIR adds, SORUNLU scrolls to
 * the sold-out box, EKLENİYOR holds its width, SEPETTE ("Sepette ✓") opens the
 * cart — the same unchanged set is never added twice — and GÜNCELLE replaces
 * the cart's copy of this set once the tray has changed. Which one applies is
 * derived from the tray and from /cart.js lines carrying this page's _set_id;
 * the tray is NOT emptied after an add.
 *
 * ONE VOICE, ONE EVENT STREAM, ONE MOVE (plan §10–11). Everything a screen
 * reader hears goes through speak() into [data-xset-live], merged per action
 * (400 ms); the visible status/notice/match lines are not live regions. Every
 * action publishes an xset_* event (track(): Shopify.analytics.publish,
 * dataLayer, `xset:track`). The picker is parked under <body> once, at idle
 * (parkPicker), so the theme's per-open re-parenting — and the third-party
 * DOM observers it fed — is gone from the tap.
 *
 * KNOWN AND INTENDED COST: a cart line carrying line-item properties is its own
 * line (the cost complementary-look.js:468 records for `_birlikte_al`). The same
 * product added here and from its own page shows twice, and two sets built in
 * one session never merge because their `_set_id` differs. That is the feature.
 */
(function () {
  'use strict';

  if (customElements.get('xenon-set-builder')) return;

  // 3: localStorage with a timestamp, and each pick remembers how it got into
  // the box (`kaynak`). A v2 record lived in sessionStorage and is not read.
  var STORAGE_VERSION = 3;
  var STORAGE_TTL = 7 * 24 * 60 * 60 * 1000;
  // Set limits of plan §3.2 the incoming-link sanitiser applies: lines per set,
  // lines per product, sets of one family in one cart.
  // A family may set its own (custom.set_adimlari "sinirlar", read in
  // connectedCallback): a page holds one builder, so these are that page's.
  var MAX_LINES = 6;
  var MAX_PER_PRODUCT = 2;
  var MAX_SETS = 2;
  // …and per category: a tray (skeleton) step holds at most 2 boxes, and the
  // steps off the tray (sarf + ekstra) at most 3 between them (plan §3.2). A
  // step's own `max` in custom.set_adimlari overrides the per-step figure.
  var MAX_PER_STEP = 2;
  var MAX_EXTRAS = 3;

  // The filled box's visible "Değiştir" cue — the same markup as
  // snippets/xenon-set-slot.liquid, for the boxes createExtraSlot() builds.
  var SLOT_CHANGE_HTML =
    '<span class="xset-slot__change" aria-hidden="true"><svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M3 5.5h9.5M10 3l2.5 2.5L10 8M13 10.5H3.5M6 8l-2.5 2.5L6 13"/></svg>Değiştir</span>';
  // A shared link is a string anyone can type; nobody needs more tokens than this.
  var MAX_LINK_TOKENS = 24;

  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* private mode, quota, storage disabled — state is a convenience, never a precondition */
    }
  }

  function storageRemove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      /* nothing to do */
    }
  }

  function money(cents) {
    var fmt = window.FoxTheme && FoxTheme.settings && FoxTheme.settings.moneyFormat;
    if (window.FoxTheme && FoxTheme.Currency && FoxTheme.Currency.formatMoney) {
      return FoxTheme.Currency.formatMoney(cents, fmt);
    }
    return (cents / 100).toFixed(2);
  }

  /* Turkish vowel harmony on the spoken numeral. The same table
     snippets/xenon-product-set-info.liquid:239 uses for the SET pill, in
     sentence case — so a set built here is named the way every ready-made set
     in this catalogue is named, and the two never disagree. Past 10 the bare
     word is the right answer; "12'li Set" is not something anyone says. */
  var SET_SIZES = ["2'li", "3'lü", "4'lü", "5'li", "6'lı", "7'li", "8'li", "9'lu", "10'lu"];

  function sizeLabel(n) {
    var word = SET_SIZES[n - 2];
    return word ? word + ' Set' : 'Set';
  }

  /* This catalogue titles products "Name | spec, spec, spec (SKU)" — the pipe is
     the store's own separator, consistently, across every candidate in every
     step (and xenon-fold-key.liquid already treats it as punctuation). The part
     before it is the product's short name, and it is what the ready-made set
     titles in this store are built from: without this, naming a three-piece set
     produced a 300-character sentence of spec lists. A custom.kart_basligi,
     where a merchant has written one, carries no pipe and passes through
     untouched. */
  function shortTitle(title) {
    var full = String(title || '').trim();
    return full.split('|')[0].trim() || full;
  }

  /* The name a selection is spoken and captioned by: custom.kisa_ad when the
     merchant wrote one (the manifest carries it as kisaAd), else the short
     title — which is what every caption used before kisa_ad existed. */
  function nameOf(sel) {
    if (!sel) return '';
    return sel.kisaAd || shortTitle(sel.title);
  }

  /* "2:5, 3:15, 4:20, 5:25" -> [{pos:2,pct:5}, …], sorted.

     Position is the slot's place in the TRAY, not its price and not the order
     the shopper picked things in. That is the merchant's own ordering — the
     steps as they are configured — so the same set always costs the same, and
     nobody can shuffle their way into a bigger discount. */
  function parseLadder(raw) {
    var out = [];
    String(raw || '')
      .split(',')
      .forEach(function (part) {
        var bits = part.split(':');
        if (bits.length !== 2) return;
        var pos = parseInt(bits[0], 10);
        var pct = parseFloat(bits[1]);
        if (!(pos > 0) || !(pct > 0)) return;
        // Clamped, because "yuzde kac" invites a merchant to type a lira amount.
        // Unclamped, "3:150" produced a negative basket total, a negative price
        // in every box and a struck-through was-price BELOW the current one.
        // The Liquid half already guards the same case for tag discounts
        // (xenon-set-compare.liquid: col_tag_discount > 0 and < 100).
        if (pct > 100) pct = 100;
        out.push({ pos: pos, pct: pct });
      });
    out.sort(function (a, b) {
      return a.pos - b.pos;
    });
    return out;
  }

  /* "Kedi Tuvaleti" → "Kedi tuvaleti": a step title inside a sentence ("Kedi
     tuvaleti seç →", plan §4.1). Turkish-aware, so "I" becomes "ı". The
     section's server-rendered CTA does the same in Liquid. */
  function phrase(label) {
    var s = String(label || '').trim();
    if (!s) return s;
    var rest = s.slice(1);
    try {
      rest = rest.toLocaleLowerCase('tr-TR');
    } catch (e) {
      rest = rest.toLowerCase();
    }
    return s.charAt(0) + rest;
  }

  /* "15:00'e", "09:00'a", "12:00'ye", "12:30'a" — the Istanbul cutoff as a
     clock time with the dative suffix its spoken form takes. From the
     helper's cutoffHour; an older cached helper has only secondsUntilCutoff,
     which gives the same time back from the current Istanbul clock. */
  var DATIVE_ONES = { 1: "'e", 2: "'ye", 3: "'e", 4: "'e", 5: "'e", 6: "'ya", 7: "'ye", 8: "'e", 9: "'a" };
  var DATIVE_TENS = { 0: "'a", 1: "'a", 2: "'ye", 3: "'a", 4: "'a", 5: "'ye" };
  function dative(n) {
    return n % 10 ? DATIVE_ONES[n % 10] : DATIVE_TENS[Math.floor(n / 10)];
  }
  function cutoffLabel(cutoffHour, secondsLeft, now) {
    var minutes = null;
    if (typeof cutoffHour === 'number' && isFinite(cutoffHour)) {
      minutes = Math.round(cutoffHour * 60);
    } else if (typeof secondsLeft === 'number') {
      try {
        var parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/Istanbul',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23',
        }).formatToParts(now);
        var get = function (type) {
          var part = parts.find(function (p) {
            return p.type === type;
          });
          return part ? Number(part.value) : 0;
        };
        var nowSeconds = get('hour') * 3600 + get('minute') * 60 + get('second');
        minutes = Math.round((nowSeconds + secondsLeft) / 60);
      } catch (e) {
        return '';
      }
    }
    if (minutes === null || minutes <= 0 || minutes >= 24 * 60) return '';
    var h = Math.floor(minutes / 60);
    var m = minutes % 60;
    var label = (h < 10 ? '0' : '') + h + ':' + (m < 10 ? '0' : '') + m;
    return label + (m ? dative(m) : dative(h));
  }

  /* The phone's one-line version of a not-today dispatch: "Yarın kargoda",
     "Pzt kargoda", or "12 Mar kargoda" past a week — the long line wraps
     to three lines inside a 48px pill. Built from the helper's dispatchDate;
     '' without it (the caller then uses the long line). */
  var WEEKDAY_SHORT = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
  var MONTH_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  function shortDispatch(result, now) {
    if (!result.dispatchDate) return '';
    var today;
    try {
      // en-CA formats as YYYY-MM-DD.
      today = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Istanbul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(now);
    } catch (e) {
      return '';
    }
    var target = Date.parse(result.dispatchDate);
    var days = Math.round((target - Date.parse(today)) / 86400000);
    if (!(days > 0)) return '';
    var d = new Date(target);
    if (days >= 7) return d.getUTCDate() + ' ' + MONTH_SHORT[d.getUTCMonth()] + ' kargoda';
    // "yarın" only where the shared long line says it too.
    if (/yarın/.test(result.deadlineText || '')) return 'Yarın kargoda';
    return WEEKDAY_SHORT[d.getUTCDay()] + ' kargoda';
  }

  function reducedMotion() {
    return !!(window.FoxTheme && FoxTheme.config && FoxTheme.config.motionReduced);
  }

  /* WRITE ONLY WHAT CHANGED (plan §10, performance). This page carries
     Clarity, Yandex Webvisor and Loox, and all three watch the DOM with
     MutationObservers that serialise every record in a microtask. Writing a
     value that is already there still queues a record — setAttribute,
     textContent and the `hidden` reflection all do — so a picker opening
     that re-wrote every card produced hundreds of records and ~30 ms of
     third-party work (4× CPU) between the tap and the sheet's first frame.
     These four compare first. */
  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function setHidden(el, hide) {
    if (el && el.hidden !== !!hide) el.hidden = !!hide;
  }

  function setAttr(el, name, value) {
    if (el && el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  function dropAttr(el, name) {
    if (el && el.hasAttribute(name)) el.removeAttribute(name);
  }

  /* Kuruş → lira, for analytics payloads: "toplam: 29297" rather than a
     formatted string, so the pixel can sum it. */
  function lira(cents) {
    return Math.round(cents || 0) / 100;
  }

  /* ------------------------------------------- teknik özellikler (specs)

     THE COMPARISON ROWS COME FROM THE SAME DATA THE PRODUCT PAGE'S "Teknik
     Özellikler" TAB SHOWS: teknik_liste.json on the CDN (templates/product.json
     → tab_8drBhd), one record per SKU, `teknik_detay` a JSON string of
     {product_type: <name>, quantity: <value>} pairs. Owner's request: compare
     the models on what was actually entered for them, not on a hand-kept
     custom.set_karsilastirma bag.

     Fetched once per page (34 kB gzipped) and only as an enhancement: until it
     arrives — or if the CDN is down — the server-rendered rows stay, so the
     table never depends on a third host to exist.

     NAMES ARE NOT CONSISTENT across products ("Ağırlık", "Ağırlık (Net/Brüt)",
     "Net Ağırlık", "Cihaz Ağırlığı"; double spaces; "Öğün Başına Porsiyon :
     Sayısı"). A key is normalised (Turkish lower case, one space, no space
     around / or :) and then mapped through TEKNIK_GROUPS, so one fact is one
     row. A group has no fixed label: the row is named by the spelling most
     columns of THIS table use, because "Hacim" is a water tank on a fountain
     and a kettle's volume on a kettle. */
  var TEKNIK_SKIP = ['ürün adı', 'adı', 'ürün kodu', 'model', 'model no', 'model numarası', 'marka', 'renk', 'aksesuar', 'kutu içeriği', 'paket içeriği', 'barkod'];

  // Each list is one fact. The first entry is only the group's id.
  var TEKNIK_GROUPS = [
    ['ağırlık', 'ağırlık (net/brüt)', 'ağırlık (net)', 'net ağırlık', 'cihaz ağırlığı', 'cihaz ağırlığı (net/brüt)', 'ürün ağırlığı'],
    ['boyutlar', 'boyut', 'ürün boyutları', 'ürün boyutu'],
    ['malzeme', 'materyaller', 'materyal'],
    ['bağlantı', 'ağ bağlantısı', 'bağlantı türü', 'bağlantısı'],
    ['kablo uzunluğu', 'güç kablosu uzunluğu', 'adaptör kablo uzunluğu'],
    ['güç girişi', 'güç kaynağı'],
    ['güç', 'nominal güç', 'anma gücü'],
    ['güç tüketimi', 'kamera güç tüketimi', 'maksimum güç tüketimi'],
    ['maksimum güç', 'maks. güç'],
    ['mobil uygulama', 'desteklenen platform'],
    ['pil', 'pil türü', 'pil tipi', 'batarya', 'batarya tipi', 'batarya türü', 'yedek güç kaynağı', 'yedek güç', 'dahili batarya'],
    ['pil kullanım süresi', 'pil ömrü', 'batarya ömrü', 'pil çalışma süresi', 'yedek güç çalışma süresi'],
    ['şarj süresi', 'şarj olma süresi'],
    ['gürültü seviyesi', 'gürültü', 'çalışma ses seviyesi', 'ses seviyesi'],
    ['ipx koruma sınıfı', 'koruma sınıfı'],
    ['çalışma nem oranı', 'çalışma nemi'],
    ['su tankı kapasitesi', 'hacim', 'su haznesi kapasitesi', 'su kabı hacmi', 'su deposu', 'su deposu kapasitesi'],
    // Window robots write each of these two or three ways.
    ['emniyet halatı uzunluğu', 'güvenlik halatı uzunluğu', 'emniyet halatı'],
    ['maksimum temizleme alanı', 'maksimum temizlik alanı', 'temizlik alanı', 'temizleme alanı'],
    ['temizleme hızı', 'ortalama temizlik hızı', 'temizlik hızı'],
    ['güç adaptörü çıkışı', 'adaptör güç çıkışı', 'adaptör çıkışı'],
    // Feeders write their hopper as either; elsewhere "Kapasite" (a power
    // bank's mAh) still reads "Kapasite" — the row takes the table's majority.
    ['mama haznesi hacmi', 'mama haznesi kapasitesi', 'mama kapasitesi', 'kapasite'],
    ['mama tipi', 'mama türü', 'uygun mama türü', 'mama tipi/boyutu'],
    ['mama boyutu', 'uygun mama boyutu', 'mama boyutu uyumluluğu'],
    ['porsiyon miktarı', '1 porsiyon miktarı'],
    ['günlük otomatik öğün sayısı', 'günlük maksimum:otomatik öğün sayısı', 'otomatik günlük öğün sayısı'],
    ['öğün başına porsiyon sayısı', 'öğün başına porsiyon:sayısı'],
    ['çift yönlü sesli iletişim', 'sesli iletişim'],
    ['anlık bildirimler', 'anlık bildirim', 'bildirim özelliği'],
    ['besleme kaydı', 'besleme kayıtları'],
    ['sıkışma algılama', 'mama sıkışma algılama'],
    ['kontrol paneli', 'dokunmatik kontrol paneli', 'kontrol ve gösterge paneli'],
    ['pir algılama mesafesi', 'pir algılama aralığı'],
    ['uygun kedi ağırlığı', 'maksimum kedi ağırlığı'],
    ['sterilizasyon', 'uv sterilizasyon']
  ];

  // What a shopper decides on comes first; what every model shares in the
  // same words (operating humidity, pairing mode) sinks. Group ids or keys.
  var TEKNIK_FIRST = [
    'kum haznesi hacmi', 'atık haznesi hacmi', 'maksimum kum kapasitesi', 'uygun kum tipi', 'uygun kedi ağırlığı',
    'mama haznesi hacmi', 'su tankı kapasitesi', 'kamera', 'kamera görüş açısı', 'gece görüşü',
    'vakum gücü', 'emiş gücü', 'temizleme hızı', 'maksimum temizleme alanı', 'emniyet halatı uzunluğu',
    'çift yönlü sesli iletişim', 'günlük otomatik öğün sayısı', 'porsiyon miktarı', 'öğün başına porsiyon sayısı',
    'mama tipi', 'mama boyutu', 'filtre', 'sterilizasyon', 'pil', 'pil kullanım süresi', 'şarj süresi',
    'gürültü seviyesi', 'bağlantı', 'mobil uygulama', 'boyutlar', 'ağırlık', 'malzeme'
  ];
  var TEKNIK_LAST = [
    'çalışma sıcaklığı', 'çalışma nem oranı', 'saklama sıcaklığı', 'saklama nem oranı', 'eşleşme modu',
    'kullanım alanı', 'çalışma gerilimi', 'çalışma akımı', 'bekleme akımı', 'bekleme gücü'
  ];

  function teknikNorm(name) {
    return (
      String(name || '')
        .toLocaleLowerCase('tr-TR')
        .replace(/\s+/g, ' ')
        .replace(/\s*([\/:])\s*/g, '$1')
        .replace(/[:\s]+$/, '')
        .trim()
        // "Bebek Kamerası - Ağırlık": the camera's own rows carry its name
        // (a monitor's rows keep theirs, so the two never merge).
        .replace(/^(bebek kamerası|kamera) ?- ?/, '')
        // "IPX" lower-cased the Turkish way is "ıpx": one spelling for keys.
        .replace(/ı/g, 'i')
    );
  }

  var TEKNIK_GROUP_OF = (function () {
    var map = Object.create(null);
    TEKNIK_GROUPS.forEach(function (list) {
      list.forEach(function (name) {
        map[teknikNorm(name)] = teknikNorm(list[0]);
      });
    });
    return map;
  })();

  var TEKNIK_SKIP_SET = (function () {
    var map = Object.create(null);
    TEKNIK_SKIP.forEach(function (name) {
      map[teknikNorm(name)] = true;
    });
    return map;
  })();

  // The order lists are written in plain Turkish; keys are teknikNorm'ed
  // (dotless ı folded), so they are compared in the same form.
  var TEKNIK_FIRST_KEYS = TEKNIK_FIRST.map(teknikNorm);
  var TEKNIK_LAST_KEYS = TEKNIK_LAST.map(teknikNorm);

  function teknikRank(group) {
    var first = TEKNIK_FIRST_KEYS.indexOf(group);
    if (first !== -1) return first;
    var last = TEKNIK_LAST_KEYS.indexOf(group);
    return last !== -1 ? 2000 + last : 1000;
  }

  // What "Sadece farklar" compares: "72 L" and "72L", "2,8 L" and "2.8l" are
  // one answer. Display keeps the record's own spelling.
  function teknikSig(value) {
    return String(value || '')
      .toLocaleLowerCase('tr-TR')
      .replace(/\s+/g, '')
      .replace(/,/g, '.');
  }

  var teknikCache = Object.create(null);

  /* url → Promise<Map<sku, Array<{group, label, value}>>>. One request per
     page however many builders or panels ask; a failure is cached too, so a
     dead CDN costs one attempt, not one per picker opening. */
  function loadTeknik(url) {
    if (teknikCache[url]) return teknikCache[url];
    teknikCache[url] = fetch(url, { credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('teknik ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var index = new Map();
        ((data && data.liste) || []).forEach(function (record) {
          var sku = String(record && record.sku ? record.sku : '').trim().toUpperCase();
          if (!sku || index.has(sku)) return;
          var pairs;
          try {
            pairs = JSON.parse(record.teknik_detay || '[]');
          } catch (e) {
            return;
          }
          if (!Array.isArray(pairs)) return;
          var specs = [];
          var seen = Object.create(null);
          pairs.forEach(function (pair) {
            var label = String((pair && pair.product_type) || '').replace(/\s+/g, ' ').trim();
            var value = String((pair && pair.quantity) || '').replace(/\s+/g, ' ').trim();
            var key = teknikNorm(label);
            if (!key || !value || TEKNIK_SKIP_SET[key]) return;
            var group = TEKNIK_GROUP_OF[key] || key;
            // "Ağırlık" and "Ağırlık (Net/Brüt)" on one record: the first said it.
            if (seen[group]) return;
            seen[group] = true;
            specs.push({
              group: group,
              label: label.replace(/\s*:\s*/g, ' ').replace(/^(Bebek Kamerası|Kamera)\s*-\s*/i, ''),
              value: value,
            });
          });
          if (specs.length) index.set(sku, specs);
        });
        return index;
      });
    return teknikCache[url];
  }

  customElements.define(
    'xenon-set-builder',
    class XenonSetBuilder extends HTMLElement {
      connectedCallback() {
        // A sentinel, not a one-shot guard: connectedCallback runs again on every
        // theme-editor section re-render and on any DOM move.
        if (this.hasAttribute('data-bound')) return;

        this.trayEl = this.querySelector('[data-xset-tray]');
        if (!this.trayEl) {
          this.classList.remove('is-restoring');
          return;
        }
        this.setAttribute('data-bound', '');

        var sectionRoot = this.closest('.shopify-section') || document;
        this.picker = document.getElementById('XsetPicker-' + this.dataset.sectionId);

        this.statusEl = this.querySelector('[data-xset-status]');
        // The one polite live region (speak()). Quiet until connect is done.
        this.liveEl = this.querySelector('[data-xset-live]');
        this.quiet = true;
        this.countEl = this.querySelector('[data-xset-count]');
        this.totalEl = this.querySelector('[data-xset-total]');
        this.submitEl = this.querySelector('[data-xset-submit]');
        this.errorEl = this.querySelector('[data-xset-error]');
        this.summaryEl = this.querySelector('[data-xset-summary]');
        this.addTileEl = this.querySelector('[data-xset-add-slot]');
        this.stepsEl = this.querySelector('[data-xset-steps]');
        // The delivery pill (plan §8.1) and its footnote under the CTA.
        this.promiseEl = this.querySelector('[data-xset-promise]');
        this.promiseLongEl = this.querySelector('[data-xset-promise-long]');
        this.promiseShortEl = this.querySelector('[data-xset-promise-short]');
        this.promiseFreeEl = this.querySelector('[data-xset-promise-free]');
        this.trustEl = this.querySelector('[data-xset-trust]');
        this.freeShippingMin = parseInt(this.dataset.freeShippingMin, 10) || 0;
        this.holidays =
          window.ShippingPromise && typeof ShippingPromise.parseHolidays === 'function'
            ? ShippingPromise.parseHolidays(this.dataset.tatil || '')
            : null;
        this.rewardEl = this.querySelector('[data-xset-reward]');
        this.rewardBadgeEl = this.querySelector('[data-xset-reward-badge]');
        this.rewardTextEl = this.querySelector('[data-xset-reward-text]');
        this.wasEl = this.querySelector('[data-xset-was]');
        this.randomEl = this.querySelector('[data-xset-random]');
        this.resetEl = this.querySelector('[data-xset-reset]');
        this.matchEl = this.querySelector('[data-xset-match]');
        this.nearestEl = this.querySelector('[data-xset-nearest]');
        this.compatEl = this.querySelector('[data-xset-compat]');
        this.priceNoteEl = this.querySelector('[data-xset-price-note]');
        this.priceNoteText = this.priceNoteEl ? this.priceNoteEl.textContent.trim() : '';
        this.readyEl = this.querySelector('[data-xset-ready]');
        this.readyTrack = this.querySelector('[data-xset-ready-track]');
        this.readyNav = this.querySelector('[data-xset-ready-nav]');
        this.noticeEl = this.querySelector('[data-xset-notice]');
        this.ctaLabelEl = this.querySelector('[data-xset-cta-label]');
        this.ctaPriceEl = this.querySelector('[data-xset-cta-price]');
        // The page's own Otomatik button, and its copy in the mobile bar. The
        // copy is shown only while the original is off screen (plan §7).
        this.pageRandomEl = this.querySelector('[data-xset-random]:not([data-xset-random-bar])');
        this.barRandomEl = this.querySelector('[data-xset-random-bar]');
        // The desktop's thin sticky bar (plan §4.1, "Masaüstüne ince bir
        // yapışkan bar eklenir"): a copy of the CTA that follows the shopper
        // while the page's own one is off screen. See observeDesktopBar.
        this.dbarEl = this.querySelector('[data-xset-dbar]');
        this.dbarBtn = this.querySelector('[data-xset-submit-mirror]');
        this.dbarLabelEl = this.querySelector('[data-xset-dbar-label]');
        this.dbarPriceEl = this.querySelector('[data-xset-dbar-price]');
        this.dbarInfoEl = this.querySelector('[data-xset-dbar-info]');
        this.dbarThumbsEl = this.querySelector('[data-xset-dbar-thumbs]');
        this.trayWrapEl = this.querySelector('[data-xset-tray-wrap]');
        // The notice band's place under the status line, so placeNotice() can
        // put it back after a phone moved it under a filled tray.
        if (this.noticeEl) {
          this.noticeHomeParent = this.noticeEl.parentNode;
          this.noticeHomeNext = this.noticeEl.nextElementSibling;
        }
        var addLabel = this.addTileEl && this.addTileEl.querySelector('.xset-slot__label');
        this.addLabelText = addLabel ? addLabel.textContent.trim() : 'Yeni ürün';

        this.pickerTitleEl = this.picker && this.picker.querySelector('[data-xset-picker-title]');
        this.pickerNoteEl = this.picker && this.picker.querySelector('[data-xset-picker-note]');
        // Editing a filled box: "Şu an setinde: …" and "Setten çıkar" (plan
        // §4.2 F, modal eki 5).
        this.pickerSubEl = this.picker && this.picker.querySelector('[data-xset-picker-sub]');
        this.pickerRemoveEl = this.picker && this.picker.querySelector('[data-xset-picker-remove]');
        this.statusMoreEl = this.querySelector('[data-xset-status-more]');
        // Otomatik Set (plan §7): its "Geri al" / "Hepsini yeniden öner" and
        // the suggestion strip.
        this.autoActionsEl = this.querySelector('[data-xset-auto-actions]');
        this.autoNextEl = this.querySelector('[data-xset-auto-next]');
        this.stripEl = this.querySelector('[data-xset-strip]');
        this.autoUsed = false;
        this.autoShown = [];
        this.autoUndo = null;
        this.lastPresetId = null;
        this.autoManualSig = '';
        // "Sadece farklar" (modal eki 3): on by default, and one answer for
        // every step of this page view — switching it off in one step and
        // finding it back on in the next would read as the page forgetting.
        this.onlyDiff = true;
        // Spec rows from the product page's "Teknik Özellikler" data (see
        // loadTeknik). Empty URL = the section turned the feature off.
        this.teknikUrl = (this.dataset.teknikUrl || '').trim();
        this.teknikRows = parseInt(this.dataset.teknikRows, 10) || 12;

        this.setLabel = this.dataset.setLabel || '';
        // "Kedi" — names the family in the cart sentences (plan §4.1, §4.2 K).
        this.familyName = (this.dataset.familyName || '').trim();
        // Sade mod: no ladder at all unless the section explicitly turned it on.
        // The attribute is not even emitted then, but the flag is checked too so
        // a stale cached ladder can never resurrect discount copy.
        this.ladderOn = this.dataset.merdiven === 'true';
        this.ladder = this.ladderOn ? parseLadder(this.dataset.discountLadder) : [];
        this.loadManifest();
        this.loadReadySets();
        this.loadPresets();
        this.minPieces = parseInt(this.dataset.minPieces, 10) || 2;
        this.targetPieces = parseInt(this.dataset.targetPieces, 10) || 5;
        // The family's own set limits (security: three cameras of one model).
        var limit = function (v) {
          var n = parseInt(v, 10);
          return n > 0 && n < 50 ? n : 0;
        };
        if (limit(this.dataset.maxLines)) MAX_LINES = limit(this.dataset.maxLines);
        if (limit(this.dataset.maxPerProduct)) MAX_PER_PRODUCT = limit(this.dataset.maxPerProduct);
        if (limit(this.dataset.maxPerStep)) MAX_PER_STEP = limit(this.dataset.maxPerStep);
        if (limit(this.dataset.maxExtras)) MAX_EXTRAS = limit(this.dataset.maxExtras);
        this.compatOn = this.dataset.compat === 'true';
        // A family whose set IS its boxes (Çeyiz: robot + air/floor + two
        // complements): the CTA waits for every box, not for a piece count —
        // a box whose products all sold out is not drawn, so it never blocks.
        this.requireAll = this.dataset.requireAll === 'true';
        // The ready state's label, when the family names it ("Setimi Oluştur").
        this.ctaText = (this.dataset.ctaText || '').trim();
        // Two colours of one model count as one product against the
        // per-product cap (Çeyiz: a green and a cream kettle are not two
        // complements). Off elsewhere: three security cameras may mix colours.
        this.colourCap = this.dataset.colourCap === 'true';
        this.storageKey = 'xenon-set:' + this.dataset.productId;
        // Every set this page ever puts in a cart carries an id with this
        // prefix (makeSetId), which is how /cart.js lines are told apart from
        // the same products added anywhere else.
        this.setIdPrefix = 'xsb-' + this.dataset.productId + '-';
        // History and URL writes are for a shopper's browser. The theme editor
        // is an iframe the merchant drives; nothing there may push entries.
        this.historyOn = !(window.Shopify && window.Shopify.designMode) && !!(window.history && history.replaceState);
        this.modalEntry = false;
        this.closing = false;
        this.urlDirty = false;
        this.cart = { loaded: false, sets: {}, order: [] };
        this.altSaved = null;
        this.submitting = false;
        this.statusMsg = null;
        this.statusSig = '';
        this.activeSlot = null;
        this.pendingExtraCategory = null;
        this.extraSeq = 0;

        // Auto-advance state. `advanceDismissed` is the one that matters: it is
        // how the shopper says "stop pushing me", and nothing clears it except
        // starting over. Deliberately NOT persisted — a reload is a new visit
        // and the feature gets another chance.
        this.autoAdvance = this.dataset.autoAdvance === 'true';
        this.advanceDismissed = false;
        this.autoOpened = false;
        this.committedSinceAutoOpen = false;
        this.advanceToken = 0;
        this.advanceTimer = null;
        this.pendingFill = null;
        this.hydrating = false;

        var self = this;
        this.state = {
          setId: this.makeSetId(),
          slots: Array.prototype.map.call(this.trayEl.querySelectorAll('[data-xset-slot]'), function (el) {
            return {
              el: el,
              uid: el.dataset.slotUid,
              categoryKey: el.dataset.categoryKey,
              label: el.dataset.categoryTitle,
              isExtra: false,
              selection: null,
            };
          }),
        };

        // The tick badge is authored in xenon-set-slot.liquid (it needs
        // {% render 'icon-check' %}), so a browser-built extra slot borrows the
        // markup from a default one rather than hand-inlining an SVG that would
        // then have to be kept in step with the snippet.
        var sampleCheck = this.querySelector('[data-xset-slot-check]');
        this.checkHTML = sampleCheck ? sampleCheck.outerHTML : '';
        // …and its × glyph, for the same reason: an extra box's remove
        // button was built empty and rendered as a blank circle.
        var sampleRemove = this.querySelector('[data-xset-slot-remove]');
        this.removeIconHTML = sampleRemove ? sampleRemove.innerHTML : '';

        // Before prepareCards() and hydrate(): the extras list's rows are
        // cards like any comparison column, and a restored extra looks its
        // card up by handle.
        this.buildExtraLists();
        this.prepareCards();
        this.prepareGhosts();
        this.carouselDeferred = true;
        this.hydrating = true;
        // A link or a stored record this code did not foresee must never cost
        // the page its handlers (everything below binds them): whatever it
        // half-applied is cleared and the builder starts from an empty tray.
        try {
          this.hydrate();
        } catch (hydrateError) {
          this.recoverFromBadState();
        }
        this.hydrating = false;

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
        // The specs are wanted the moment a box is opened. Fetched when the
        // shopper first reaches for the builder, or a few seconds in, so the
        // first picker usually opens with them already parsed.
        if (this.teknikUrl) {
          var teknikUrl = this.teknikUrl;
          var prefetchTeknik = function () {
            loadTeknik(teknikUrl).catch(function () {});
          };
          this.addEventListener('pointerover', prefetchTeknik, { once: true });
          this.addEventListener('focusin', prefetchTeknik, { once: true });
          this.teknikTimer = setTimeout(prefetchTeknik, 4000);
        }
        if (this.picker) {
          this.picker.addEventListener('click', this.onClick.bind(this));
          this.picker.addEventListener('keydown', this.onKeydown.bind(this));
        }
        if (this.picker) {
          // Once per frame, not once per scroll event: it reads layout
          // (scrollWidth, getBoundingClientRect), and a swipe through the
          // table fires dozens of these.
          var scrollQueued = false;
          this.picker.addEventListener(
            'scroll',
            function () {
              if (scrollQueued) return;
              scrollQueued = true;
              requestAnimationFrame(function () {
                scrollQueued = false;
                self.syncScrollAffordance();
              });
            },
            { capture: true, passive: true }
          );
          // The real hook. showPanel's own rAF fires while the modal is still
          // display:none mid-open, so scrollWidth equals clientWidth and the
          // arrows decide there is nothing to scroll to — on a table twice the
          // width of the viewport. ModalComponent announces when it has finished
          // showing (theme.js:816); measure then.
          this.picker.addEventListener('modal:handleAfterShow', this.syncScrollAffordance.bind(this));
          this.picker.addEventListener('drawer:handleAfterShow', this.syncScrollAffordance.bind(this));

          // The close side, which nothing subscribed to before. Two names for
          // the same hedge as above: BasicModal emits modal:*, DrawerComponent
          // emits drawer:* (theme.js:620, 884), and swapping one for the other
          // in the snippet must not silently drop the feedback.
          this.picker.addEventListener('modal:handleAfterHide', this.onPickerHidden.bind(this));
          this.picker.addEventListener('drawer:handleAfterHide', this.onPickerHidden.bind(this));

          // See wrapPickerTab: the theme's trap cannot hold this modal, because
          // every category panel stays in the DOM and only one is visible.
          this.onPickerKeydown = this.wrapPickerTab.bind(this);
          this.picker.addEventListener('keydown', this.onPickerKeydown, true);

          // The history half of the picker. ModalComponent dispatches a
          // bubbling `toggle` synchronously from attributeChangedCallback on
          // every open/close (theme.js:752), whichever route closed it — a
          // pick, the ✕, Esc on document, the overlay — so this one hook sees
          // them all. The native <details> toggle inside the picker does not
          // bubble, and is filtered by target anyway.
          this.picker.addEventListener('toggle', this.onPickerToggle.bind(this));

          // "Sadece farklar" is a native checkbox; its state arrives as change.
          this.picker.addEventListener('change', function (event) {
            if (event.target.matches && event.target.matches('[data-xset-diff-toggle]')) {
              self.onlyDiff = !!event.target.checked;
              var panel = event.target.closest('[data-xset-panel]');
              if (panel) self.applyDiff(panel);
              self.measureHead(panel);
            }
          });
        }

        this.parkPicker();

        if (this.historyOn) {
          this.onPopState = this.handlePopState.bind(this);
          window.addEventListener('popstate', this.onPopState);
        }
        // bfcache: the page comes back exactly as it was left — possibly with
        // the picker open over an entry that is no longer on top, and with a
        // cart that changed on another page. Re-read both (plan §2.2 step 5).
        this.onPageShow = function (event) {
          if (event.persisted) self.resumeFromCache();
        };
        window.addEventListener('pageshow', this.onPageShow);

        // Subscribe only. PUB_SUB_EVENTS is a closed enum in theme.js:271 and it
        // is vendor code the Foxtheme updater overwrites, so this file never adds
        // a key to it; anything outward-facing goes out as a bubbling CustomEvent.
        // A cart change anywhere (drawer remove, quantity) can take this set out
        // of the cart, which moves the CTA off "Sepette ✓".
        if (window.FoxTheme && FoxTheme.pubsub) {
          this.unsubscribeCart = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (event) {
            var cart = event && event.cart;
            if (cart && Array.isArray(cart.items)) self.applyCart(cart);
            else self.refreshCart();
          });
        }

        if (this.readyTrack) {
          this.readyTrack.addEventListener('scroll', this.syncReadyNav.bind(this), { passive: true });
        }

        this.renderFacts();
        this.render();
        // The tray is the real one now: show the panel the section's inline
        // script hid for a restore (CLS).
        this.classList.remove('is-restoring');
        var fillCarousel = function () {
          if (!self.carouselDeferred) return;
          self.carouselDeferred = false;
          self.renderReadyCarousel();
          self.syncReadyNav();
        };
        if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(fillCarousel, { timeout: 1500 });
        else setTimeout(fillCarousel, 300);
        this.quiet = false;
        this.track('xset_view', {
          giris: this.entry || 'dogrudan',
          geri_yuklendi: this.pieceCount() > 0,
          adim_sayisi: this.defaultSlots().length,
        });
        // The pill names a cutoff ("Bugün 15:00'e kadar…"), so it has to
        // turn over when the clock passes it, not only when the tray moves. A
        // minute is fine-grained enough; a tab coming back re-reads at once.
        this.promiseTimer = setInterval(this.renderPromise.bind(this), 60 * 1000);
        this.onPromiseVisible = function () {
          if (document.visibilityState === 'visible') self.renderPromise();
        };
        document.addEventListener('visibilitychange', this.onPromiseVisible);
        this.measureBar();
        this.observeRandom();
        this.observeDesktopBar();
        // Loaded ONTO the picker's own entry: the shopper left the page with
        // the picker open and came back without bfcache (or reloaded with it
        // open). The picker is closed now, so that entry stands for nothing —
        // step back to the base entry below it, or the next back press would
        // land on the same page again instead of leaving it (plan §2.2 step 6).
        var hs = this.historyOn ? history.state : null;
        if (hs && hs.xset === 'modal' && hs.sid === this.dataset.sectionId) {
          this.closing = true;
          this.closingTimer = setTimeout(function () {
            if (self.closing) self.finishClosing();
          }, 1500);
          history.back();
        }
        // The address the tray now stands for: ?bas= / ?oneri= become the
        // ?set= they produced, a sanitised link loses what was dropped, an
        // empty tray loses the parameter. (Deferred to the popstate when the
        // step back above is under way.)
        this.syncUrl();
        this.refreshCart();
        // Stored, not inline-bound: `.bind()` makes a new identity every call,
        // so an inline listener can never be removed again.
        this.onResize = function () {
          self.measureBar();
          self.syncDesktopBar();
          self.syncReadyNav();
          if (self.picker && self.picker.open) self.measureHead(self.visiblePanel());
        };
        window.addEventListener('resize', this.onResize);
      }

      /* THE SHEET LIVES UNDER <body> FROM THE START (plan §10, INP).
         BasicModal re-parents itself to <body> on every show and back on
         every hide (theme.js:790, 808). For this picker that is a 1,500-node
         subtree removed and re-inserted on each tap — and Clarity, Yandex
         Webvisor and Loox each walk and serialise every node of it in their
         MutationObservers, inside the same task as the tap: ~30 ms of a
         4×-throttled phone's 100 ms budget. Moved once while the page is
         idle, the theme finds it already under <body> (parentElementBeforeAppend
         stays null) and never moves it again. Everything the sheet needs is
         addressed by id (#XsetPicker-<section>), which is why the theme's own
         move was already safe. Not in the theme editor: a section re-render
         there would orphan a parked sheet. */
      parkPicker() {
        var picker = this.picker;
        if (!picker || (window.Shopify && window.Shopify.designMode)) return;
        var self = this;
        var park = function () {
          if (!self.isConnected || picker.open || picker.parentElement === document.body) return;
          document.body.appendChild(picker);
        };
        if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(park, { timeout: 4000 });
        else setTimeout(park, 2000);
      }

      /* The theme editor replaces this node on every settings change, and the
         `data-bound` sentinel only guards re-entry on the SAME node. Without
         this, each edit left another detached instance subscribed to cartUpdate
         and listening for resize — and measureBar() on a detached node reads
         offsetHeight 0, so it zeroed --xsb-bar-height and stripped
         body.xsb-bar-enabled, un-padding the live builder's fixed mobile bar
         until it covered the trust strip below. The bar variables are global,
         so they are retired here too. */
      disconnectedCallback() {
        clearTimeout(this.teknikTimer);
        window.removeEventListener('resize', this.onResize);
        if (this.onPopState) window.removeEventListener('popstate', this.onPopState);
        if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
        if (this.randomObserver) this.randomObserver.disconnect();
        if (this.dbarObserver) this.dbarObserver.disconnect();
        document.body.classList.remove('xsb-dbar-up');
        document.documentElement.style.removeProperty('--xsb-dbar-height');
        clearTimeout(this.closingTimer);
        clearTimeout(this.reconcileTimer);
        if (this.picker && this.onPickerKeydown) {
          this.picker.removeEventListener('keydown', this.onPickerKeydown, true);
        }
        if (typeof this.unsubscribeCart === 'function') this.unsubscribeCart();
        clearInterval(this.promiseTimer);
        if (this.onPromiseVisible) document.removeEventListener('visibilitychange', this.onPromiseVisible);
        this.disarmAdvanceCancel();
        clearTimeout(this.advanceTimer);
        clearTimeout(this.celebrateTimer);
        clearTimeout(this.hideFallbackTimer);
        clearTimeout(this.measureTimer);
        document.documentElement.style.removeProperty('--xsb-bar-height');
        document.body.classList.remove('xsb-bar-enabled');
        this.removeAttribute('data-bound');
      }

      /* ---------------------------------------------------------------- setup */

      makeSetId() {
        var rand = Math.random().toString(36).slice(2, 6);
        return 'xsb-' + this.dataset.productId + '-' + Date.now().toString(36) + '-' + rand;
      }

      /* ------------------------------------------------------------ manifest */

      loadManifest() {
        var el = document.getElementById('xset-manifest-' + this.dataset.sectionId);
        var data = null;
        try {
          data = el ? JSON.parse(el.textContent) : null;
        } catch (e) {
          data = null;
        }
        this.manifest = data && Array.isArray(data.adimlar) ? data : { adimlar: [] };

        // Per step, by handle. NOT one global map: the extras pool overlaps the
        // skeleton collections, so the same product legitimately appears in
        // two steps — with the same price, but a different step key and
        // column flag.
        //
        // Every index here is a PROTOTYPE-FREE object. Their keys arrive from
        // a shared link (?set=<adım>.<varyant>), and on a plain {} a step key
        // like "constructor" or "__proto__" resolved to an Object.prototype
        // member: hydrate() then threw on `entry.step.gizli` and the builder
        // came up with no click handlers at all.
        var index = Object.create(null);
        // And by product id, across steps, in step order — the key a ready
        // set's components arrive in. One product can sit in two steps (a
        // skeleton step and the extras pool), so each id keeps every hit.
        var byProduct = Object.create(null);
        this.manifest.adimlar.forEach(function (step) {
          if (!step || typeof step.anahtar !== 'string' || !step.anahtar) return;
          var byHandle = Object.create(null);
          // And by variant id — the key a ?set= link and a preset carry.
          var byVariant = Object.create(null);
          (step.adaylar || []).forEach(function (c) {
            // First entry wins: a product that is both a column of its own and
            // somebody's colour sibling keeps its column identity.
            if (!byHandle[c.handle]) byHandle[c.handle] = c;
            (c.variants || []).forEach(function (v) {
              if (!byVariant[String(v.id)]) byVariant[String(v.id)] = c;
            });
            var pid = String(c.productId);
            if (!byProduct[pid]) byProduct[pid] = [];
            if (!byProduct[pid].some(function (hit) { return hit.key === step.anahtar; })) {
              byProduct[pid].push({ key: step.anahtar, step: step, c: c });
            }
          });
          index[step.anahtar] = { step: step, byHandle: byHandle, byVariant: byVariant };
        });
        this.manifestIndex = index;
        this.manifestByProduct = byProduct;
      }

      stepInfo(key) {
        var entry = this.manifestIndex && this.manifestIndex[key];
        return entry ? entry.step : null;
      }

      /* Tray steps drawing from the same pool as `key` (Çeyiz's two
         complement boxes share one collection), `key` first. Every family
         whose steps each have their own pool gets [key]. */
      twinKeys(key) {
        var step = this.stepInfo(key);
        var pool = step && step.havuz;
        var keys = [key];
        if (!pool || !this.isTrayStep(key)) return keys;
        var self = this;
        Object.keys(this.manifestIndex || {}).forEach(function (k) {
          var other = self.stepInfo(k);
          if (k !== key && other && other.havuz === pool && self.isTrayStep(k)) keys.push(k);
        });
        return keys;
      }

      candidate(key, handle) {
        var entry = this.manifestIndex && this.manifestIndex[key];
        return (entry && handle && entry.byHandle[handle]) || null;
      }

      /* Candidates the picker can actually offer: every in-stock candidate of
         the step. Every accepted candidate has a comparison column, all on
         screen, and the colour siblings ride on their columns' swatches, so nothing
         in the manifest is out of the shopper's reach any more. (It used to be
         `kolon` only, when the columns past the cap did not exist.) */
      pickable(key) {
        var step = this.stepInfo(key);
        if (!step) return [];
        return (step.adaylar || []).filter(function (c) {
          return c.available;
        });
      }

      /* The cheapest price a shopper can actually put in the box for this
         candidate — its in-stock variants only. */
      floorPrice(c) {
        var prices = (c.variants || [])
          .filter(function (v) {
            return v.available && v.price > 0;
          })
          .map(function (v) {
            return v.price;
          });
        return prices.length ? Math.min.apply(Math, prices) : 0;
      }

      /* Turns a pointer — step, column identity, product actually chosen,
         variant — into a tray selection, from manifest data only. Returns null
         when the product or variant is not a buyable candidate of this step,
         which is what lets applySnapshot() drop a stale pick instead of resurrecting
         an unbuyable line. A single-variant product needs no variant id. */
      selectionFor(key, uid, handle, variantId) {
        var c = this.candidate(key, handle || uid);
        if (!c || !c.available) return null;
        var variants = c.variants || [];
        var v = null;
        if (variantId) {
          v = variants.find(function (x) {
            return String(x.id) === String(variantId);
          });
        } else if (variants.length === 1) {
          v = variants[0];
        }
        if (!v || !v.available) return null;
        return {
          uid: uid || c.handle,
          handle: c.handle,
          productId: String(c.productId),
          title: c.title,
          kisaAd: c.kisaAd || shortTitle(c.title),
          price: v.price || 0,
          variantId: String(v.id),
          optionName: c.secenek || 'Seçenek',
          image: c.img || '',
          setDisi: !!c.setDisi,
          // How it got into the box: 'secim' (the shopper picked it), 'bas'
          // (the product page they came from, "Senin seçimin") or 'oneri' (a
          // preset filled it, "Önerimiz"). Plan §4.2 B: an 'oneri' box becomes
          // 'secim' the moment the shopper picks in it.
          kaynak: 'secim',
        };
      }

      /* An empty box shows a ghost of its category. Liquid draws it from the
         step's collection; when that collection does not resolve (a renamed
         handle — the tag fallback still finds candidates) the box would be a
         blank dashed square, so the first candidate's photo stands in. */
      prepareGhosts() {
        var self = this;
        this.state.slots.forEach(function (slot) {
          var ghost = slot.el.querySelector('.xset-slot__ghost');
          if (!ghost || ghost.querySelector('img')) return;
          var first = self.pickable(slot.categoryKey).find(function (c) {
            return c.img;
          });
          if (!first) return;
          var img = document.createElement('img');
          img.src = first.img;
          img.alt = '';
          img.loading = 'lazy';
          img.className = 'xset-slot__ghost-image';
          ghost.appendChild(img);
        });
      }

      /* "Sete ekstra ürün ekle" as a grouped list (plan §6, Ek A5): one
         heading per product type, one row per product with its colours as
         swatches, and one "Ekle". Built once, from the manifest — every
         field a row needs is there, and the manifest's candidates already
         passed the step's `haric` exclusions, the stock gate and the
         set-product rule. Products that are also tray-step candidates stay
         in the list (commit() files them under their own step); the pet
         dryer and anything else the pool holds is here too.

         Order: groups by their cheapest product, rows by price — the small
         add-ons first, the 23.999 TL dryer at the end (plan §3.3).

         Each row carries the [data-xset-card] contract of a comparison
         column, so choosing, colours and variants all run through commit(),
         pickColor() and pickVariant() unchanged. */
      buildExtraLists() {
        if (!this.picker) return;
        var self = this;
        Array.prototype.forEach.call(this.picker.querySelectorAll('[data-xset-extra-list]'), function (host) {
          if (host.dataset.built) return;
          var key = host.dataset.stepKey;
          var step = self.stepInfo(key);
          if (!step) return;
          host.dataset.built = 'true';

          var all = (step.adaylar || []).filter(function (c) {
            return c.available;
          });
          var siblings = {};
          all.forEach(function (c) {
            if (c.ana) (siblings[c.ana] = siblings[c.ana] || []).push(c);
          });
          // A part is grouped under the device it fits, not its product type:
          // the catalogue types the fountain filters "Pet Setleri", which as a
          // heading told a shopper nothing. A model code in its title that is
          // a tray step's candidate ("Filtresi (X8650 Uyumlu)" → Su Kabı)
          // names the group; anything else keeps its product type, so the
          // devices of the extras list group exactly as before.
          var codeStep = {};
          ((self.manifest && self.manifest.adimlar) || []).forEach(function (st) {
            if (!st.tepside) return;
            (st.adaylar || []).forEach(function (cand) {
              if (cand.kod) codeStep[String(cand.kod).toUpperCase()] = st.baslik;
            });
          });
          var groupOf = function (c) {
            var own = String(c.kod || '').toUpperCase();
            // The merchant's own pairing first (custom.uyumlu_modeller), then
            // a model code in the title.
            var byPid = (c.uyumlu || [])
              .map(function (id) {
                var hits = (self.manifestByProduct && self.manifestByProduct[String(id)]) || [];
                return hits.length && hits[0].c.kod ? String(hits[0].c.kod).toUpperCase() : '';
              })
              .filter(Boolean);
            var codes = byPid.concat(String(c.title || '').toUpperCase().match(/X\d{4}/g) || []);
            for (var i = 0; i < codes.length; i += 1) {
              if (codes[i] !== own && codeStep[codes[i]]) return codeStep[codes[i]] + ' için';
            }
            return String(c.tip || '').trim() || 'Diğer ürünler';
          };
          var groups = {};
          var order = [];
          all.forEach(function (c) {
            if (c.ana) return;
            var type = groupOf(c);
            if (!groups[type]) {
              groups[type] = [];
              order.push(type);
            }
            groups[type].push(c);
          });
          var byPrice = function (a, b) {
            return self.floorPrice(a) - self.floorPrice(b);
          };
          order.forEach(function (type) {
            groups[type].sort(byPrice);
          });
          order.sort(function (a, b) {
            return byPrice(groups[a][0], groups[b][0]);
          });

          order.forEach(function (type, gi) {
            var group = document.createElement('li');
            group.className = 'xset-extra__group';
            var heading = document.createElement('h3');
            heading.className = 'xset-extra__heading';
            heading.id = 'XsetExtra-' + self.dataset.sectionId + '-' + key + '-' + gi;
            heading.textContent = type;
            var rows = document.createElement('ul');
            rows.className = 'xset-extra__rows';
            rows.setAttribute('role', 'list');
            rows.setAttribute('aria-labelledby', heading.id);
            groups[type].forEach(function (c) {
              rows.appendChild(self.extraRow(key, c, siblings[c.handle] || []));
            });
            group.appendChild(heading);
            group.appendChild(rows);
            host.appendChild(group);
          });
        });
      }

      /* Model codes of what the tray holds now ("X8650"), for syncFits(). */
      trayCodes() {
        var self = this;
        var codes = [];
        (this.state.slots || []).forEach(function (slot) {
          var sel = slot.selection;
          if (!sel) return;
          var c = self.candidate(slot.categoryKey, sel.handle || sel.uid);
          var code = c && c.kod ? String(c.kod).toUpperCase() : '';
          if (code && codes.indexOf(code) === -1) codes.push(code);
        });
        return codes;
      }

      /* A list panel's parts that fit the set: "Setindeki X8650 ile uyumlu",
         and those rows (and their groups) first. Re-run on every opening —
         the tray may have changed fountains since. Rows keep their price
         order otherwise; nothing is hidden, a part for another model is
         still a part a shopper may want. */
      syncFits(panel) {
        var host = panel && panel.querySelector('[data-xset-extra-list]');
        if (!host) return;
        var tray = this.trayCodes();
        var sig = tray.join(',');
        if (host.dataset.fitSig === sig) return;
        host.dataset.fitSig = sig;
        Array.prototype.forEach.call(host.querySelectorAll('[data-xset-row][data-fits]'), function (row) {
          var match = row.dataset.fits.split(' ').filter(function (code) {
            return tray.indexOf(code) !== -1;
          });
          var fitEl = row.querySelector('[data-xset-row-fit]');
          row.classList.toggle('is-fit', match.length > 0);
          setHidden(fitEl, !match.length);
          setText(fitEl, match.length ? 'Setindeki ' + match[0] + ' ile uyumlu' : '');
        });
        // Fitting rows first, then the rest, each in its original (price)
        // order — `rank` remembers it, so a row lifted for the last fountain
        // goes back to its place when the fountain changes. Only a parent
        // whose children are actually out of that order is touched.
        var arrange = function (parent, isFit) {
          var items = Array.prototype.slice.call(parent.children);
          items.forEach(function (x, i) {
            if (!x.dataset.rank) x.dataset.rank = String(i);
          });
          var byRank = items.slice().sort(function (a, b) {
            return a.dataset.rank - b.dataset.rank;
          });
          var order = byRank.filter(isFit).concat(
            byRank.filter(function (x) {
              return !isFit(x);
            })
          );
          var same = order.every(function (x, k) {
            return items[k] === x;
          });
          if (same) return;
          order.forEach(function (x) {
            parent.appendChild(x);
          });
        };
        Array.prototype.forEach.call(host.querySelectorAll('.xset-extra__rows'), function (list) {
          arrange(list, function (row) {
            return row.classList.contains('is-fit');
          });
        });
        arrange(host, function (g) {
          return !!g.querySelector('.xset-extra__row.is-fit');
        });
      }

      /* One row of the extras list. */
      extraRow(key, c, siblings) {
        var el = function (tag, cls, text) {
          var node = document.createElement(tag);
          if (cls) node.className = cls;
          if (text) node.textContent = text;
          return node;
        };
        var variants = c.variants || [];
        var multi = variants.length > 1;
        // The row shows the model code itself, so a title's "(X8805)" only
        // repeats it, and "X7570 ile uyumlu" is said by the group heading.
        // A "(X8650 Uyumlu)" stays: it is which fountain a filter fits.
        var ownCode = String(c.kod || '').toUpperCase();
        var name = (c.kisaAd || shortTitle(c.title))
            .replace(/\s*\((X\d{4})\)/g, function (m, code) {
              return code.toUpperCase() === ownCode ? '' : m;
            })
            .replace(/,?\s*X\d{4}\s+(?:ile\s+uyumlu|[İi]çin\s+[Uu]ygundur)/g, '')
            .trim();

        var row = el('li', 'xset-extra__row');
        row.setAttribute('data-xset-card', '');
        row.setAttribute('data-xset-row', '');
        row.dataset.productId = String(c.productId);
        row.dataset.productHandle = c.handle;
        row.dataset.productTitle = c.title;
        row.dataset.stepKey = key;
        row.dataset.price = String(this.floorPrice(c) || c.price || 0);
        row.dataset.available = 'true';
        row.dataset.optionName = c.secenek || 'Seçenek';
        if (!multi && variants[0]) row.dataset.variantId = String(variants[0].id);
        row.dataset.groupIds = [String(c.productId)]
          .concat(
            siblings.map(function (s) {
              return String(s.productId);
            })
          )
          .join(' ');

        var media = el('span', 'xset-extra__media');
        if (c.img) {
          var img = el('img', 'xset-extra__image xset-card__image');
          img.src = c.img;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 56;
          img.height = 56;
          media.appendChild(img);
        }
        row.appendChild(media);

        var text = el('span', 'xset-extra__text');
        text.appendChild(el('span', 'xset-extra__name', name));
        var meta = el('span', 'xset-extra__meta');
        var code = el('span', 'xset-code' + (c.kod ? '' : ' is-empty'), c.kod || '');
        code.setAttribute('data-xset-code', '');
        meta.appendChild(code);
        var owned = el('span', 'xset-extra__owned');
        owned.setAttribute('data-xset-row-owned', '');
        owned.hidden = true;
        meta.appendChild(owned);
        text.appendChild(meta);
        // The devices a part says it fits ("Filtresi (X8650 Uyumlu)", "X8623,
        // X8624 ve X8620 Uyumlu"): every model code in its title but its own.
        // syncFits() turns the ones the tray holds into "Setindeki X8650 ile
        // uyumlu" and lifts the row, so the filter for the fountain in the set
        // is the first one a shopper sees.
        var ownCode = String(c.kod || '').toUpperCase();
        var fits = (String(c.title || '').toUpperCase().match(/X\d{4}/g) || []).filter(function (code, i, all) {
          return code !== ownCode && all.indexOf(code) === i;
        });
        if (fits.length) {
          row.dataset.fits = fits.join(' ');
          var fit = el('span', 'xset-extra__fit');
          fit.setAttribute('data-xset-row-fit', '');
          fit.hidden = true;
          text.appendChild(fit);
        }
        var effect = el('span', 'xset-extra__effect');
        effect.setAttribute('data-xset-card-save', '');
        effect.hidden = true;
        text.appendChild(effect);

        if (siblings.length) {
          var colors = el('span', 'xset-card__colors xset-extra__colors');
          colors.setAttribute('role', 'radiogroup');
          colors.setAttribute('aria-label', name + ' rengi');
          colors.setAttribute('data-xset-colors', '');
          [c].concat(siblings).forEach(function (s, i) {
            var b = el('button', 'xset-card__color' + (i === 0 ? ' is-active' : ''));
            b.type = 'button';
            b.setAttribute('role', 'radio');
            b.setAttribute('aria-checked', i === 0 ? 'true' : 'false');
            b.setAttribute('data-xset-color', '');
            b.dataset.productHandle = s.handle;
            b.dataset.productId = String(s.productId);
            b.dataset.productTitle = s.title;
            b.dataset.price = String(s.price || 0);
            if ((s.variants || []).length === 1) b.dataset.variantId = String(s.variants[0].id);
            b.title = s.renk || s.kisaAd || '';
            b.appendChild(el('span', 'visually-hidden', s.renk || s.kisaAd || s.title));
            if (s.img) {
              var t = el('img', 'xset-card__color-thumb');
              t.src = s.img;
              t.alt = '';
              t.loading = 'lazy';
              b.appendChild(t);
            }
            colors.appendChild(b);
          });
          text.appendChild(colors);
        }
        row.appendChild(text);

        row.appendChild(el('span', 'xset-extra__price', money(this.floorPrice(c) || c.price || 0)));

        var add = el('button', 'xset-extra__add');
        add.type = 'button';
        add.setAttribute('data-xset-toggle', '');
        add.appendChild(el('span', 'btn__text', 'Ekle'));
        row.appendChild(add);

        if (multi) {
          var wrap = el('div', 'xset-card__expand-wrap xset-extra__variants');
          wrap.setAttribute('data-xset-expand', '');
          wrap.setAttribute('aria-hidden', 'true');
          var inner = el('div', 'xset-card__expand');
          inner.appendChild(el('span', 'xset-card__variant-label', c.secenek || 'Seçenek'));
          var chips = el('div', 'xset-card__variants');
          chips.setAttribute('role', 'radiogroup');
          chips.setAttribute('aria-label', name + ' ' + (c.secenek || 'seçenek'));
          variants.forEach(function (v) {
            var chip = el('button', 'xset-card__variant' + (v.available ? '' : ' xset-card__variant--unavailable'), v.title);
            chip.type = 'button';
            chip.setAttribute('role', 'radio');
            chip.setAttribute('aria-checked', 'false');
            chip.setAttribute('data-xset-variant', '');
            chip.dataset.variantId = String(v.id);
            chip.dataset.price = String(v.price || 0);
            chip.tabIndex = -1;
            if (!v.available) {
              chip.disabled = true;
              chip.setAttribute('aria-disabled', 'true');
            }
            chips.appendChild(chip);
          });
          inner.appendChild(chips);
          wrap.appendChild(inner);
          row.appendChild(wrap);
        }
        return row;
      }

      prepareCards() {
        if (!this.picker) return;
        Array.prototype.forEach.call(this.picker.querySelectorAll('[data-xset-card]'), function (card) {
          // The immutable identity. data-product-handle is never rewritten, even
          // when a colour switch re-points the card, because it is what a restored
          // session matches on — a restore that matched on the CURRENT colour
          // would silently resurrect the wrong product.
          card.dataset.activeHandle = card.dataset.productHandle;
          card.dataset.activeProductId = card.dataset.productId;
          if (card.dataset.variantId) card.dataset.activeVariantId = card.dataset.variantId;

          var toggle = card.querySelector('[data-xset-toggle]');
          if (!toggle) return;
          toggle.setAttribute('role', 'button');
          toggle.setAttribute('tabindex', '0');
        });
      }

      /* ------------------------------------------------------------- selectors */

      filledSlots() {
        return this.state.slots.filter(function (s) {
          return s.selection;
        });
      }

      pieceCount() {
        return this.filledSlots().length;
      }

      total() {
        return this.filledSlots().reduce(function (sum, s) {
          return sum + (s.selection.price || 0);
        }, 0);
      }

      /* Which position a pick made right now would take.

         Not simply pieceCount + 1: filling the third tray box while the second
         is still empty makes that product the SECOND in the set, and replacing
         something keeps the position it already had. Position is the slot's
         place among the filled ones, in tray order — the merchant's ordering. */
      /* How many boxes before this index already hold something. */
      filledBefore(idx) {
        var n = 0;
        for (var i = 0; i < idx; i += 1) {
          if (this.state.slots[i].selection) n += 1;
        }
        return n;
      }

      /* The position a FILLED box occupies in the set. */
      positionOf(slot) {
        if (!slot || !slot.selection) return 0;
        return this.filledBefore(this.state.slots.indexOf(slot)) + 1;
      }

      pendingPosition() {
        var slots = this.state.slots;
        var target = this.activeSlot;

        if (!target && this.pendingExtraCategory) {
          // commit() fills an empty box of the chosen category before opening a
          // new one, so that is the box this pick would land in.
          var key = this.pendingExtraCategory.key;
          target = slots.find(function (s) {
            return s.categoryKey === key && !s.selection;
          });
        }
        // Nothing to land in means a new box at the end of the tray.
        if (!target) return this.pieceCount() + 1;

        return this.filledBefore(slots.indexOf(target)) + 1;
      }

      /* The rate for the Nth product in the set. Positions above the last rung
         keep the last rung's rate — a sixth product should not silently lose the
         discount the fifth earned. */
      tierFor(position) {
        var pct = 0;
        for (var i = 0; i < this.ladder.length; i += 1) {
          if (this.ladder[i].pos <= position) pct = this.ladder[i].pct;
        }
        return pct;
      }

      ladderMax() {
        return this.ladder.length ? this.ladder[this.ladder.length - 1].pos : 0;
      }

      /* The next rung the shopper has not reached, or null at the top. */
      nextTier() {
        var n = this.pieceCount();
        for (var i = 0; i < this.ladder.length; i += 1) {
          if (this.ladder[i].pos > n) return this.ladder[i];
        }
        return null;
      }

      discountEarned() {
        return this.savings() > 0;
      }

      /* Per item, at that item's own rate — not a percentage of the basket.

         Rounded per line the way Shopify rounds, so the figure the builder
         quotes is the figure a checkout would produce. A builder that quotes a
         saving the checkout does not honour is worse than one that quotes
         none. */
      savings() {
        return this.savingsFor(
          this.filledSlots().map(function (s) {
            return s.selection;
          })
        );
      }

      /* The same for any list of selections in tray order (engineTotal). */
      savingsFor(sels) {
        var self = this;
        return sels.reduce(function (sum, sel, i) {
          var pct = self.tierFor(i + 1);
          if (!pct) return sum;
          return sum + Math.round(((sel.price || 0) * pct) / 100);
        }, 0);
      }

      /* Completion is the SKELETON being complete — every default box filled —
         not a piece count. The default boxes are what the merchant says a set
         of this kind consists of; extras are the shopper's own additions and
         waiting for an arbitrary number of them would mean a set that is
         finished never says so. */
      isComplete() {
        var defaults = this.defaultSlots();
        if (!defaults.length) return false;
        if (this.requireAll) {
          // Every box the shopper can still fill. A twin box whose shared
          // pool's last model already sits in the other box would otherwise
          // hold the set back for good.
          var self = this;
          if (!defaults.some(function (s) { return s.selection; })) return false;
          return defaults.every(function (s) {
            return !!s.selection || !self.fillable(s);
          });
        }
        return defaults.every(function (s) {
          return !!s.selection;
        });
      }

      /* A default box that can still be filled: its step has a panel and an
         in-stock model the per-product cap (colours as one under
         renk_birlikte) does not refuse for this box. */
      fillable(slot) {
        var self = this;
        if (!this.panelFor(slot.categoryKey)) return false;
        return this.pickable(slot.categoryKey).some(function (c) {
          return self.productUsed(c.productId, slot) < MAX_PER_PRODUCT;
        });
      }

      defaultSlots() {
        return this.state.slots.filter(function (s) {
          return !s.isExtra;
        });
      }

      /* "Otomatik Kedi Tuvaleti, Akıllı Mama Kabı ve Kedi Su Pınarı | 3'lü Set"
         — the exact shape every ready-made set in this catalogue is titled
         with. Names come from data-product-title, which is custom.kart_basligi
         where the merchant wrote one and the catalogue title otherwise
         (xenon-set-compare.liquid:178); shortTitle() is what makes the second
         case usable. */
      setName() {
        var titles = this.filledSlots().map(function (s) {
          return nameOf(s.selection);
        });
        if (!titles.length) return '';
        var names =
          titles.length === 1
            ? titles[0]
            : titles.slice(0, -1).join(', ') + ' ve ' + titles[titles.length - 1];
        return names + ' | ' + sizeLabel(titles.length);
      }

      panelFor(categoryKey) {
        if (!this.picker) return null;
        return this.picker.querySelector('[data-xset-panel][data-category-key="' + categoryKey + '"]');
      }

      slotByUid(uid) {
        return this.state.slots.find(function (s) {
          return s.uid === uid;
        });
      }

      /* ---------------------------------------------------------------- events */

      onClick(event) {
        // set-item-modal.js binds its own document-level listener on
        // [data-set-item] and preventDefaults any match (set-item-modal.js:196).
        // Bailing keeps a detail trigger from also driving the tray underneath it.
        if (event.target.closest('[data-set-item]')) return;

        var el = event.target;

        var addTrigger = el.closest('[data-xset-add-trigger]');
        if (addTrigger) {
          // At the 6-line cap the tile is aria-disabled (plan §4.2 G): it says
          // why instead of opening a chooser whose every door would refuse.
          if (this.lineFull()) return this.announce(this.lineFullText());
          return this.openPickerForNew(addTrigger);
        }

        var remove = el.closest('[data-xset-slot-remove]');
        if (remove) {
          event.stopPropagation();
          var removedEl = remove.closest('[data-xset-slot]');
          var removed = this.slotByUid(removedEl.dataset.slotUid);
          var after = removedEl.nextElementSibling;
          this.emptySlot(removed);
          return this.focusAfterRemove(removed, after);
        }

        // The sheet's backdrop. It carries no aria-controls (see
        // xenon-set-picker.liquid), so the theme does not bind it; this does.
        if (el.matches && el.matches('[data-xset-overlay]')) {
          if (this.picker && this.picker.open) this.closePicker();
          return;
        }

        var slotTrigger = el.closest('[data-xset-slot-trigger]');
        if (slotTrigger) return this.onSlotClick(slotTrigger.closest('[data-xset-slot]'), slotTrigger);

        var chooseCat = el.closest('[data-xset-choose-category]');
        if (chooseCat) return this.chooseCategory(chooseCat.dataset.xsetChooseCategory);

        // The picker's own additions (plan §6, modal ekleri 4–6) and the
        // status line's way back into the extras list.
        if (el.closest('[data-xset-picker-remove]')) return this.removeFromPicker();
        var teknikAll = el.closest('[data-xset-teknik-all]');
        if (teknikAll) return this.toggleTeknikAll(teknikAll);
        var again = el.closest('[data-xset-again]');
        if (again) return this.addAnother(again.closest('[data-xset-card]'));
        var moreExtra = el.closest('[data-xset-more-extra]');
        if (moreExtra) return this.reopenExtras(moreExtra);

        if (el.closest('[data-xset-submit]')) return this.onCta(el.closest('[data-xset-submit]'));
        if (el.closest('[data-xset-submit-mirror]')) return this.onCta(el.closest('[data-xset-submit-mirror]'));
        if (el.closest('[data-xset-retry]')) return this.submit(el.closest('[data-xset-retry]'));

        var noticeAction = el.closest('[data-xset-notice-action]');
        if (noticeAction) return this.onNoticeAction(noticeAction.dataset.xsetNoticeAction, noticeAction);

        var randomBtn = el.closest('[data-xset-random]');
        if (randomBtn) {
          this.autoSet();
          // The sticky bar's copy is pressed from far below the boxes: take
          // the shopper up to the set it just built (owner, 2026-10-13).
          // Focus follows to the page's own button — the bar's copy hides
          // itself once that one is on screen and would drop focus on <body>.
          if (randomBtn.hasAttribute('data-xset-random-bar')) {
            this.scrollToTray();
            if (this.pageRandomEl) this.pageRandomEl.focus({ preventScroll: true });
          }
          return;
        }
        if (el.closest('[data-xset-auto-undo]')) return this.autoUndoRun();
        if (el.closest('[data-xset-auto-next]')) return this.autoNext();
        var presetChip = el.closest('[data-xset-preset]');
        if (presetChip) return this.choosePreset(presetChip.dataset.xsetPreset);
        if (el.closest('[data-xset-preset-roll]')) return this.rollAll();
        if (el.closest('[data-xset-reset]')) return this.resetAll();

        if (el.closest('[data-xset-nearest-add]')) return this.addNearest();
        var compatBtn = el.closest('[data-xset-compat-add]');
        if (compatBtn) return this.addCompat(compatBtn.getAttribute('data-product-id'));
        var readyLoad = el.closest('[data-xset-ready-load]');
        if (readyLoad) return this.loadReadySet(readyLoad.dataset.xsetReadyLoad);
        var readyScroll = el.closest('[data-xset-ready-scroll]');
        if (readyScroll) return this.scrollReady(readyScroll.dataset.xsetReadyScroll);

        var scrollBtn = el.closest('[data-xset-scroll]');
        if (scrollBtn) return this.scrollCompare(scrollBtn.dataset.xsetScroll);

        var fix = el.closest('[data-xset-fix]');
        if (fix) return this.openPickerForSlot(this.slotByUid(fix.dataset.xsetFix), fix);

        var variantBtn = el.closest('[data-xset-variant]');
        if (variantBtn) return this.pickVariant(variantBtn);

        var colorBtn = el.closest('[data-xset-color]');
        if (colorBtn) return this.pickColor(colorBtn);

        var toggle = el.closest('[data-xset-toggle]');
        if (toggle) return this.commit(toggle.closest('[data-xset-card]'));
      }

      onKeydown(event) {
        var toggle = event.target.closest && event.target.closest('[data-xset-toggle]');
        if (toggle && (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar')) {
          event.preventDefault();
          this.commit(toggle.closest('[data-xset-card]'));
          return;
        }

        var chip = event.target.closest && event.target.closest('[data-xset-variant]');
        if (!chip) return;
        var group = chip.parentElement;
        var chips = Array.prototype.filter.call(group.querySelectorAll('[data-xset-variant]'), function (c) {
          return !c.disabled;
        });
        if (!chips.length) return;
        var i = chips.indexOf(chip);
        var next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = chips[(i + 1) % chips.length];
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = chips[(i - 1 + chips.length) % chips.length];
        else if (event.key === 'Home') next = chips[0];
        else if (event.key === 'End') next = chips[chips.length - 1];
        if (!next) return;
        event.preventDefault();
        chips.forEach(function (c) {
          c.setAttribute('tabindex', '-1');
        });
        next.setAttribute('tabindex', '0');
        next.focus();
      }

      /* Tapping a box — full or empty — goes straight to that category's
         products.

         It used to pop a Değiştir / Detay / Çıkar menu over a filled box. That
         is a menu whose first item is what everyone wanted: the reason you touch
         a box you already filled is to change what is in it. Removing has its
         own control already — the × in the corner — and "Detay" is the
         comparison table the picker opens onto, which carries the specs, the
         price, the rating and the "Neden bu?" badges for every candidate side by
         side. One tap, one outcome. */
      onSlotClick(slotEl, trigger) {
        var slot = this.slotByUid(slotEl.dataset.slotUid);
        if (!slot) return;
        this.openPickerForSlot(slot, trigger);
      }

      emptySlot(slot, via) {
        if (!slot) return;
        // Removing is not progress, and the box it frees is right there — an
        // advance here would read as punishment for changing your mind.
        this.cancelAdvance();
        var title = slot.selection ? nameOf(slot.selection) : slot.label;
        var gone = slot.selection;
        slot.selection = null;
        // A default box is the set's skeleton and stays; an extra box was only
        // ever a container for one choice, so emptying it removes it.
        if (slot.isExtra) {
          slot.el.remove();
          this.state.slots = this.state.slots.filter(function (s) {
            return s !== slot;
          });
        } else {
          this.renderSlot(slot);
        }
        // The banner may still be naming the product that just left the tray.
        this.hideError();
        this.announce(title + ' setten çıkarıldı.');
        this.persist();
        this.render();
        if (gone) {
          this.track('xset_remove', {
            adim: slot.categoryKey,
            urun: gone.handle,
            varyant: gone.variantId,
            fiyat: lira(gone.price),
            kaynak: via || 'tepsi',
          });
        }
      }

      /* Where focus goes once a box's × has run (plan §10): the same box,
         now empty, for a skeleton box; for an extra box (which is gone) the
         box that took its place, or "+ Sete ürün ekle" when it was the last. */
      focusAfterRemove(slot, after) {
        if (!slot) return;
        var target = null;
        if (!slot.isExtra && slot.el && slot.el.isConnected) {
          target = slot.el.querySelector('[data-xset-slot-trigger]');
        } else if (after && after.isConnected) {
          target = after.querySelector('[data-xset-slot-trigger], [data-xset-add-trigger]');
        }
        if (!target && this.addTileEl) target = this.addTileEl.querySelector('[data-xset-add-trigger]');
        if (target) target.focus({ preventScroll: true });
      }

      createExtraSlot(categoryKey, label) {
        this.extraSeq += 1;
        var uid = categoryKey + '-ekstra-' + this.extraSeq;
        var li = document.createElement('li');
        li.className = 'xset-slot is-placeholder';
        li.setAttribute('data-xset-slot', '');
        li.dataset.slotUid = uid;
        li.dataset.categoryKey = categoryKey;
        li.dataset.categoryTitle = label;
        li.innerHTML =
          '<button type="button" class="xset-slot__trigger" data-xset-slot-trigger aria-haspopup="dialog"></button>' +
          '<div class="xset-slot__box">' +
          '<div class="xset-slot__ghost" aria-hidden="true"></div>' +
          '<div class="xset-slot__media" data-xset-slot-media hidden></div>' +
          this.checkHTML +
          '</div>' +
          '<div class="xset-slot__info">' +
          '<span class="xset-slot__label" data-xset-slot-label></span>' +
          '<s class="xset-slot__was" data-xset-slot-was hidden></s>' +
          '<span class="xset-slot__price" data-xset-slot-price hidden></span>' +
          SLOT_CHANGE_HTML +
          '</div>' +
          '<button type="button" class="xset-slot__remove" data-xset-slot-remove hidden>' +
          (this.removeIconHTML || '') +
          '</button>';
        li.querySelector('[data-xset-slot-label]').textContent = label;
        li.querySelector('[data-xset-slot-remove]').setAttribute('aria-label', label + ' seçimini kaldır');
        li.querySelector('[data-xset-slot-trigger]').setAttribute('aria-label', label + ' seç');

        var slot = { el: li, uid: uid, categoryKey: categoryKey, label: label, isExtra: true, selection: null };
        this.state.slots.push(slot);
        this.trayEl.insertBefore(li, this.addTileEl);
        return slot;
      }

      renderSlot(slot) {
        var el = slot.el;
        var media = el.querySelector('[data-xset-slot-media]');
        var ghost = el.querySelector('.xset-slot__ghost');
        var labelEl = el.querySelector('[data-xset-slot-label]');
        var priceEl = el.querySelector('[data-xset-slot-price]');
        var removeEl = el.querySelector('[data-xset-slot-remove]');
        var trigger = el.querySelector('[data-xset-slot-trigger]');
        var checkEl = el.querySelector('[data-xset-slot-check]');
        var wasEl = el.querySelector('[data-xset-slot-was]');
        var sel = slot.selection;

        el.classList.toggle('is-placeholder', !sel);
        el.classList.toggle('is-product', !!sel);
        // The highlight belongs to the moment a box was filled. Left on an
        // emptied box it replays on the next fill before that fill happens.
        if (!sel) {
          el.classList.remove('is-discounted');
          // The sold-out ring belongs to a specific product. Emptying the box
          // retires the accusation with it — nothing else cleared it except a
          // later submit(), so a repaired box kept its red outline while the
          // banner named a product that was no longer in the tray.
          el.classList.remove('is-stale');
          el.classList.remove('is-just-filled');
          el.classList.remove('is-popping');
          el.classList.remove('is-ticking');
        }
        if (checkEl) checkEl.hidden = !sel;
        if (wasEl && !sel) wasEl.hidden = true;
        if (removeEl) removeEl.hidden = !sel;
        if (ghost) ghost.hidden = !!sel;
        if (priceEl) {
          priceEl.hidden = !sel;
          if (!sel) priceEl.textContent = '';
        }

        this.renderSlotChip(slot);

        if (!sel) {
          if (media) {
            media.hidden = true;
            media.innerHTML = '';
          }
          if (labelEl) labelEl.textContent = slot.label;
          if (trigger) trigger.setAttribute('aria-label', slot.label + ' seç');
          return;
        }

        if (media) {
          media.hidden = false;
          media.innerHTML = '';
          if (sel.image) {
            var img = document.createElement('img');
            img.src = sel.image;
            img.alt = '';
            img.loading = 'lazy';
            img.className = 'xset-slot__image';
            media.appendChild(img);
          }
        }
        // The short name, not the catalogue title. A box caption is two lines
        // tall, so a full "Name | spec, spec, spec (SKU)" title arrived as
        // "Otomatik Kedi Tuvaleti (Kameralı) | 72 Lt, 1080p, Mobil Kontrol…" —
        // three boxes of truncated spec lists, which is the opposite of a set
        // you can read at a glance. The full title is still on the card the
        // choice was made from and behind "Detay".
        var shortName = nameOf(sel);
        if (labelEl) labelEl.textContent = shortName;

        // The price this box actually contributes, at its own position in the
        // set. Showing the list price here while the panel below counts a
        // discounted total made the two disagree on the same screen.
        var save = 0;
        var pct = this.tierFor(this.positionOf(slot));
        if (pct) save = Math.round(((sel.price || 0) * pct) / 100);
        if (priceEl) priceEl.textContent = money((sel.price || 0) - save);
        if (wasEl) {
          wasEl.hidden = !save;
          wasEl.textContent = save ? money(sel.price) : '';
        }
        // Green means "this one is discounted", so the first box — which earns
        // nothing — must not wear it.
        el.classList.toggle('is-discounted', save > 0);
        if (trigger) trigger.setAttribute('aria-label', shortName + ' — değiştir');
      }

      /* "Senin seçimin" / "Önerimiz" (plan §2.2, §4.2 B). Says that a box was
         filled FOR the shopper — from the product page they came from, or
         from a preset — and is theirs to change: the × beside it removes it in
         one tap. A box the shopper filled themselves carries nothing. A span,
         created on demand: theme.css hides an empty div. */
      renderSlotChip(slot) {
        var info = slot.el.querySelector('.xset-slot__info');
        if (!info) return;
        var chip = info.querySelector('[data-xset-slot-chip]');
        var kaynak = slot.selection && slot.selection.kaynak;
        var text = kaynak === 'bas' ? 'Senin seçimin' : kaynak === 'oneri' ? 'Önerimiz' : '';
        if (!text) {
          if (chip) chip.remove();
          return;
        }
        if (!chip) {
          chip = document.createElement('span');
          chip.setAttribute('data-xset-slot-chip', '');
          info.insertBefore(chip, info.firstChild);
        }
        chip.className = 'xset-slot__chip' + (kaynak === 'bas' ? ' xset-slot__chip--bas' : '');
        chip.textContent = text;
      }

      /* The "you just did this" feedback: an exclusive ring, a tick that draws
         itself, and a pop.

         EXCLUSIVE is the important word. The ring used to accumulate — it was
         only ever removed from the slot it was being re-added to — so after
         three picks all three boxes wore it and it stopped meaning "here". With
         auto-advance those three picks arrive in seconds, which turns a slow
         leak into the tray's normal state. One ring at a time, and it is the
         breadcrumb the advance needs.

         The ring is NOT gated on reduced motion. It is state, not movement; only
         the pop and the stroke draw are motion, and those carry their own
         classes so the CSS can switch them off without taking the ring with
         them. */
      highlight(slot) {
        if (!slot || !slot.el) return;

        this.state.slots.forEach(function (other) {
          if (other !== slot) other.el.classList.remove('is-just-filled');
        });

        slot.el.classList.remove('is-just-filled', 'is-popping', 'is-ticking');
        // Force a reflow so re-adding the classes restarts the animations — a
        // class that never left does not replay.
        void slot.el.offsetWidth;
        slot.el.classList.add('is-just-filled');

        if (reducedMotion()) return;
        slot.el.classList.add('is-popping', 'is-ticking');

        // Transient by definition: left on, `is-popping` would suppress the
        // replay on the next fill of the same box.
        var el = slot.el;
        var clear = function () {
          el.classList.remove('is-popping', 'is-ticking');
        };
        el.addEventListener('animationend', clear, { once: true });
        // animationend does not fire in a backgrounded tab; the longest of the
        // two animations is 380ms.
        setTimeout(clear, 700);
      }

      /* ---------------------------------------------------------------- picker */

      openPickerForSlot(slot, trigger) {
        if (!slot || !this.picker) return;
        // Opening a picker by hand supersedes any pending advance, and marks
        // this picker as shopper-opened until runAdvance says otherwise.
        this.cancelAdvance();
        this.autoOpened = false;
        this.activeSlot = slot;
        this.pendingExtraCategory = null;
        // A filled box opens in EDIT mode (plan §4.2 F): "Mama Kabı ·
        // değiştiriyorsun", what is in it now, and "Setten çıkar".
        var t0 = performance.now();
        this.renderEditHead(slot.selection ? slot : null);
        this.showPanel(slot.categoryKey, slot.selection ? slot.label + ' · değiştiriyorsun' : slot.label);
        this.picker.show(trigger);
        this.afterPickerPaint();
        this.trackOpen(slot.categoryKey, slot.selection ? 'degistir' : 'sec', t0);
      }

      openPickerForNew(trigger) {
        if (!this.picker) return;
        this.cancelAdvance();
        this.autoOpened = false;
        this.activeSlot = null;
        this.pendingExtraCategory = null;
        var t0 = performance.now();
        this.renderEditHead(null);
        this.showPanel('__choose', 'Sete ürün ekle');
        this.picker.show(trigger);
        this.afterPickerPaint();
        this.trackOpen('__choose', 'ekle', t0);
        // Refused at the door when the set is already at its line cap — the
        // chooser would otherwise offer four doors that all refuse.
        if (this.lineFull()) this.noteInPicker(this.lineFullText());
      }

      /* xset_step_open, with how long the sheet took to reach the screen:
         from the handler's start to the frame after it (plan §10's
         "kutucuktan modalın açılmasına ≤100 ms", measured in the field).
         Sent after that frame, so `otomatik` already knows whether
         runAdvance opened it. One "dismiss" is owed per opening that ends
         without a pick; the chooser handing over to a category is the same
         opening. */
      trackOpen(key, islem, t0) {
        var self = this;
        if (key !== '__choose' || !this.openKey) this.pickedSinceOpen = false;
        this.openKey = key;
        var aday = key === '__choose' ? 0 : this.pickable(key).length;
        requestAnimationFrame(function () {
          setTimeout(function () {
            self.track('xset_step_open', {
              adim: key,
              islem: islem,
              aday: aday,
              otomatik: !!self.autoOpened,
              acilma_ms: Math.round(performance.now() - t0),
            });
          }, 0);
        });
      }

      /* The edit-mode header: the sub-line and "Setten çıkar". `slot` null
         hides both. */
      renderEditHead(slot) {
        var sel = slot && slot.selection;
        if (this.pickerSubEl) {
          var c = sel ? this.candidate(slot.categoryKey, sel.handle) : null;
          var code = c && c.kod ? ' ' + c.kod : '';
          setText(this.pickerSubEl, sel ? 'Şu an setinde: ' + nameOf(sel) + code : '');
          setHidden(this.pickerSubEl, !sel);
        }
        if (this.pickerRemoveEl) {
          setHidden(this.pickerRemoveEl, !sel);
          if (sel) setAttr(this.pickerRemoveEl, 'aria-label', nameOf(sel) + ' ürününü setten çıkar');
        }
      }

      /* Is the picker editing a filled box right now? */
      editingSlot() {
        var slot = this.activeSlot;
        return slot && slot.selection ? slot : null;
      }

      /* "Setten çıkar" in the edit header: the box empties exactly as its ×
         would empty it, and the sheet closes. */
      removeFromPicker() {
        var slot = this.editingSlot();
        if (!slot) return;
        var wasExtra = slot.isExtra;
        this.closePicker();
        this.emptySlot(slot, 'modal');
        // An extra box is gone with its pick, so the theme's focus return
        // would aim at a removed trigger: the "+ Yeni ürün" tile is where the
        // shopper continues (plan §10).
        if (wasExtra && this.addTileEl) {
          var add = this.addTileEl.querySelector('[data-xset-add-trigger]');
          if (add) {
            setTimeout(function () {
              if (!document.activeElement || document.activeElement === document.body) add.focus({ preventScroll: true });
            }, 400);
          }
        }
      }

      chooseCategory(categoryKey) {
        var panel = this.panelFor(categoryKey);
        if (!panel) return;
        // Refused at the door rather than after the shopper has browsed a
        // category and picked something out of it.
        if (this.lineFull()) {
          this.noteInPicker(this.lineFullText());
          return;
        }
        if (this.categoryFull(categoryKey)) {
          this.noteInPicker(
            this.limitText(categoryKey, panel.dataset.categoryTitle) + ' Önce mevcut olanı çıkar ya da değiştir.'
          );
          return;
        }
        this.clearPickerNote();
        this.renderEditHead(null);
        this.pendingExtraCategory = { key: categoryKey, label: panel.dataset.categoryTitle };
        var t0 = performance.now();
        this.showPanel(categoryKey, panel.dataset.categoryTitle);
        this.afterPickerPaint();
        this.trackOpen(categoryKey, 'ekle', t0);
      }

      /* "Bir ürün daha ekle" under the status line: straight back into the
         extras list the last "Ekle" closed (plan §6, Ek A5). */
      reopenExtras(trigger) {
        var key = this.lastExtraKey;
        if (!key || !this.panelFor(key)) return;
        this.openPickerForNew(trigger);
        this.chooseCategory(key);
      }

      showPanel(categoryKey, title) {
        if (!this.picker) return;
        var panels = this.picker.querySelectorAll('[data-xset-panel]');
        Array.prototype.forEach.call(panels, function (p) {
          setHidden(p, p.dataset.categoryKey !== categoryKey);
        });
        setText(this.pickerTitleEl, title || 'Ürün seç');

        // A note belongs to the panel that produced it.
        if (categoryKey !== '__choose') this.clearPickerNote();
        this.renderChooser();
        var panel = this.panelFor(categoryKey);
        // The extras list gets a narrower sheet (CSS keys on this).
        setAttr(this.picker, 'data-view', panel && panel.classList.contains('xset-panel--list') ? 'list' : 'compare');
        // Column order is decided per opening: the product being edited goes
        // first, everything else returns to the merchant's order.
        if (panel) this.layoutPanel(panel, true);
        if (panel) this.renderCards(panel);
        if (panel) this.applyDiff(panel);
        if (panel) this.fillTeknik(panel);
        if (panel) this.syncFits(panel);
        this.syncWhatsApp(panel);
        // After paint: called inline it measured a panel that was still hidden,
        // so scrollWidth equalled clientWidth and the arrows stayed hidden on a
        // table that very much did scroll.
        var self = this;
        requestAnimationFrame(function () {
          self.measureHead(panel);
          self.syncScrollAffordance();
        });
      }

      visiblePanel() {
        return this.picker ? this.picker.querySelector('[data-xset-panel]:not([hidden])') : null;
      }

      /* ------------------------------------------------- comparison layout */

      /* Column order, plus the toolbar's model count.

         Every accepted candidate is a server-rendered column and every one is
         on screen — no "Diğer N model" fold (owner, 2026-10-13). Order: the
         product already in the box being edited first (plan §4.2 F "İlk
         sütun mevcut ürün"), then the merchant's collection order. */
      layoutPanel(panel, reset) {
        var table = panel.querySelector('[data-xset-compare-table]');
        var countEl = panel.querySelector('[data-xset-model-count]');
        if (!table) {
          // The extras list: count its rows.
          if (countEl) {
            var rows = panel.querySelectorAll('[data-xset-row]').length;
            setText(countEl, rows ? rows + ' ürün' : '');
          }
          return;
        }
        var cards = Array.prototype.slice.call(table.querySelectorAll('[data-xset-card]'));
        cards.sort(function (a, b) {
          return (parseInt(a.dataset.colIndex, 10) || 0) - (parseInt(b.dataset.colIndex, 10) || 0);
        });
        var editing = this.editingSlot();
        if (editing && editing.categoryKey === panel.dataset.categoryKey) {
          var current = cards.find(function (card) {
            return card.dataset.productHandle === editing.selection.uid;
          });
          if (current) {
            cards.splice(cards.indexOf(current), 1);
            cards.unshift(current);
            this.syncCardTo(current, editing.selection);
          }
        }

        var order = cards.map(function (card) {
          return card.dataset.col;
        });


        // Whole columns move: every row holds one cell per product, tagged
        // data-col. Only rows whose order is
        // actually different are touched — re-inserting a cell that is
        // already in place is a remove + an add for every DOM observer on
        // the page, and most openings change nothing.
        Array.prototype.forEach.call(table.querySelectorAll('tr'), function (tr) {
          var cells = {};
          var present = [];
          Array.prototype.forEach.call(tr.querySelectorAll('td[data-col]'), function (td) {
            cells[td.dataset.col] = td;
            present.push(td.dataset.col);
          });
          var inPlace =
            present.join('|') ===
            order
              .filter(function (handle) {
                return !!cells[handle];
              })
              .join('|');
          order.forEach(function (handle) {
            var td = cells[handle];
            if (!td) return;
            if (!inPlace) tr.appendChild(td);
            setHidden(td, false);
          });
        });

        if (countEl) {
          setText(countEl, cards.length === 1 ? 'Bu kategoride şu an tek model var.' : cards.length + ' model');
        }
        // A table that keeps its own rows (step "teknik": false — Fiyat and
        // Puan only) has nothing for "Sadece farklar" to fold.
        var ownRows = panel.querySelector('[data-xset-compare-table][data-teknik="off"]');
        setHidden(panel.querySelector('[data-xset-diff]'), cards.length < 2 || !!ownRows);
      }

      /* Puts a column's controls on the selection it stands for — its colour
         and its variant — so "Setinde kalsın" in a recoloured or sized column
         means exactly what is in the box. */
      syncCardTo(card, sel) {
        if (!card || !sel) return;
        if (sel.handle !== card.dataset.activeHandle) {
          var colorBtn = card.querySelector('[data-xset-color][data-product-handle="' + sel.handle + '"]');
          if (colorBtn) {
            var wasHydrating = this.hydrating;
            this.hydrating = true;
            this.pickColor(colorBtn);
            this.hydrating = wasHydrating;
          }
        }
        if (sel.variantId) card.dataset.activeVariantId = sel.variantId;
        card.dataset.price = String(sel.price);
      }

      /* "Sadece farklar" (modal eki 3). A row whose value is the same in
         every visible column is hidden while the switch is on — but at least
         two rows always stay, so the table never collapses to its header.
         The count of hidden rows is written beside the switch ("3 ortak
         özellik gizli"); the switch itself is the way to show them. */
      applyDiff(panel) {
        var table = panel && panel.querySelector('[data-xset-compare-table]');
        if (!table) return;
        // Spec rows past the first `teknikRows` are folded behind "Tüm teknik
        // özellikler" (CSS keys on .is-extra + data-teknik-all). While folded
        // they are not part of this count — "keep two rows" must count rows a
        // shopper can actually see.
        var folded = table.dataset.teknikAll !== 'true';
        var allRows = Array.prototype.slice.call(table.querySelectorAll('tbody tr'));
        var rows = allRows.filter(function (tr) {
          return !(folded && tr.classList.contains('is-extra'));
        });
        var columns = Array.prototype.filter.call(table.querySelectorAll('[data-xset-card]'), function (card) {
          return !card.hidden;
        }).map(function (card) {
          return card.dataset.col;
        });
        var on = this.onlyDiff && columns.length > 1;
        var input = panel.querySelector('[data-xset-diff-toggle]');
        if (input) input.checked = this.onlyDiff;

        var isCommon = function (tr) {
          if (columns.length < 2) return false;
          var first = null;
          return columns.every(function (handle) {
            var td = tr.querySelector('td[data-col="' + handle + '"]');
            var sig = '';
            if (td && td.dataset.sig !== undefined) {
              // A spec cell says how it compares (teknikSig): "72 L" = "72L".
              sig = td.dataset.sig;
            } else if (td) {
              sig = td.textContent.replace(/\s+/g, ' ').trim();
              Array.prototype.forEach.call(td.querySelectorAll('[data-rating]'), function (r) {
                sig += '|' + r.getAttribute('data-rating') + '/' + r.getAttribute('data-raters');
              });
            }
            if (first === null) {
              first = sig;
              return true;
            }
            return sig === first;
          });
        };
        var common = rows.map(isCommon);

        var keep = rows.length - common.filter(Boolean).length;
        var hiddenCount = 0;
        rows.forEach(function (tr, i) {
          var hide = on && common[i];
          if (hide && keep < 2) {
            hide = false;
            keep += 1;
          }
          setHidden(tr, hide);
          if (hide) hiddenCount += 1;
        });

        setText(panel.querySelector('[data-xset-diff-note]'), hiddenCount ? '· ' + hiddenCount + ' ortak özellik gizli' : '');

        // What "Tüm teknik özellikler" would add: the folded rows that the
        // switch would not hide anyway. Zero = no button.
        var foldedShowable = folded
          ? allRows.filter(function (tr) {
              return tr.classList.contains('is-extra') && !(on && isCommon(tr));
            }).length
          : 0;
        this.renderTeknikToggle(panel, table, foldedShowable);
      }

      /* ------------------------------------------------ teknik özellikler

         Replaces the custom.set_karsilastirma rows of a step's table with
         rows built from the product page's spec data (loadTeknik). Fiyat and
         Puan stay on top; merchandising rows (Uygun Dost, Neden bu?) stay
         below. Runs once per table, after the panel is laid out; until the
         data is in — or when it never comes — the server rows stand. */
      fillTeknik(panel) {
        var table = panel && panel.querySelector('[data-xset-compare-table]');
        if (!table || !this.teknikUrl || table.dataset.teknik) return;
        table.dataset.teknik = 'pending';
        var self = this;
        loadTeknik(this.teknikUrl)
          .then(function (index) {
            table.dataset.teknik = self.buildTeknikRows(panel, table, index) ? 'done' : 'none';
          })
          .catch(function () {
            table.dataset.teknik = 'failed';
          });
      }

      // The SKU a column stands for right now: its active colour's, else its own.
      teknikSkuOf(card) {
        var active = card.dataset.activeHandle && card.querySelector('[data-xset-color][data-product-handle="' + card.dataset.activeHandle + '"]');
        var sku = (active && active.dataset.sku) || card.dataset.sku || '';
        return sku.trim().toUpperCase();
      }

      /* A column's spec record: by SKU, else by the model code the column
         shows (xenon-set-model-code also reads it off the title), which is
         what the records are keyed by when a variant carries a barcode-style
         SKU ("B03878600014"). Neither → [] and the cells stay empty. */
      teknikSpecsFor(card, index) {
        var specs = index.get(this.teknikSkuOf(card));
        if (specs) return specs;
        var codeEl = card.querySelector('[data-xset-code]');
        var code = codeEl ? codeEl.textContent.trim().toUpperCase() : '';
        return (code && index.get(code)) || null;
      }

      buildTeknikRows(panel, table, index) {
        var cards = Array.prototype.slice.call(table.querySelectorAll('thead [data-xset-card]'));
        var specsByCol = Object.create(null);
        var withData = 0;
        var self = this;
        cards.forEach(function (card) {
          var specs = self.teknikSpecsFor(card, index);
          if (specs) withData += 1;
          specsByCol[card.dataset.col] = specs || [];
        });
        // One model with a record is a spec sheet, not a comparison.
        if (withData < 2) return false;

        // group → {count, labels{label: n}, firstSeen}
        var groups = Object.create(null);
        var order = 0;
        cards.forEach(function (card) {
          specsByCol[card.dataset.col].forEach(function (spec) {
            var g = groups[spec.group];
            if (!g) g = groups[spec.group] = { group: spec.group, count: 0, labels: Object.create(null), firstSeen: order++ };
            g.count += 1;
            g.labels[spec.label] = (g.labels[spec.label] || 0) + 1;
          });
        });
        var rowsSpec = Object.keys(groups)
          .map(function (key) {
            return groups[key];
          })
          .filter(function (g) {
            return g.count >= 2;
          })
          .sort(function (a, b) {
            return teknikRank(a.group) - teknikRank(b.group) || b.count - a.count || a.firstSeen - b.firstSeen;
          });
        if (!rowsSpec.length) return false;

        var tbody = table.querySelector('tbody');
        if (!tbody) return false;
        // The hand-kept rows this replaces. Fiyat, Puan, Uygun Dost and Neden
        // bu? come from other sources and stay.
        Array.prototype.forEach.call(tbody.querySelectorAll('tr[data-row-path="custom.set_karsilastirma"]'), function (tr) {
          tr.remove();
        });
        var anchor = tbody.querySelector('tr[data-compare-key="puan"]') || tbody.querySelector('tr[data-compare-key="fiyat"]');
        var before = anchor ? anchor.nextElementSibling : tbody.firstElementChild;
        var limit = this.teknikRows;

        var frag = document.createDocumentFragment();
        rowsSpec.forEach(function (g, i) {
          var label = Object.keys(g.labels).sort(function (a, b) {
            return g.labels[b] - g.labels[a];
          })[0];
          var tr = document.createElement('tr');
          tr.className = 'xset-compare__row xset-compare__row--teknik' + (i >= limit ? ' is-extra' : '');
          tr.dataset.compareKey = 'teknik-' + g.group;
          tr.dataset.teknikGroup = g.group;
          var th = document.createElement('th');
          th.scope = 'row';
          th.className = 'td-label background-1 text-body';
          th.textContent = label;
          tr.appendChild(th);
          cards.forEach(function (card) {
            var td = document.createElement('td');
            td.className = 'td-product xset-compare__cell';
            td.setAttribute('data-xset-compare-cell', '');
            td.dataset.col = card.dataset.col;
            td.dataset.label = label;
            td.hidden = card.hidden;
            self.writeTeknikCell(td, specsByCol[card.dataset.col], g.group);
            tr.appendChild(td);
          });
          frag.appendChild(tr);
        });
        tbody.insertBefore(frag, before);
        table.dataset.teknikExtra = String(Math.max(0, rowsSpec.length - limit));

        this.applyDiff(panel);
        requestAnimationFrame(function () {
          self.measureHead(panel);
          self.syncScrollAffordance();
        });
        return true;
      }

      writeTeknikCell(td, specs, group) {
        var spec = null;
        for (var i = 0; i < specs.length; i++) {
          if (specs[i].group === group) {
            spec = specs[i];
            break;
          }
        }
        td.textContent = '';
        if (spec) {
          td.textContent = spec.value;
          td.dataset.sig = teknikSig(spec.value);
        } else {
          // Left empty, as the server cells are (owner: "bilgi yok yazan
          // yerleri boş bırak"); the marker lets a phone hide the row label.
          var empty = document.createElement('span');
          empty.className = 'xset-compare__empty';
          td.appendChild(empty);
          td.dataset.sig = '';
        }
      }

      /* A colour switch makes the column a different product; its spec cells
         follow (siblings usually share specs, but the record is per SKU). */
      refreshTeknikColumn(card) {
        var table = card && card.closest('[data-xset-compare-table]');
        if (!table || table.dataset.teknik !== 'done' || !this.teknikUrl) return;
        var self = this;
        loadTeknik(this.teknikUrl)
          .then(function (index) {
            var specs = self.teknikSpecsFor(card, index) || [];
            Array.prototype.forEach.call(table.querySelectorAll('tr[data-teknik-group]'), function (tr) {
              var td = tr.querySelector('td[data-col="' + card.dataset.col + '"]');
              if (td) self.writeTeknikCell(td, specs, tr.dataset.teknikGroup);
            });
            var panel = table.closest('[data-xset-panel]');
            if (panel) self.applyDiff(panel);
          })
          .catch(function () {});
      }

      renderTeknikToggle(panel, table, foldedShowable) {
        var btn = panel.querySelector('[data-xset-teknik-all]');
        var extra = parseInt(table.dataset.teknikExtra, 10) || 0;
        var open = table.dataset.teknikAll === 'true';
        if (!extra || (!open && !foldedShowable)) {
          setHidden(btn, true);
          return;
        }
        if (!btn) {
          btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'xset-compare__teknik-all';
          btn.setAttribute('data-xset-teknik-all', '');
          table.insertAdjacentElement('afterend', btn);
        }
        setHidden(btn, false);
        setAttr(btn, 'aria-expanded', open ? 'true' : 'false');
        setText(btn, open ? 'Daha az teknik özellik göster' : 'Tüm teknik özellikleri göster (+' + foldedShowable + ')');
      }

      toggleTeknikAll(btn) {
        var panel = btn.closest('[data-xset-panel]');
        var table = panel && panel.querySelector('[data-xset-compare-table]');
        if (!table) return;
        if (table.dataset.teknikAll === 'true') {
          delete table.dataset.teknikAll;
        } else {
          table.dataset.teknikAll = 'true';
        }
        this.applyDiff(panel);
        var self = this;
        requestAnimationFrame(function () {
          self.measureHead(panel);
          self.syncScrollAffordance();
        });
      }

      /* Column widths for the table on screen. It used to also measure the
         slice a sticky header row kept pinned; the owner had the header
         scroll away with the rows (2026-10-13), so only the widths are left.
         Kept under its old name: every layout change already calls it. */
      measureHead(panel) {
        var table = panel && panel.querySelector('[data-xset-compare-table]');
        if (!table || !table.offsetParent) return;
        this.fitColumns(table);
      }

      /* Desktop: the columns share the sheet's width, 150–220px each, so a
         step of four or five models fits without scrolling sideways; past
         that the table scrolls (the arrows say so). On a phone (≈1.5 columns
         by design) the CSS widths stand. */
      fitColumns(table) {
        var phone = window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches;
        var cols = table.querySelectorAll('thead td[data-xset-card]').length;
        if (phone || !cols) {
          if (table.style.getPropertyValue('--xset-col-w')) table.style.removeProperty('--xset-col-w');
          return;
        }
        var label = table.querySelector('thead .td-label');
        var room = table.clientWidth - (label ? label.offsetWidth : 150);
        var colW = Math.max(150, Math.min(220, Math.floor(room / cols)));
        if (table.style.getPropertyValue('--xset-col-w') !== colW + 'px') table.style.setProperty('--xset-col-w', colW + 'px');
      }

      /* Modal eki 8 — only when the section has a confirmed number. The draft
         names the step and what the set holds, so the answer can be about
         this set rather than a generic "how can I help". */
      syncWhatsApp(panel) {
        var link = panel && panel.querySelector('[data-xset-wa]');
        if (!link) return;
        var names = this.filledSlots().map(function (s) {
          return nameOf(s.selection);
        });
        var text =
          'Merhaba, Kendi Setini Oluştur sayfasında ' +
          (panel.dataset.categoryTitle || 'ürün') +
          ' seçiyorum.' +
          (names.length ? ' Setimde: ' + names.join(', ') + '.' : '') +
          ' Hangisini önerirsiniz?';
        link.href = 'https://wa.me/' + link.dataset.waNumber + '?text=' + encodeURIComponent(text);
      }


      /* The table is wider than the modal whenever a category has more than two
         or three candidates, and a table that is merely clipped reads as one
         that ends there. These buttons are the affordance AND the control:
         hidden when there is nothing to scroll to, so they never lie. */
      syncScrollAffordance() {
        var scroller = this.picker && this.picker.querySelector('[data-xset-panel]:not([hidden]) .h-scrollable');
        var prev = this.picker && this.picker.querySelector('[data-xset-scroll="prev"]');
        var next = this.picker && this.picker.querySelector('[data-xset-scroll="next"]');
        if (!prev || !next) return;
        if (!scroller) {
          setHidden(prev, true);
          setHidden(next, true);
          return;
        }
        this.scroller = scroller;
        var max = scroller.scrollWidth - scroller.clientWidth;
        // The arrows sit over the photo band, which is the part of the
        // sticky header that scrolls away; once the table has scrolled down
        // they would float over the pinned names and buttons instead, so
        // they step aside until the shopper is back at the top.
        var down = scroller.scrollTop > 24;
        setHidden(prev, down || max <= 4 || scroller.scrollLeft <= 4);
        setHidden(next, down || max <= 4 || scroller.scrollLeft >= max - 4);
        // Measured from the scroller rather than fixed in CSS: the toolbar
        // above the table ("N model · Sadece farklar") wraps differently per
        // step and per width.
        var viewport = scroller.closest('.xset-picker__viewport');
        if (viewport) {
          var top = Math.max(0, Math.round(scroller.getBoundingClientRect().top - viewport.getBoundingClientRect().top + 40)) + 'px';
          if (prev.style.top !== top) prev.style.top = top;
          if (next.style.top !== top) next.style.top = top;
        }
      }

      scrollCompare(direction) {
        var scroller = this.picker && this.picker.querySelector('[data-xset-panel]:not([hidden]) .h-scrollable');
        if (!scroller) return;
        // Exactly one column per press. Landing mid-column would make the
        // shopper re-aim before they can read anything, and a column is the
        // unit they are actually moving through.
        var col = scroller.querySelector('.td-product');
        var step = col ? Math.round(col.getBoundingClientRect().width) : Math.round(scroller.clientWidth * 0.6);
        scroller.scrollBy({ left: direction === 'prev' ? -step : step, behavior: reducedMotion() ? 'auto' : 'smooth' });
      }

      /* The focusable elements a shopper can actually reach right now.

         theme.js:35 selects with a plain querySelectorAll and no visibility
         test, so every card button in the hidden category panels counts: 28
         focusables in this modal, 5 of them on screen. */
      visibleFocusables() {
        if (!this.picker || !window.FoxTheme || !FoxTheme.a11y) return [];
        return FoxTheme.a11y.getFocusableElements(this.picker).filter(function (el) {
          return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
        });
      }

      /* Keeps Tab inside the picker.

         The theme's trap wraps at its `last` element, and `last` here is a
         button in a hidden panel that can never take focus — so the wrap never
         fired and Tab walked out of the open modal into the page behind it.
         Fixed in this component rather than in theme.js: that trap is shared by
         every modal in the store, and this modal is the one keeping a pile of
         hidden siblings in its own subtree.

         The list is recomputed on each keypress, so switching panels mid-tab
         cannot leave it pointing at a panel that has since been hidden. */
      wrapPickerTab(event) {
        if (event.key !== 'Tab' || event.defaultPrevented) return;
        if (!this.picker || !this.picker.hasAttribute('open')) return;

        var reachable = this.visibleFocusables();
        if (!reachable.length) return;

        var first = reachable[0];
        var last = reachable[reachable.length - 1];
        var index = reachable.indexOf(document.activeElement);

        // Focus somewhere outside the reachable set — the modal element itself,
        // or a control in a panel that has just been hidden.
        if (index === -1) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
          return;
        }

        if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      }

      afterPickerPaint() {
        // MANDATORY after any panel swap. FoxTheme.a11y.trapFocus captures the
        // first and last focusable ONCE (theme.js:44-45) and keeps them on a
        // single global object with no stack, so a trap armed against the
        // previous panel now points at elements that are hidden. set-item-modal.js:145
        // re-arms for exactly this reason.
        var self = this;
        requestAnimationFrame(function () {
          if (!self.picker || !self.picker.hasAttribute('open')) return;
          if (window.FoxTheme && FoxTheme.a11y && FoxTheme.a11y.trapFocus) {
            FoxTheme.a11y.trapFocus(self.picker, self.picker.focusElement);
          }
          // Re-arming is not enough on its own — trapFocus leaves focus on the
          // modal element itself, which reports as <body> once the sheet has
          // settled, so the first Tab starts from outside the list.
          var reachable = self.visibleFocusables();
          if (reachable.length && !self.picker.contains(document.activeElement)) {
            reachable[0].focus();
          }
        });
        // Belt and braces for the arrows. The modal's own "shown" event is the
        // right hook, but it fires off a transition that does not always run
        // (reduced motion, a backgrounded tab), and a measurement taken while
        // the sheet is still collapsing decides the table does not scroll.
        setTimeout(function () {
          self.syncScrollAffordance();
        }, 800);
      }

      closePicker() {
        if (this.picker) this.picker.hide();
        this.forgetPickerState();

        // Belt and braces for the beat. ModalComponent resolves its hide on the
        // overlay's transitionend (theme.js:852) — which never arrives in a
        // backgrounded tab, or on a UA that skips the transition — and hide()
        // returns undefined outright when the modal was not open. Whichever of
        // the event and this timer lands first wins; runPendingFill() is
        // idempotent.
        var self = this;
        clearTimeout(this.hideFallbackTimer);
        this.hideFallbackTimer = setTimeout(function () {
          self.runPendingFill();
        }, 1200);
      }

      /* Everything a closed picker must forget. Shared because closePicker() is
         only reachable from a successful commit(), so the ✕, the overlay, Esc
         and browser-back all land in onPickerHidden() instead — and used to
         leave `tentativeCard` behind, which re-applied the half-selected state
         to an abandoned card in a different category's panel, and left a
         `role="alert"` refusal note waiting on the chooser. */
      forgetPickerState() {
        this.activeSlot = null;
        this.pendingExtraCategory = null;
        this.tentativeCard = null;
        this.clearPickerNote();
      }

      onPickerHidden() {
        // Closed with no answer (✕, Esc, the backdrop, the back button).
        if (this.openKey && !this.pickedSinceOpen) {
          this.track('xset_dismiss', { adim: this.openKey, otomatik: !!this.autoOpened });
        }
        this.openKey = null;
        this.forgetPickerState();
        this.parkPicker();
        // A picker that opened itself and was closed without a choice is the
        // shopper declining the flow. That is the only unambiguous "no" on
        // offer, so it is honoured for the rest of the page view — without it
        // they would have to decline the same offer at every step.
        if (this.autoOpened && !this.committedSinceAutoOpen) {
          this.advanceDismissed = true;
          this.announce('Otomatik geçiş kapatıldı. Kutucukları kendin seçebilirsin.');
        }
        this.autoOpened = false;
        this.runPendingFill();
      }

      /* The beat: the tray is visible again, so now the box can say what
         happened. Idempotent — both the hide event and its timeout fallback
         call it, and only the first one does anything. */
      runPendingFill() {
        clearTimeout(this.hideFallbackTimer);
        var pending = this.pendingFill;
        if (!pending) return;
        this.pendingFill = null;

        this.highlight(pending.slot);
        // Focus goes to the box that was just filled (plan §10). The theme
        // hands it back to whatever opened the sheet — the "+ Sete ürün ekle"
        // tile, or the box an advance opened from — in a timeout of its own
        // queued before this one, so this runs after it and wins.
        var filledEl = pending.slot.el;
        setTimeout(function () {
          if (!filledEl || !filledEl.isConnected) return;
          var active = document.activeElement;
          if (active && filledEl.contains(active)) return;
          if (active && active !== document.body && !active.closest('xenon-set-builder, [data-xset-picker]')) return;
          var t = filledEl.querySelector('[data-xset-slot-trigger]');
          if (t) t.focus({ preventScroll: true });
        }, 0);
        // The whole status line — the sentence that says what was added and
        // the link beside it — not just the link.
        var more = pending.more && this.statusMoreEl && !this.statusMoreEl.hidden ? this.statusMoreEl : null;
        this.revealSlot(pending.slot, more ? more.closest('.xset__status-line') || more : null);

        if (!pending.advance) return;
        this.scheduleAdvance();
      }

      /* Bring a filled box into view. On mobile the tray is a column now, so
         the box that just changed can easily be below the fold. */
      revealSlot(slot, also) {
        if (!slot || !slot.el || typeof slot.el.getBoundingClientRect !== 'function') return;
        var rect = slot.el.getBoundingClientRect();
        var barH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--xsb-bar-height'), 10) || 0;

        // After "Ekle" in the extras list, "Bir ürün daha ekle" on the status
        // line is the way back in (Ek A5) — and on a phone it sits at the top
        // of the builder, well above a box near the foot of the tray. Both
        // are brought into view when they fit together under the theme's
        // sticky header; when they do not, the box wins.
        if (also && !also.hidden && also.getClientRects().length) {
          var head = document.querySelector('.header-sticky');
          var headH = head ? Math.max(0, head.getBoundingClientRect().bottom) : 0;
          var a = also.getBoundingClientRect();
          var top = Math.min(rect.top, a.top);
          var bottom = Math.max(rect.bottom, a.bottom);
          var room = window.innerHeight - barH - headH;
          if (top >= headH && bottom <= window.innerHeight - barH) return;
          if (bottom - top <= room - 16) {
            window.scrollBy({ top: top - headH - 8, behavior: reducedMotion() ? 'auto' : 'smooth' });
            return;
          }
        }

        if (rect.top >= 0 && rect.bottom <= window.innerHeight - barH) return;
        slot.el.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      }

      /* Hold, then open. The hold is what makes this momentum rather than a
         conveyor belt: long enough for the pop to finish and for the eye to
         read the counter tick over from 1/3 to 2/3.

         ANY input during the hold cancels it. Someone who has started making
         their own next move has answered the question the advance was about to
         ask. */
      scheduleAdvance() {
        var self = this;
        var token = ++this.advanceToken;
        clearTimeout(this.advanceTimer);

        this.advanceCancel = function () {
          self.cancelAdvance();
        };
        this.addEventListener('pointerdown', this.advanceCancel, true);
        document.addEventListener('keydown', this.advanceCancel, true);
        window.addEventListener('wheel', this.advanceCancel, { capture: true, passive: true });
        window.addEventListener('touchmove', this.advanceCancel, { capture: true, passive: true });

        this.advanceTimer = setTimeout(function () {
          self.disarmAdvanceCancel();
          if (token !== self.advanceToken) return;
          self.runAdvance();
        }, 520);
      }

      disarmAdvanceCancel() {
        if (!this.advanceCancel) return;
        this.removeEventListener('pointerdown', this.advanceCancel, true);
        document.removeEventListener('keydown', this.advanceCancel, true);
        window.removeEventListener('wheel', this.advanceCancel, { capture: true });
        window.removeEventListener('touchmove', this.advanceCancel, { capture: true });
        this.advanceCancel = null;
      }

      cancelAdvance() {
        this.advanceToken += 1;
        clearTimeout(this.advanceTimer);
        this.disarmAdvanceCancel();
      }

      runAdvance() {
        // Re-derived at fire time, not reused from schedule time: half a second
        // is long enough for a cartUpdate re-render, a removal, or a roll to
        // have changed which box is next — or whether there is one.
        var target = this.nextAdvanceTarget();
        if (!target || !this.picker || this.picker.open) return;
        if (this.advanceDismissed) return;

        // Never open over another modal. Two builders on one page (the theme
        // editor does this), the set-item detail modal, and the cart drawer all
        // share the theme's single global focus trap — opening across one of
        // them tears down its trap and strands focus.
        var otherModal = document.querySelector('basic-modal[open], drawer-component[open]');
        if (otherModal && otherModal !== this.picker) return;

        // Said before the sheet arrives, and it names the way out. That second
        // half is what makes this an offer rather than a shove. The category
        // name also keeps consecutive advances distinct, so announce()'s repeat
        // guard does not swallow the second one.
        this.announce('Sırada ' + target.label + '. Kapatmak için Esc.');
        this.openPickerForSlot(target, target.el.querySelector('[data-xset-slot-trigger]'));
        this.autoOpened = true;
        this.committedSinceAutoOpen = false;
      }

      /* -------------------------------------------------------------- choosing */

      /* The card is the pointer (which column, which colour, which variant);
         the manifest is the data. The DOM read is only the fallback for a cell
         the manifest does not know — it should never happen, and if it does the
         shopper still gets the product they pressed. */
      readCard(card) {
        var sel = this.selectionFor(
          card.dataset.stepKey,
          card.dataset.productHandle,
          card.dataset.activeHandle,
          card.dataset.activeVariantId
        );
        return sel || this.readCardDom(card);
      }

      readCardDom(card) {
        var title = card.dataset.productTitle;
        return {
          uid: card.dataset.productHandle,
          handle: card.dataset.activeHandle,
          productId: card.dataset.activeProductId,
          title: title,
          kisaAd: shortTitle(title),
          price: parseInt(card.dataset.price, 10) || 0,
          variantId: card.dataset.activeVariantId || '',
          optionName: card.dataset.optionName || 'Seçenek',
          image: (card.querySelector('.xset-card__image') || {}).currentSrc || (card.querySelector('.xset-card__image') || {}).src || '',
          setDisi: false,
          kaynak: 'secim',
        };
      }

      /* What a card would cost if pressed now, for the card's own save chip. */
      cardPrice(card) {
        var c = this.candidate(card.dataset.stepKey, card.dataset.activeHandle);
        if (c) {
          var vid = card.dataset.activeVariantId;
          var v = vid
            ? (c.variants || []).find(function (x) {
                return String(x.id) === String(vid);
              })
            : null;
          return v ? v.price : c.price;
        }
        return parseInt(card.dataset.price, 10) || 0;
      }

      commit(card) {
        if (!card || card.dataset.available === 'false') return;

        // A multi-variant product cannot fill a box yet: TENTATIVE even when only
        // one variant is in stock, because auto-picking hides a chooser the
        // shopper can see is there and they never learn what they bought.
        if (!card.dataset.activeVariantId) {
          this.tentativeCard = card;
          this.renderCards();
          var chip = card.querySelector('[data-xset-variant]:not([disabled])');
          if (chip) {
            chip.setAttribute('tabindex', '0');
            chip.focus({ preventScroll: true });
          }
          var pointed = this.candidate(card.dataset.stepKey, card.dataset.activeHandle);
          this.announce(
            (pointed ? pointed.kisaAd : shortTitle(card.dataset.productTitle)) +
              ' için ' +
              (card.dataset.optionName || 'seçenek') +
              ' seçin.'
          );
          return;
        }

        var slot = this.activeSlot;
        var sel = this.readCard(card);
        var fromExtras = false;
        // An extras box being edited that hands its product over to a tray
        // box (below). Set only in the edit branch.
        var moveFrom = null;

        // EDITING, and this is the product already in the box: "Setinde
        // kalsın" (plan §4.2 F). The box keeps its product and becomes the
        // shopper's own pick — an "Önerimiz" box chosen here is no longer a
        // suggestion. This used to re-commit the same product and announce
        // "sete eklendi" for an add that never happened ("Bir daha ekle").
        if (slot && slot.selection && this.isCurrentCard(card, slot)) {
          return this.keepCurrent(slot);
        }

        if (!slot) {
          var pending = this.pendingExtraCategory;
          if (!pending) return;
          var key = pending.key;
          var label = pending.label;

          // A product from the extras list that is also a candidate of a tray
          // step belongs to that step: it fills that step's empty box, or
          // takes a second box of that step — counted against the step's own
          // limit, not the extras'. A litter box picked from "Sete ekstra
          // ürün ekle" beside an empty "Kedi Tuvaleti" box filled a new extra
          // box and left the skeleton box empty.
          if (!this.isTrayStep(key)) {
            fromExtras = true;
            var home = this.trayHomeFor(sel.productId, sel.handle);
            if (home) {
              var homeSel = this.selectionFor(home.key, home.uid, sel.handle, sel.variantId);
              if (homeSel) {
                key = home.key;
                label = home.label;
                sel = homeSel;
              }
            }
          }

          // Fill an empty box of this category before opening a new one. Someone
          // who taps "+", picks a water bowl, and gets a SECOND water-bowl box
          // while the empty "Su Kabı" box sits right beside it has been given the
          // opposite of what they asked for.
          slot = this.state.slots.find(function (s) {
            return s.categoryKey === key && !s.selection;
          });

          var refusal = this.pickRefusal(sel.productId, slot || null, !slot);
          if (!refusal && !slot && this.categoryFull(key)) refusal = this.limitText(key, label);
          if (refusal) {
            this.noteInPicker(refusal);
            return;
          }
          // Only now is a new box the right answer — the checks above are
          // what the chooser already greys out, so reaching them means the
          // tray changed underneath an open picker.
          if (!slot) slot = this.createExtraSlot(key, label);
        } else {
          // Editing an EXTRAS box with a product that belongs to a tray step
          // whose own box is empty: the product goes to that box and the
          // extras box goes away — the same routing an add from the extras
          // list gets. Swapping a fountain into the extras box beside an
          // empty "Su Pınarı" box left the skeleton box empty.
          if (!this.isTrayStep(slot.categoryKey)) {
            var editHome = this.trayHomeFor(sel.productId, sel.handle);
            var homeBox =
              editHome &&
              this.state.slots.find(function (s) {
                return s.categoryKey === editHome.key && !s.selection;
              });
            var movedSel = homeBox && this.selectionFor(editHome.key, editHome.uid, sel.handle, sel.variantId);
            if (movedSel) {
              moveFrom = slot;
              slot = homeBox;
              sel = movedSel;
            }
          }
          // Counted without the box being edited (or vacated): its product
          // leaves the set, and no line is added either way.
          var why = this.pickRefusal(sel.productId, moveFrom || slot, false);
          if (why) {
            this.noteInPicker(why);
            return;
          }
        }

        // Captured BEFORE the assignment below. It is the whole trigger
        // predicate for auto-advance: an empty box being filled is progress, a
        // filled one being changed is editing. The three routes into the picker
        // — an empty box (onSlotClick), the slot menu's "Değiştir", and the
        // error panel's repair button — all land in openPickerForSlot and are
        // indistinguishable from here; this is the fact that separates them.
        var wasEmpty = !slot.selection;
        var replaced = slot.selection;

        if (moveFrom) {
          // What the shopper replaced is the extras box's product; the box
          // it leaves is removed the way emptySlot() removes an extra one.
          replaced = moveFrom.selection;
          wasEmpty = false;
          moveFrom.selection = null;
          moveFrom.el.remove();
          this.state.slots = this.state.slots.filter(function (s) {
            return s !== moveFrom;
          });
        }

        slot.selection = sel;
        this.tentativeCard = null;
        // Same reasoning as emptySlot: this box now holds something else, so any
        // standing sold-out verdict against it — and the banner that named the
        // old product — is stale.
        slot.el.classList.remove('is-stale');
        this.hideError();
        this.renderSlot(slot);
        // A replacement says what the set costs now (plan §4.2 F: "Mama Kabı
        // X8605 ile değiştirildi: setin 22.712,35 TL.").
        var newCode = (this.candidate(slot.categoryKey, slot.selection.handle) || {}).kod;
        this.announce(
          replaced
            ? nameOf(slot.selection) + (newCode ? ' ' + newCode : '') + ' ile değiştirildi: setin ' + money(this.displayTotal()) + '.'
            : nameOf(slot.selection) + ' sete eklendi.'
        );
        // "Ekle" closed the extras list; the status line offers the way back
        // in for as long as it is still reporting this add (renderStatus).
        this.lastExtraKey = fromExtras ? this.pendingExtraCategory.key : null;
        this.statusMore = fromExtras;

        // Persist and render BEFORE the close, not after: the close takes a
        // few hundred milliseconds and a shopper who navigates inside that
        // window should still find their choice saved.
        this.persist();
        this.render();

        this.pickedSinceOpen = true;
        this.track('xset_pick', {
          adim: slot.categoryKey,
          urun: sel.handle,
          varyant: sel.variantId,
          fiyat: lira(sel.price),
          islem: replaced ? 'degistir' : 'ekle',
          kaynak: this.autoOpened ? 'otomatik_gecis' : fromExtras ? 'ekstra_listesi' : 'manuel',
        });

        if (this.autoOpened) this.committedSinceAutoOpen = true;

        // The feedback waits for the picker to get out of the way. It used to
        // fire here, with the modal still covering the tray at both breakpoints
        // — on mobile the sheet, on desktop the drawer plus its overlay — so
        // the pop played to nobody. onPickerHidden() picks this up.
        this.pendingFill = { slot: slot, advance: this.shouldAdvance(slot, wasEmpty), more: fromExtras };
        this.closePicker();

        // The theme hands focus back to the trigger that opened the picker —
        // the extras box that no longer exists. The box the product moved to
        // is where the shopper continues.
        if (moveFrom) {
          var homeTrigger = slot.el.querySelector('[data-xset-slot-trigger]');
          if (homeTrigger) {
            setTimeout(function () {
              if (!document.activeElement || document.activeElement === document.body) homeTrigger.focus({ preventScroll: true });
            }, 400);
          }
        }
      }

      /* Whether filling this slot should open the next empty category.

         Everything that does NOT route through commit() is excluded
         structurally rather than by a flag — Otomatik (runAuto) and applySnapshot() both
         assign `slot.selection` directly and never reach this method. That is
         the arrangement worth keeping: a predicate can be got wrong, a code
         path that is never taken cannot. */
      shouldAdvance(slot, wasEmpty) {
        if (!this.autoAdvance || this.advanceDismissed || this.hydrating) return false;
        // Reduced motion turns this off, and not out of caution. The advance is
        // built on a beat — the box pops, you see it land, then the next sheet
        // opens. Under reduced motion highlight() skips the pop, so there is no
        // beat left and the advance becomes a jump cut: a full-screen surface
        // appearing unbidden, which is the closest thing to what that
        // preference asks us not to do.
        if (reducedMotion()) return false;
        if (window.Shopify && window.Shopify.designMode) return false;
        // An extra box is an addition on top of a finished thought, not a step.
        if (!slot || slot.isExtra || !wasEmpty) return false;
        return !!this.nextAdvanceTarget();
      }

      /* The next empty default box that has something to offer.

         Barren categories are SKIPPED rather than opened. Auto-opening a sheet
         whose entire body reads "Bu kategoride şu an stokta ürün yok." is the
         worst possible advance — an interruption that exists to show you
         nothing. "Something to offer" is a pickable, in-stock candidate in the
         manifest — and a panel to show it in. */
      nextAdvanceTarget() {
        var self = this;
        var found = null;
        this.defaultSlots().some(function (slot) {
          if (slot.selection) return false;
          if (!self.panelFor(slot.categoryKey)) return false;
          if (!self.pickable(slot.categoryKey).length) return false;
          // Under requireAll, never back to a box nothing can go into.
          if (self.requireAll && !self.fillable(slot)) return false;
          found = slot;
          return true;
        });
        return found;
      }


      /* Says no where the shopper is looking.

         announce() writes to the builder's status line, which is on the page
         BEHIND this modal — so a refusal issued while the picker is open was
         announced to a screen reader and to nobody else. Pressing "Seç" looked
         like it did nothing at all. */
      noteInPicker(message) {
        if (!this.pickerNoteEl) return this.announce(message);
        this.pickerNoteEl.textContent = message || '';
        this.pickerNoteEl.hidden = !message;
      }

      clearPickerNote() {
        if (!this.pickerNoteEl) return;
        this.pickerNoteEl.textContent = '';
        this.pickerNoteEl.hidden = true;
      }

      /* How many boxes of a category the tray may hold in total — skeleton and
         extras together. The step's own `max` when it sets one; otherwise
         plan §3.2: two of a tray step (two identical bowls for two cats is
         a real case, three litter boxes in one set is not), and no per-step
         cap off the tray, where the shared sarf + ekstra cap applies instead
         (categoryFull). */
      categoryMax(key) {
        var step = this.stepInfo(key);
        var max = step ? parseInt(step.max, 10) : NaN;
        if (!(max > 0)) {
          var panel = this.panelFor(key);
          max = parseInt(panel && panel.dataset.categoryMax, 10);
        }
        if (max > 0) return max;
        return this.isTrayStep(key) ? MAX_PER_STEP : Infinity;
      }

      /* A skeleton step — one with its own box on the tray. Sarf and ekstra
         steps are not. Unknown keys count as off-tray: the stricter cap. */
      isTrayStep(key) {
        var step = this.stepInfo(key);
        return !!(step && step.tepside);
      }

      categoryUsed(key) {
        return this.state.slots.filter(function (s) {
          return s.categoryKey === key && s.selection;
        }).length;
      }

      /* Filled boxes of every off-tray step together (plan §3.2 "Sarf +
         ekstra en fazla 3"). */
      extrasUsed() {
        var self = this;
        return this.state.slots.filter(function (s) {
          return s.selection && !self.isTrayStep(s.categoryKey);
        }).length;
      }

      categoryFull(key) {
        if (this.categoryUsed(key) >= this.categoryMax(key)) return true;
        return !this.isTrayStep(key) && this.extrasUsed() >= MAX_EXTRAS;
      }

      /* The sentence for a refused category (plan §3.2 texts). */
      limitText(key, label) {
        if (!this.isTrayStep(key) && this.extrasUsed() >= MAX_EXTRAS && this.categoryUsed(key) < this.categoryMax(key)) {
          return 'Sette en fazla ' + MAX_EXTRAS + ' ek ürün olabilir.';
        }
        return label + ' kategorisinden en fazla ' + this.categoryMax(key) + ' ürün ekleyebilirsin.';
      }

      /* ------------------------------------------- set limits (plan §3.2) */

      /* Boxes holding this product, other than `except`. Per PRODUCT, so two
         colours of one fountain are two products. */
      productUsed(productId, except) {
        var pid = String(productId || '');
        if (this.colourCap) {
          var self = this;
          var group = this.groupOf(pid);
          return this.state.slots.filter(function (s) {
            return s !== except && s.selection && self.groupOf(s.selection.productId) === group;
          }).length;
        }
        return this.state.slots.filter(function (s) {
          return s !== except && s.selection && String(s.selection.productId) === pid;
        }).length;
      }

      /* A product's colour group: the column it rides in (`ana`), else its
         own handle; a product the manifest does not know is its own group. */
      groupOf(productId) {
        var pid = String(productId || '');
        var hits = this.manifestByProduct && this.manifestByProduct[pid];
        var c = hits && hits[0] && hits[0].c;
        return c ? c.ana || c.handle : 'id:' + pid;
      }

      lineFull() {
        return this.pieceCount() >= MAX_LINES;
      }

      lineFullText() {
        return 'Setin dolu: en fazla ' + MAX_LINES + ' ürün. Önce birini çıkar.';
      }

      /* Why putting this product into `slot` (or into a new box, `newBox`)
         would break a set limit — or '' when it would not. `slot` is the box
         being filled or replaced: replacing never adds a line, and the
         product already in it does not count against itself. */
      pickRefusal(productId, slot, newBox) {
        if (this.productUsed(productId, slot) >= MAX_PER_PRODUCT) {
          return 'Bu üründen sette en fazla ' + MAX_PER_PRODUCT + ' tane olabilir.';
        }
        var addsLine = newBox || !slot || !slot.selection;
        if (addsLine && this.lineFull()) return this.lineFullText();
        return '';
      }

      /* Why "Ekle" on this extras-list row would be refused right now, or ''
         — the same routing and checks pick() runs for it (a product of a tray
         step goes to that step's empty box or a new box of that step, and
         counts against that step's cap), so the row's state and the refusal
         note can never disagree. */
      rowRefusal(card) {
        var pending = this.pendingExtraCategory;
        if (!pending) return '';
        var key = pending.key;
        var label = pending.label;
        var pid = card.dataset.activeProductId || card.dataset.productId;
        if (!this.isTrayStep(key)) {
          var home = this.trayHomeFor(pid, card.dataset.activeHandle || card.dataset.productHandle);
          if (home) {
            key = home.key;
            label = home.label;
          }
        }
        var slot = this.state.slots.find(function (s) {
          return s.categoryKey === key && !s.selection;
        });
        var refusal = this.pickRefusal(pid, slot || null, !slot);
        if (!refusal && !slot && this.categoryFull(key)) refusal = this.limitText(key, label);
        return refusal;
      }

      /* The tray step a product of the extras pool belongs to, if any: the
         first tray step that has it as a candidate. `uid` is that step's
         column identity for it (a colour sibling's column is its `ana`). */
      trayHomeFor(productId, handle) {
        var self = this;
        var hits = (this.manifestByProduct && this.manifestByProduct[String(productId)]) || [];
        var hit = hits.find(function (h) {
          return self.isTrayStep(h.key) && h.c && h.c.available;
        });
        if (!hit) return null;
        return { key: hit.key, uid: hit.c.ana || hit.c.handle || handle, label: hit.step.baslik || hit.key };
      }

      /* Editing: is this column the product already in the box — the same
         column, colour and variant? */
      isCurrentCard(card, slot) {
        var sel = slot && slot.selection;
        if (!sel || !card) return false;
        if (card.dataset.productHandle !== sel.uid) return false;
        if (card.dataset.activeHandle && card.dataset.activeHandle !== sel.handle) return false;
        var vid = card.dataset.activeVariantId;
        return !vid || String(vid) === String(sel.variantId);
      }

      /* What the page would show as the set's total with `sel` in `slot`
         instead of what is there — through displayTotal() itself, so a
         ready-set match the swap makes or breaks, and the ladder with it,
         are in the figure. A tray that sells as a ready set for 21.999 and
         stops matching when its mama kabı changes really goes up by more
         than the two bowls' price difference, and "Setin +X TL" has to say
         so. Nothing is rendered or persisted; the ready-set cache is put
         back as it was. */
      totalWithSwap(slot, sel) {
        var keep = slot.selection;
        var cache = this.readyCache;
        slot.selection = sel;
        try {
          return this.displayTotal();
        } finally {
          slot.selection = keep;
          this.readyCache = cache;
        }
      }

      /* The selection a column stands for, priced the way cardPrice() prices
         it — enough for totalWithSwap(): the product, its price, the colour
         and variant on show. A column whose variant is not chosen yet still
         has a product and a price. */
      swapCandidate(card, price) {
        var sel = card.dataset.activeVariantId ? this.readCard(card) : null;
        if (sel) return sel;
        return {
          uid: card.dataset.productHandle,
          handle: card.dataset.activeHandle || card.dataset.productHandle,
          productId: String(card.dataset.activeProductId || card.dataset.productId || ''),
          price: price,
          variantId: card.dataset.activeVariantId || '',
          setDisi: false,
        };
      }

      /* "Setinde kalsın": nothing changes but who chose it. */
      keepCurrent(slot) {
        if (slot.selection.kaynak !== 'secim') {
          slot.selection.kaynak = 'secim';
          this.persist();
        }
        this.tentativeCard = null;
        this.pickedSinceOpen = true;
        this.announce(nameOf(slot.selection) + ' setinde kaldı.');
        this.render();
        this.closePicker();
      }

      /* "+ 1 tane daha ekle" (modal eki 6): a second box of the same
         category with the same product, colour and variant — "birden fazla
         kedin varsa". Within the limits: 2 per product, the category's own
         cap, 6 lines. */
      addAnother(card) {
        var slot = this.editingSlot();
        if (!slot || !card || !this.isCurrentCard(card, slot)) return;
        var refusal = this.againRefusal(slot);
        if (refusal) {
          this.noteInPicker(refusal);
          return;
        }
        var target = this.state.slots.find(function (s) {
          return s.categoryKey === slot.categoryKey && !s.selection;
        });
        if (!target) target = this.createExtraSlot(slot.categoryKey, slot.label);
        var copy = {};
        Object.keys(slot.selection).forEach(function (k) {
          copy[k] = slot.selection[k];
        });
        copy.kaynak = 'secim';
        slot.selection.kaynak = 'secim';
        target.selection = copy;
        this.tentativeCard = null;
        this.renderSlot(target);
        this.announce(nameOf(copy) + ' sete bir tane daha eklendi.');
        this.persist();
        this.render();
        this.pickedSinceOpen = true;
        this.track('xset_pick', {
          adim: target.categoryKey,
          urun: copy.handle,
          varyant: copy.variantId,
          fiyat: lira(copy.price),
          islem: 'bir_daha',
          kaynak: 'manuel',
        });
        this.pendingFill = { slot: target, advance: false };
        this.closePicker();
      }

      /* Why "+ 1 tane daha ekle" cannot run for the box being edited, or ''. */
      againRefusal(slot) {
        if (!slot || !slot.selection) return '';
        if (this.lineFull()) return this.lineFullText();
        if (this.productUsed(slot.selection.productId) >= MAX_PER_PRODUCT) {
          return 'Bu üründen sette en fazla ' + MAX_PER_PRODUCT + ' tane olabilir.';
        }
        var hasEmpty = this.state.slots.some(function (s) {
          return s.categoryKey === slot.categoryKey && !s.selection;
        });
        if (!hasEmpty && this.categoryFull(slot.categoryKey)) {
          return 'Bu kategoriden en fazla ' + this.categoryMax(slot.categoryKey) + ' tane eklenebilir.';
        }
        return '';
      }

      /* Reflected on the pick button so an already-chosen product does not look
         like a switch that is already flipped. */
      isChosenHandle(handle) {
        return this.state.slots.some(function (s) {
          return s.selection && s.selection.uid === handle;
        });
      }

      /* The category chooser must not offer a door that is locked.

         It listed every step regardless, so with the skeleton filled three of
         its four options could only ever refuse — and the refusal was invisible
         (see noteInPicker). Marked here instead, before the tap. */
      renderChooser() {
        if (!this.picker) return;
        var self = this;

        /* The lead. Opening this chooser means adding a piece, and the one fact
           that decides whether to bother is what that piece earns. The page says
           so behind the modal; the modal covers the page. */
        var lead = this.picker.querySelector('[data-xset-choose-lead]');
        if (lead) {
          var pct = this.tierFor(this.pendingPosition());
          var reward = pct > 0;
          setText(
            lead,
            reward
              ? 'Bu ürün setinin ' + this.pendingPosition() + '. parçası — %' + pct + ' indirimli'
              : 'Sete ne eklemek istersiniz?'
          );
          lead.classList.toggle('is-reward', reward);
        }

        Array.prototype.forEach.call(this.picker.querySelectorAll('[data-xset-choose-category]'), function (btn) {
          var key = btn.dataset.xsetChooseCategory;
          var full = self.categoryFull(key);
          btn.classList.toggle('is-full', full);
          setAttr(btn, 'aria-disabled', full ? 'true' : 'false');

          /* What is behind the door: the models the panel will actually show
             (columns, not the whole manifest — that would promise shelves the
             shopper cannot reach yet) and the cheapest in-stock price among
             them and their colours. */
          var metaEl = btn.querySelector('[data-xset-choose-meta]');
          if (metaEl) {
            var shown = self.pickable(key);
            var n = shown.filter(function (c) {
              return !c.ana;
            }).length;
            var floor = 0;
            shown.forEach(function (c) {
              var price = self.floorPrice(c);
              if (price && (!floor || price < floor)) floor = price;
            });
            var parts = [];
            // A list door (extras, consumables) holds products, not models
            // of one device — the panel behind it counts "N ürün" too.
            var info = self.stepInfo(key);
            if (n) parts.push(n + (info && !info.tepside ? ' ürün' : ' model'));
            if (floor) parts.push(money(floor) + "'den");
            setText(metaEl, parts.join(' · '));
          }

          /* Choosing a second bowl is allowed (up to categoryMax), so this
             is a note, not a block. It only makes the unusual choice a
             deliberate one. */
          var ownedEl = btn.querySelector('[data-xset-choose-owned]');
          if (ownedEl) {
            var used = self.categoryUsed(key);
            setHidden(ownedEl, used < 1);
            if (used) setText(ownedEl, 'Sette ' + used + ' var');
          }
        });
      }

      /* The answer a shopper needs before touching anything: what this costs.
         Derived from the manifest, so it is never a number someone has to
         remember to update. Run once at connect — the candidate list does not
         change during a session.

         There is no combined star rating any more (plan §8.5): an average
         across every candidate describes no product anyone can buy. Ratings
         stay per product, in the comparison. */
      renderFacts() {
        this.renderFromPrice();
      }

      /* The floor: cheapest available candidate in each default category, with
         the set discount applied if that many pieces earns it. It is the honest
         smallest number this page can produce, which is exactly what a shopper
         weighing whether to start needs. The same models are what the delivery
         pill speaks for while the tray is empty (plan §8.1), so their handles
         and total are kept for renderPromise(). */
      renderFromPrice() {
        var self = this;
        var defaults = this.defaultSlots();
        if (!defaults.length) return;

        // Cheapest pickable, in-stock price per default category, from the
        // manifest. A category with nothing to pick leaves the floor unsaid —
        // a floor that skipped a category would understate the set.
        var floors = [];
        var handles = [];
        var complete = defaults.every(function (slot) {
          var best = null;
          self.pickable(slot.categoryKey).forEach(function (c) {
            var price = self.floorPrice(c);
            if (price > 0 && (!best || price < best.price)) best = { price: price, handle: c.handle };
          });
          if (!best) return false;
          floors.push(best.price);
          handles.push(best.handle);
          return true;
        });
        if (!complete || !floors.length) return;

        // The floor walks the same ladder the basket will (none in Sade mod),
        // cheapest first, so the deepest rungs land where they actually would.
        var n = defaults.length;
        var total = floors.reduce(function (sum, price, i) {
          var pct = self.tierFor(i + 1);
          return sum + price - (pct ? Math.round((price * pct) / 100) : 0);
        }, 0);
        if (!total) return;

        // Only the delivery pill reads it now: the "N parçalık set …'den
        // başlıyor" line under the heading was removed (owner, 2026-10-13).
        this.floorSet = { handles: handles, total: total };
      }

      /* ------------------------------------------------------ delivery pill */

      /* THE DELIVERY PILL (plan §8.1). When the set leaves the warehouse, and
         "Ücretsiz kargo" when it clears the store's threshold — nothing else.

         Dispatch comes from shipping-promise.js, the same table and
         per-product holds (DISPATCH_NOT_BEFORE) the product pages and cards
         use, plus this section's holiday list. A set leaves when its LATEST
         product does, so every product on the tray is resolved (the ready set
         too, when the tray sells as one) and the latest dispatch date wins.
         On an empty tray the pill speaks for the cheapest model of every
         default box — the same set the "…'den başlıyor" floor prices.

           today    "Bugün 15:00'e kadar sipariş ver, bugün kargoda"
                    phone: "Bugün kargoda"
           later    the helper's shared line, "En geç pazartesi kargoda" /
                    "Çarşamba günü kargoda"; phone: "Pzt kargoda"
           free     "· Ücretsiz kargo" (desktop) / second line (phone), only
                    when the tray's total (or the floor) ≥ the threshold

         No asterisk and no footnote under the CTA any more (owner,
         2026-10-13: "*Stoktaki ürünler için; paketler ayrı çıkabilir." —
         "kaldır"). Nothing is shown if the helper did not load — no promise
         beats a wrong one. */
      renderPromise() {
        if (!this.promiseEl) return;
        var SP = window.ShippingPromise;
        var filled = this.filledSlots();
        var handles = [];
        var total = 0;

        if (filled.length) {
          filled.forEach(function (slot) {
            if (slot.selection.handle) handles.push(slot.selection.handle);
          });
          var match = this.readyState().match;
          if (match && match.handle) handles.push(match.handle);
          total = this.displayTotal();
        } else if (this.floorSet) {
          handles = this.floorSet.handles.slice();
          total = this.floorSet.total;
        }

        if (!SP || typeof SP.resolve !== 'function') {
          this.setPromiseShown(false);
          return;
        }

        var self = this;
        var now = new Date();
        var latest = null;
        (handles.length ? handles : ['']).forEach(function (handle) {
          var r = SP.resolve({ handle: handle, now: now, holidays: self.holidays });
          // dispatchDate is ISO, so plain string order is date order. Without
          // it (an older cached helper) fall back to "anything not today".
          if (!latest) {
            latest = r;
          } else if (r.dispatchDate && latest.dispatchDate) {
            if (r.dispatchDate > latest.dispatchDate) latest = r;
          } else if (latest.shipsToday && !r.shipsToday) {
            latest = r;
          }
        });

        var long = '';
        var short = '';
        if (latest.shipsToday) {
          var at = cutoffLabel(latest.cutoffHour, latest.secondsUntilCutoff, now);
          long = at ? 'Bugün ' + at + ' kadar sipariş ver, bugün kargoda' : 'Bugün kargoda';
          short = 'Bugün kargoda';
        } else if (latest.deadlineText) {
          long = latest.deadlineText.replace(/!\s*$/, '');
          short = shortDispatch(latest, now) || long;
        }
        if (!long) {
          this.setPromiseShown(false);
          return;
        }

        if (this.promiseLongEl && this.promiseLongEl.textContent !== long) this.promiseLongEl.textContent = long;
        if (this.promiseShortEl && this.promiseShortEl.textContent !== short) this.promiseShortEl.textContent = short;
        if (this.promiseFreeEl) {
          this.promiseFreeEl.hidden = !(this.freeShippingMin > 0 && total >= this.freeShippingMin);
        }
        this.setPromiseShown(true);
      }

      /* The pill keeps its box while unworded (visibility, not display), so
         the row never changes shape when the JS arrives. */
      setPromiseShown(shown) {
        this.promiseEl.classList.toggle('is-ready', shown);
        if (shown) this.promiseEl.removeAttribute('aria-hidden');
        else this.promiseEl.setAttribute('aria-hidden', 'true');
      }

      /* ------------------------------------------------------- otomatik set */

      /* OTOMATİK SET AND THE SUGGESTION STRIP (plan §7).

         The button:
           1. no default box filled → the first "oneri" block, whole
              ("En çok tercih edilen set yüklendi: …");
           2. first press with the shopper's own picks on the tray → that
              suggestion fills ONLY the empty or suggested boxes ("Senin
              seçtiğin X8623 yerinde; …"), with "Hepsini yeniden öner";
           3. every later press → a new combination for the boxes that are not
              the shopper's own, weighted by which products the store sells
              together, never one already shown, its total within ±25% of the
              current one.
         The shopper's own picks (kaynak 'secim' or 'bas') are never touched
         by the button. Only an explicit choice replaces them: a chip in the
         strip, "Başka öneri", or "Hepsini yeniden öner". Extras of the
         extras pool stay through all of it.

         Every one of those leaves a "Geri al" that puts the tray back exactly
         as it was. It has no timeout: it stands until the tray changes in
         any other way (autoUndo.sig is the tray it undoes FROM).

         Prices always come from the engine — engineTotal(), the same figure
         the CTA shows: in Sade mode the ready set's own price when the tray
         is exactly an in-stock ready set, else the plain sum; with the
         ladder on, the sum less the ladder. Nothing reads a price off a
         block. */

      isManual(sel) {
        return !!sel && sel.kaynak !== 'oneri';
      }

      /* A filled box as a resolved pick placePicks() can lay again. */
      slotPick(slot) {
        var sel = slot && slot.selection;
        if (!sel) return null;
        var c = this.candidate(slot.categoryKey, sel.handle);
        if (!c) return null;
        var v = (c.variants || []).find(function (x) {
          return String(x.id) === String(sel.variantId);
        });
        if (!v) return null;
        return { key: slot.categoryKey, c: c, v: v, kaynak: sel.kaynak || 'secim' };
      }

      /* A record's picks as selections, in tray order — what the engine
         prices. Picks that no longer resolve are left out, as applySnapshot()
         would leave them out. */
      snapSels(snap) {
        var self = this;
        var out = [];
        ((snap && snap.slots) || []).forEach(function (s) {
          if (!s || !s.pick) return;
          var sel = self.selectionFor(s.categoryKey, s.pick.uid, s.pick.handle || s.pick.uid, s.pick.variantId);
          if (sel) out.push(sel);
        });
        return out;
      }

      /* A record's identity as a multiset of variants: what "the same set"
         means for the pressed chip and for "never shown twice". */
      contentSig(snap) {
        return ((snap && snap.slots) || [])
          .filter(function (s) {
            return s && s.pick && s.pick.variantId;
          })
          .map(function (s) {
            return String(s.pick.variantId);
          })
          .sort()
          .join(',');
      }

      /* The tray a set of fresh picks would make.

         mode 'fill' (the button): a fresh pick goes only where the box is
         empty or holds a suggestion; the shopper's own picks stay.
         mode 'all' (a chip, "Başka öneri", "Hepsini yeniden öner"): every
         box of a step the fresh picks cover is replaced, the shopper's own
         included.
         In both, a step the fresh picks do not cover keeps what it holds —
         a suggestion without a water bowl does not empty the water bowl box,
         and the extras pool is never covered — except with
         `opts.replaceSuggestions` (a complete preset chip): an uncovered box
         holding an earlier SUGGESTION is emptied, so suggestions replace
         each other while the shopper's own picks stay. The kept picks are laid first,
         so the set limits (placePicks) can only ever cut a fresh pick.
         `clearExtras` (Otomatik): the extra boxes are not kept either — the
         button builds a set, not around the shopper's extras. */
      planWith(fresh, mode, clearExtras, opts) {
        var replaceSuggestions = !!(opts && opts.replaceSuggestions);
        var self = this;
        var covered = {};
        fresh.forEach(function (p) {
          covered[p.key] = true;
        });
        var kept = [];
        var keptPerKey = {};
        this.state.slots.forEach(function (slot) {
          if (!slot.selection) return;
          if (clearExtras && slot.isExtra) return;
          // A set part (registerParts) came with its ready set and belongs to
          // it: a new suggestion replaces the products it fits, so it goes too.
          var held = self.candidate(slot.categoryKey, slot.selection.handle || slot.selection.uid);
          if (held && held.parca) return;
          var keep = !covered[slot.categoryKey] || (mode === 'fill' && self.isManual(slot.selection));
          // A new suggestion replaces the last one whole: in 'all' mode a box
          // the fresh picks do not cover keeps only what the shopper chose
          // themselves. (A family whose suggestions use different boxes —
          // Bebek: Uyku Takip vs Yenidoğan — would otherwise pile the
          // previous suggestion's humidifier onto the next one's price.)
          if (keep && replaceSuggestions && !covered[slot.categoryKey] && !self.isManual(slot.selection)) keep = false;
          if (!keep) return;
          var p = self.slotPick(slot);
          if (!p) return;
          kept.push(p);
          keptPerKey[p.key] = (keptPerKey[p.key] || 0) + 1;
        });
        var add = [];
        var addedPerKey = {};
        fresh.forEach(function (p) {
          if (mode === 'fill') {
            var room = self.isTrayStep(p.key)
              ? self.defaultSlots().filter(function (s) {
                  return s.categoryKey === p.key;
                }).length
              : 1;
            if ((keptPerKey[p.key] || 0) + (addedPerKey[p.key] || 0) >= room) return;
          }
          addedPerKey[p.key] = (addedPerKey[p.key] || 0) + 1;
          add.push({ key: p.key, c: p.c, v: p.v, kaynak: 'oneri' });
        });
        var placed = this.placePicks(kept.concat(add), this.state.setId);
        // Kept picks are laid first, so anything placePicks cut is fresh: a
        // suggestion that no longer fits whole (the shopper's own extras
        // fill the places it needs).
        return { snapshot: placed.snapshot, added: add.length, cut: placed.overflow || 0 };
      }

      /* A plan, priced. */
      pricePlan(plan) {
        var sels = this.snapSels(plan.snapshot);
        var priced = this.engineTotal(sels);
        plan.sels = sels;
        plan.total = priced.total;
        plan.ready = priced.ready;
        plan.sig = this.contentSig(plan.snapshot);
        plan.cut = plan.cut || 0;
        // What a chip shows: the suggestion's own price — the engine's total
        // for the suggested products alone, without the shopper's own picks
        // the plan merely keeps (an extra, a care product, a box the
        // suggestion leaves alone). Read as the suggestion's price, a chip
        // must not jump by a 23.999 TL dryer the shopper added, nor drift
        // when an own product happens to make a different set with it.
        var ownKept = ((plan.snapshot && plan.snapshot.slots) || []).filter(function (s) {
          return s && s.pick && s.pick.kaynak !== 'oneri';
        }).length;
        plan.ownKept = ownKept;
        var own = ownKept > 0;
        if (!own) {
          plan.chipTotal = plan.total;
        } else {
          var fresh = { slots: ((plan.snapshot && plan.snapshot.slots) || []).filter(function (s) {
            return s && s.pick && s.pick.kaynak === 'oneri';
          }) };
          plan.chipTotal = this.engineTotal(this.snapSels(fresh)).total;
        }
        return plan;
      }

      presetPlan(preset, mode, clearExtras) {
        if (!preset) return null;
        var res = this.presetPicks(preset, 'oneri');
        if (!res.picks.length) return null;
        // A chip replaces the last suggestion whole — unless one of its own
        // products is out of stock: then the step it would have filled is
        // not "left out on purpose", and the box keeps what it holds.
        var whole = !res.missing && !(parseInt(preset.eksik, 10) || 0);
        return this.pricePlan(this.planWith(res.picks, mode, clearExtras, { replaceSuggestions: mode === 'all' && whole }));
      }

      /* The tray without its extra boxes: the set itself. */
      coreSnapshot(snap) {
        snap = snap || this.snapshot();
        return {
          setId: snap.setId,
          slots: (snap.slots || []).filter(function (s) {
            return s && !s.isExtra;
          }),
        };
      }

      /* Which products the store sells together, as pair weights: every
         ready set of the family counts each pair of its components once,
         every named suggestion twice (they are the best sellers). Read from
         data the page already has — no handle is named here — and built
         once. */
      pairWeights() {
        if (this.pairCache) return this.pairCache;
        var w = {};
        var addPairs = function (ids, n) {
          var u = ids.map(String).filter(function (id, i, a) {
            return a.indexOf(id) === i;
          });
          for (var i = 0; i < u.length; i += 1) {
            for (var j = i + 1; j < u.length; j += 1) {
              var k = u[i] < u[j] ? u[i] + ',' + u[j] : u[j] + ',' + u[i];
              w[k] = (w[k] || 0) + n;
            }
          }
        };
        (this.readySets || []).forEach(function (s) {
          addPairs(s.ids, 1);
        });
        var self = this;
        (this.presets || []).forEach(function (p) {
          addPairs(
            self.presetPicks(p, 'oneri').picks.map(function (x) {
              return x.c.productId;
            }),
            2
          );
        });
        this.pairCache = w;
        return w;
      }

      comboWeight(sels) {
        var w = this.pairWeights();
        var ids = sels.map(function (s) {
          return String(s.productId);
        });
        var score = 1;
        for (var i = 0; i < ids.length; i += 1) {
          for (var j = i + 1; j < ids.length; j += 1) {
            var k = ids[i] < ids[j] ? ids[i] + ',' + ids[j] : ids[j] + ',' + ids[i];
            score += w[k] || 0;
          }
        }
        return score;
      }

      /* The dice's faces for one step: in-stock candidates with a single
         variant, colour siblings folded into their column — the same rule
         commit() follows, never auto-picking a variant. */
      rollPool(key) {
        return this.pickable(key).filter(function (c) {
          return !c.ana && (c.variants || []).length === 1 && c.variants[0].available;
        });
      }

      /* A new combination for the default boxes `mode` may replace ('fill':
         the ones that are not the shopper's own; 'all': every one). Never the
         tray as it is and never one already shown in this session; within
         ±25% of `ref` when there is a reference total, else the nearest to
         that band; weighted by pairWeights(). When every combination has been
         shown the memory starts over once. Null when nothing can change.
         `clearExtras`: as planWith; "the tray as it is" is then the tray
         without its extras, so a roll never merely clears them. */
      rollPlan(mode, ref, clearExtras) {
        var self = this;
        var keys = [];
        this.defaultSlots().forEach(function (slot) {
          if (mode === 'fill' && self.isManual(slot.selection)) return;
          // A box the family keeps out of Otomatik (`otomatik: false`).
          var info = self.stepInfo(slot.categoryKey);
          if (info && info.otomatik === false) return;
          if (keys.indexOf(slot.categoryKey) === -1 && self.rollPool(slot.categoryKey).length) keys.push(slot.categoryKey);
        });
        if (!keys.length) return null;
        var pools = keys.map(function (k) {
          return self.rollPool(k);
        });

        // Every combination when there are few (a cat tray: 4 × 8 × 4), a
        // fixed-size random sample when the product is large.
        var size = pools.reduce(function (n, p) {
          return n * p.length;
        }, 1);
        var LIMIT = 600;
        var combos = [];
        if (size <= LIMIT) {
          var idx = pools.map(function () {
            return 0;
          });
          for (var n = 0; n < size; n += 1) {
            combos.push(idx.slice());
            for (var d = idx.length - 1; d >= 0; d -= 1) {
              idx[d] += 1;
              if (idx[d] < pools[d].length) break;
              idx[d] = 0;
            }
          }
        } else {
          var seen = {};
          for (var t = 0; t < LIMIT * 3 && combos.length < LIMIT; t += 1) {
            var c = pools.map(function (p) {
              return Math.floor(Math.random() * p.length);
            });
            var ck = c.join('.');
            if (seen[ck]) continue;
            seen[ck] = true;
            combos.push(c);
          }
        }

        var current = this.contentSig(clearExtras ? this.coreSnapshot() : this.snapshot());
        var plans = [];
        var bySig = {};
        combos.forEach(function (combo) {
          var fresh = combo.map(function (ci, k) {
            var cand = pools[k][ci];
            return { key: keys[k], c: cand, v: cand.variants[0] };
          });
          var plan = self.pricePlan(self.planWith(fresh, mode, clearExtras));
          if (!plan.sels.length || plan.sig === current || bySig[plan.sig]) return;
          bySig[plan.sig] = true;
          plan.weight = self.comboWeight(plan.sels);
          plans.push(plan);
        });

        // A plan that lost a pick to a set limit leaves a box empty — two
        // boxes rolling from one pool (Çeyiz's two complements) draw the same
        // product about one roll in eight. Only when nothing else exists.
        var whole = plans.filter(function (p) {
          return !p.cut;
        });
        if (whole.length) plans = whole;

        var choose = function (shown) {
          var fresh = plans.filter(function (p) {
            return shown.indexOf(p.sig) === -1;
          });
          if (!fresh.length) return null;
          var pool = fresh;
          if (ref > 0) {
            var lo = ref * 0.75;
            var hi = ref * 1.25;
            var inBand = fresh.filter(function (p) {
              return p.total >= lo && p.total <= hi;
            });
            if (inBand.length) pool = inBand;
            else {
              // Nothing inside the band: the nearest to it, still weighted.
              var dist = function (p) {
                return p.total < lo ? lo - p.total : p.total - hi;
              };
              var best = Math.min.apply(
                Math,
                fresh.map(function (p) {
                  return dist(p);
                })
              );
              pool = fresh.filter(function (p) {
                return dist(p) === best;
              });
            }
          }
          var sum = pool.reduce(function (s, p) {
            return s + p.weight;
          }, 0);
          var r = Math.random() * sum;
          for (var i = 0; i < pool.length; i += 1) {
            r -= pool[i].weight;
            if (r < 0) return pool[i];
          }
          return pool[pool.length - 1];
        };

        var pick = choose(this.autoShown || []);
        if (!pick && plans.length) {
          this.autoShown = [current];
          pick = choose(this.autoShown);
        }
        return pick;
      }

      /* The model code a box is spoken by in the diff ("Mama Kabı X8612 →
         X8605"), else its short name cut to its head: the part before an
         " & " / " + " / " - " / comma, at most ~36 characters on a word
         boundary — "Akıllı Çelik Mama Kabı (Kameralı) & Çift Kase Kiti"
         reads "Akıllı Çelik Mama Kabı (Kameralı)". A candidate without a
         custom.kod is a data gap; the sentence should still read as one. */
      codeOf(key, sel) {
        var c = this.candidate(key, sel.handle);
        if (c && c.kod) return c.kod;
        var name = nameOf(sel);
        var head = name.split(/\s+[&+\-\u2013\u2014]\s+|,\s*/)[0].trim() || name;
        if (head.length > 36) {
          var cut = head.slice(0, 36);
          var sp = cut.lastIndexOf(' ');
          head = (sp > 12 ? cut.slice(0, sp) : cut).replace(/[\s(]+$/, '') + '…';
        }
        return head;
      }

      /* "Mama Kabı X8612 → X8605, Su Kabı X8650 → X8653" — default boxes
         only, in tray order, the ones that changed. */
      diffText(before) {
        var self = this;
        var was = {};
        (before.slots || []).forEach(function (s) {
          if (s && !s.isExtra && s.pick) was[s.uid] = s.pick;
        });
        var parts = [];
        this.defaultSlots().forEach(function (slot) {
          var sel = slot.selection;
          var old = was[slot.uid];
          if (!sel) return;
          if (old && String(old.variantId) === String(sel.variantId)) return;
          var now = self.codeOf(slot.categoryKey, sel);
          if (!old) {
            parts.push(slot.label + ' ' + now);
            return;
          }
          var oldSel = self.selectionFor(slot.categoryKey, old.uid, old.handle || old.uid, old.variantId);
          var then = oldSel ? self.codeOf(slot.categoryKey, oldSel) : '';
          parts.push(then ? slot.label + ' ' + then + ' → ' + now : slot.label + ' ' + now);
        });
        return parts.join(', ');
      }

      /* "19.799,00 TL (hazır set fiyatı)" — the tray's engine total, and
         where it came from when that is not the plain sum. */
      priceText() {
        var text = money(this.displayTotal());
        var st = this.readyState();
        if (st.match) return text + (st.rest.length ? ' (set + ' + st.rest.length + ' ürün ayrı)' : ' (set fiyatı)');
        if (this.ladderOn && this.savings() > 0) return text + ' (set indirimi dahil)';
        return text;
      }

      listText(items) {
        if (items.length <= 1) return items.join('');
        return items.slice(0, -1).join(', ') + ' ve ' + items[items.length - 1];
      }

      lowerTr(s) {
        try {
          return String(s || '').toLocaleLowerCase('tr-TR');
        } catch (e) {
          return String(s || '').toLowerCase();
        }
      }

      /* Lays a plan onto the tray and says what happened, with "Geri al". */
      runAuto(plan, info) {
        var before = this.snapshot();
        var beforeSig = this.contentSig(before);
        var wasEmpty = !this.defaultSlots().some(function (s) {
          return !!s.selection;
        });
        var prev = {};
        this.state.slots.forEach(function (s) {
          if (s.selection) prev[s.uid] = String(s.selection.variantId);
        });

        this.applySnapshot(plan.snapshot);
        this.hideError();
        this.autoUsed = true;
        if (info.preset) this.lastPresetId = info.preset.id;
        this.autoShown = this.autoShown || [];
        // The set before the press counts as shown with and without its
        // extras: Otomatik clears them, and must not roll back to it.
        [beforeSig, this.contentSig(this.coreSnapshot(before)), this.contentSig(this.snapshot())].forEach(function (sig) {
          if (sig && this.autoShown.indexOf(sig) === -1) this.autoShown.push(sig);
        }, this);

        var n = this.pieceCount();
        var price = this.priceText();
        var name = info.preset ? info.preset.baslik || 'Önerdiğimiz' : info.set ? info.set.kisaAd || shortTitle(info.set.title) : '';
        var diff = this.diffText(before);
        var msg;
        if (info.kind === 'hazir') {
          msg = name + ' kutucuklara yüklendi: ' + n + ' ürün, ' + price + '.';
        } else if (info.kind === 'doldur') {
          msg =
            'Senin seçtiğin ' +
            this.listText(info.manual) +
            ' yerinde; kalanları ' +
            this.lowerTr(name) +
            ' setle doldurduk: ' +
            price +
            '.';
        } else if (info.kind === 'baska') {
          msg = 'Başka bir öneri: ' + price + '.';
        } else if (info.kind === 'ekstrasiz') {
          msg = 'Ekstra ürünler çıkarıldı: ' + n + ' ürün, ' + price + '.';
        } else if (info.kind === 'ilk-rastgele') {
          msg = 'Sana bir set kurduk: ' + n + ' ürün, ' + price + '.';
        } else if (info.kind === 'cip-baska') {
          msg = diff && !wasEmpty ? 'Başka bir öneri yüklendi: ' + diff + '. ' + price + '.' : 'Başka bir öneri: ' + n + ' ürün, ' + price + '.';
        } else if (wasEmpty || !diff) {
          msg = name + ' set yüklendi: ' + n + ' ürün, ' + price + '.';
        } else {
          msg = name + ' set yüklendi: ' + diff + '. ' + price + '.';
        }

        this.announce(msg);
        this.autoUndo = { snap: before, sig: this.stateSig(), next: info.kind === 'doldur' };
        this.persist();
        this.render();

        // Focus stays where it was (plan §10) — except when the control that
        // was pressed hides itself: "Hepsini yeniden öner" goes once used,
        // and focus then moves to "Geri al" beside it, not to <body>.
        if (info.kind === 'yeniden' && info.fromActions && this.autoActionsEl && !this.autoActionsEl.hidden) {
          var undoBtn = this.autoActionsEl.querySelector('[data-xset-auto-undo]');
          if (undoBtn) undoBtn.focus({ preventScroll: true });
        }

        var changed = this.state.slots.filter(function (s) {
          return s.selection && prev[s.uid] !== String(s.selection.variantId);
        });
        this.staggerHighlight(changed);

        this.track(info.kind === 'hazir' ? 'xset_ready_load' : info.kind === 'cip' || info.kind === 'yeniden' ? 'xset_preset' : 'xset_auto', {
          tur: info.kind,
          oneri: info.preset ? info.preset.id : null,
          hazir_set: info.set ? info.set.handle : null,
          basis: this.autoPresses || 0,
          degisen: changed.length,
        });
      }

      /* Boxes fill one after another, 60 ms apart (plan §4.3: ≤ 500 ms in
         all); at once under reduced motion. */
      staggerHighlight(slots) {
        var self = this;
        (this.highlightTimers || []).forEach(clearTimeout);
        this.highlightTimers = [];
        if (reducedMotion()) {
          slots.forEach(function (slot) {
            self.highlight(slot);
          });
          return;
        }
        slots.slice(0, 8).forEach(function (slot, i) {
          self.highlightTimers.push(
            setTimeout(function () {
              if (slot.el.isConnected) self.highlight(slot);
            }, i * 60)
          );
        });
      }

      /* The Otomatik button (page and mobile bar alike). It builds a set:
         every press clears the extra boxes (a dryer, a filter, a ready set's
         parts) and replaces every box, the shopper's own picks included.
         The one exception is the first press after the shopper picked
         something themselves: their picks stay and the suggestion fills the
         rest; the press after that changes them too. "Geri al" brings back
         the tray as it was, extras included. */
      autoSet() {
        this.cancelAdvance();
        var defaults = this.defaultSlots();
        if (!defaults.length) return;
        this.autoPresses = (this.autoPresses || 0) + 1;
        var self = this;
        var filled = defaults.filter(function (s) {
          return !!s.selection;
        });
        var manual = filled.filter(function (s) {
          return self.isManual(s.selection);
        });
        // Picks of their own this press has not yet seen: those it keeps.
        var manualSig = manual
          .map(function (s) {
            return String(s.selection.variantId);
          })
          .sort()
          .join(',');
        var newManual = !!manual.length && manualSig !== this.autoManualSig;
        this.autoManualSig = manualSig;
        var preset = this.defaultPreset();
        var snap = this.snapshot();
        var core = this.coreSnapshot(snap);
        // What a plan must differ from: the boxes without the extras, so a
        // press never only clears the extras and leaves the set as it was.
        var coreSig = this.contentSig(core);

        // 1. An empty tray: the family's first suggestion, whole.
        if (!filled.length && preset) {
          var first = this.presetPlan(preset, 'fill', true);
          if (first && first.sig !== coreSig) return this.runAuto(first, { kind: 'ilk', preset: preset });
        }

        // 2. The first press over new picks of the shopper's own: the
        //    suggestion fills the rest and leaves theirs where they are.
        if (newManual && preset) {
          var fill = this.presetPlan(preset, 'fill', true);
          if (fill && fill.added && fill.sig !== coreSig) {
            return this.runAuto(fill, {
              kind: 'doldur',
              preset: preset,
              manual: manual.map(function (s) {
                return self.codeOf(s.categoryKey, s.selection);
              }),
            });
          }
        }

        // 3. Every later press: another combination for every box, around
        //    this set's total (extras left out — they go with the press). A
        //    tray with empty boxes has no total to stay near (one tuvalet is
        //    not the price of a set), so the band is then centred on what
        //    the family's suggestion would make of this tray.
        var ref = 0;
        if (filled.length === defaults.length) ref = this.pricePlan({ snapshot: core }).total;
        else if (preset) {
          var refPlan = this.presetPlan(preset, 'fill', true);
          ref = refPlan ? refPlan.total : 0;
        }
        var roll = this.rollPlan('all', ref, true);
        if (roll) return this.runAuto(roll, { kind: filled.length ? 'baska' : 'ilk-rastgele' });

        // No other combination: the extras still go, if there are any.
        if (coreSig !== this.contentSig(snap) && filled.length) {
          return this.runAuto(this.pricePlan({ snapshot: core }), { kind: 'ekstrasiz' });
        }

        this.autoUsed = true;
        this.announce('Şu anda başka bir öneri bulunamadı.');
        this.render();
      }

      /* A chip of the strip: an explicit choice, every box replaced. */
      choosePreset(id) {
        var preset = this.presetById(id);
        if (!preset) return;
        this.cancelAdvance();
        var plan = this.presetPlan(preset, 'all');
        if (!plan) {
          this.announce((preset.baslik || 'Bu öneri') + ' şu an kurulamıyor; ürünleri stokta değil.');
          return;
        }
        if (plan.cut) {
          this.announce(
            (preset.baslik || 'Bu öneri') + ' için sette yer yok: eklediğin ürünlerden birini çıkarınca kurabilirsin.'
          );
          return;
        }
        if (plan.sig === this.contentSig(this.snapshot())) {
          this.announce((preset.baslik || 'Bu öneri') + ' zaten setinde.');
          return;
        }
        this.runAuto(plan, { kind: 'cip', preset: preset });
      }

      /* "Başka öneri" in the strip: a new combination for every box. */
      rollAll() {
        this.cancelAdvance();
        var ref = this.pieceCount() ? this.displayTotal() : 0;
        var plan = this.rollPlan('all', ref);
        if (!plan) {
          this.announce('Şu anda başka bir öneri bulunamadı.');
          return;
        }
        this.runAuto(plan, { kind: 'cip-baska' });
      }

      /* "Hepsini yeniden öner": the next named suggestion — starting from
         the one last applied — that is not what the tray holds, every box
         replaced. No presets: a new combination for every box. */
      autoNext() {
        this.cancelAdvance();
        var list = this.presets || [];
        var current = this.contentSig(this.snapshot());
        var start = 0;
        var self = this;
        list.forEach(function (p, i) {
          if (p.id === self.lastPresetId) start = i;
        });
        for (var k = 0; k < list.length; k += 1) {
          var preset = list[(start + k) % list.length];
          var plan = this.presetPlan(preset, 'all');
          if (plan && plan.sig !== current) return this.runAuto(plan, { kind: 'yeniden', preset: preset, fromActions: true });
        }
        this.rollAll();
      }

      /* "Geri al": the tray exactly as it was before the last suggestion. */
      autoUndoRun() {
        var u = this.autoUndo;
        if (!u || u.sig !== this.stateSig()) return;
        this.autoUndo = null;
        this.cancelAdvance();
        this.applySnapshot(u.snap);
        this.hideError();
        var n = this.pieceCount();
        this.announce(n ? 'Geri alındı: ' + n + ' ürün, ' + this.priceText() + '.' : 'Geri alındı: kutucuklar boş.');
        this.persist();
        this.render();
        this.track('xset_undo', {});
        // The button that was pressed is gone; focus goes back to Otomatik
        // rather than to <body>.
        // (Chrome keeps a just-hidden button as activeElement until the next
        // style pass, so "inside the actions row" counts as lost too.)
        var back = this.pageRandomEl;
        var active = document.activeElement;
        var lost =
          !active ||
          active === document.body ||
          !this.contains(active) ||
          (this.autoActionsEl && this.autoActionsEl.contains(active));
        if (back && lost) back.focus({ preventScroll: true });
      }

      /* The strip: shown once the shopper has met a suggestion (an Otomatik
         press, or boxes that arrived suggested) — and always in the theme
         editor, so the merchant sees the prices. Each chip: the engine's
         total for the tray it would make; pressed when it IS the tray. */
      renderStrip() {
        if (!this.stripEl) return;
        var suggested = this.state.slots.some(function (s) {
          return s.selection && s.selection.kaynak === 'oneri';
        });
        var design = !!(window.Shopify && window.Shopify.designMode);
        var show = !!(this.presets && this.presets.length) && (design || this.autoUsed || suggested);
        this.stripEl.hidden = !show;
        if (!show) return;
        var sig = this.stateSig() + '#' + this.readyVersion;
        if (sig === this.stripSig) return;
        this.stripSig = sig;
        var current = this.contentSig(this.snapshot());
        var self = this;
        // Pressed: the one chip that IS the tray — never one that cannot be
        // built whole, and when several make the same tray (one suggestion
        // plus the shopper's own gateway = another suggestion), the one that
        // keeps the fewest of the shopper's own picks.
        var rows = [];
        Array.prototype.forEach.call(this.stripEl.querySelectorAll('[data-xset-preset]'), function (chip) {
          var item = chip.closest('.xset-strip__item') || chip;
          var preset = self.presetById(chip.dataset.xsetPreset);
          var plan = preset ? self.presetPlan(preset, 'all') : null;
          item.hidden = !plan;
          if (!plan) return;
          var priceEl = chip.querySelector('[data-xset-preset-price]');
          if (priceEl) priceEl.textContent = plan.cut ? '' : money(plan.chipTotal);
          chip.classList.toggle('is-full', !!plan.cut);
          chip.classList.toggle('is-ready-price', !!plan.ready);
          chip.title = plan.ready ? 'Set fiyatı' : '';
          rows.push({ chip: chip, plan: plan });
        });
        var pressed = null;
        rows.forEach(function (row) {
          if (row.plan.cut || row.plan.sig !== current) return;
          if (!pressed || row.plan.ownKept < pressed.plan.ownKept) pressed = row;
        });
        rows.forEach(function (row) {
          row.chip.setAttribute('aria-pressed', row === pressed ? 'true' : 'false');
        });
      }

      /* "Sıfırla" sits under the tray, and emptying the tray shortens
         everything above it: done where it was pressed, the page jumped and
         the screen was left on whatever slid under the viewport (owner,
         2026-10-13: "ekran yukarı gidiyor ama otomatik scroll olmadığı için
         garip kaymış"). So the page first goes up to the boxes, and the tray
         empties once it is there — in view, where the change can be seen.
         Focus then goes to Otomatik: the pressed button hides itself on an
         empty tray and would drop focus on <body>. */
      resetAll() {
        if (!this.pieceCount() || this.resetPending) return;
        var self = this;
        var run = function () {
          self.resetPending = false;
          if (!self.pieceCount()) return;
          var active = document.activeElement;
          var hadFocus = active && self.contains(active) && active.closest('[data-xset-reset]');
          self.cancelAdvance();
          self.reset();
          self.altSaved = null;
          self.clearNotice();
          self.hideError();
          self.announce('Set sıfırlandı.');
          self.render();
          self.syncUrl();
          if ((hadFocus || active === document.body) && self.pageRandomEl) {
            self.pageRandomEl.focus({ preventScroll: true });
          }
        };
        var target = this.trayAnchor();
        var top = target ? target.getBoundingClientRect().top : 0;
        if (!target || (top >= 0 && top < window.innerHeight / 2)) return run();
        this.resetPending = true;
        if (reducedMotion()) {
          target.scrollIntoView({ block: 'start', behavior: 'auto' });
          return run();
        }
        var done = false;
        var finish = function () {
          if (done) return;
          done = true;
          window.removeEventListener('scrollend', finish);
          run();
        };
        window.addEventListener('scrollend', finish);
        // Safari has no scrollend; a smooth scroll of a screen or two is over by then.
        setTimeout(finish, 500);
        target.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }

      pickVariant(btn) {
        if (btn.disabled) return;
        var card = btn.closest('[data-xset-card]');
        card.dataset.activeVariantId = btn.dataset.variantId;
        if (btn.dataset.price) card.dataset.price = btn.dataset.price;
        this.renderCards();
        this.commit(card);
      }

      pickColor(btn) {
        var card = btn.closest('[data-xset-card]');
        if (!card) return;

        card.dataset.activeHandle = btn.dataset.productHandle;
        card.dataset.activeProductId = btn.dataset.productId;
        card.dataset.activeVariantId = btn.dataset.variantId || '';
        card.dataset.productTitle = btn.dataset.productTitle;
        card.dataset.price = btn.dataset.price;

        var img = card.querySelector('.xset-card__image');
        var thumb = btn.querySelector('.xset-card__color-thumb');
        if (img && thumb) {
          img.src = thumb.src;
          img.removeAttribute('srcset');
        }

        // The model code follows the colour on show.
        var c = this.candidate(card.dataset.stepKey, btn.dataset.productHandle);
        var codeEl = card.querySelector('[data-xset-code]');
        if (codeEl && c) {
          codeEl.textContent = c.kod || '';
          codeEl.classList.toggle('is-empty', !c.kod);
        }

        Array.prototype.forEach.call(card.querySelectorAll('[data-xset-color]'), function (b) {
          var on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-checked', on ? 'true' : 'false');
        });

        // The card now IS a different product, so the variant chips still on
        // screen belong to the one it used to be. Left live, a shopper who
        // pressed Seç (revealing chips), switched colour, then tapped a chip
        // welded this handle to that product's variant id — adding the wrong
        // colour, or failing preflight against a variant the new handle does
        // not own and blocking checkout with a false "tükendi".
        //
        // Colour siblings are always single-variant (xenon-set-compare.liquid
        // skips any with variants.size > 1), so there is nothing left to pick.
        this.tentativeCard = null;
        this.renderCards();
        this.refreshTeknikColumn(card);

        // applySnapshot() re-applies a saved colour through here; that is not news.
        if (!this.hydrating) this.announce(shortTitle(btn.dataset.productTitle) + ' rengi seçildi.');
      }


      /* ------------------------------------------------------------ ready sets */

      /* The ready-set map (snippets/xenon-set-ready-map.liquid): sets of this
         family with ≥2 distinct, verified components. `ids` is the component
         list as strings, `key` the same sorted — a set's identity as a
         multiset of products, one each. */
      loadReadySets() {
        var el = document.getElementById('xset-hazir-' + this.dataset.sectionId);
        var data = null;
        try {
          data = el ? JSON.parse(el.textContent) : null;
        } catch (e) {
          data = null;
        }
        var sets = data && Array.isArray(data.setler) ? data.setler : [];
        this.readySets = sets
          .filter(function (s) {
            return s && s.variantId && s.price > 0 && Array.isArray(s.components) && s.components.length >= 2;
          })
          .map(function (s, i) {
            s.order = i;
            s.ids = s.components.map(String);
            s.key = s.ids.slice().sort().join(',');
            return s;
          });
        this.registerParts(data && Array.isArray(data.parcalar) ? data.parcalar : []);
        // Bumped whenever a set's availability is learned to have changed, so
        // the memoised readyState() cannot keep selling a set that just sold out.
        this.readyVersion = 0;
        this.readyCache = null;
        this.readySig = '';
      }

      /* Set parts (xenon-set-ready-map.liquid): the spare filter, the extra
         bowl, the camera a ready set carries that no step offers. Each joins
         its step's lookups — byHandle, byVariant, byProduct — so a tray, a
         ?set= link, the engine and the cart treat it like any candidate, but
         NOT step.adaylar: pickable(), the counts, the floors, Otomatik Set and
         every list read adaylar, and a part is offered nowhere. It only ever
         arrives with its set (loadReadySet) or as the nearest set's missing
         piece. */
      registerParts(parts) {
        var self = this;
        parts.forEach(function (c) {
          if (!c || !c.handle || !c.step) return;
          var entry = self.manifestIndex && self.manifestIndex[c.step];
          if (!entry || entry.byHandle[c.handle]) return;
          c.parca = true;
          entry.byHandle[c.handle] = c;
          (c.variants || []).forEach(function (v) {
            if (!entry.byVariant[String(v.id)]) entry.byVariant[String(v.id)] = c;
          });
          var pid = String(c.productId);
          if (!self.manifestByProduct[pid]) self.manifestByProduct[pid] = [];
          self.manifestByProduct[pid].push({ key: c.step, step: entry.step, c: c });
        });
      }

      /* The variant a ready-set component stands for: the candidate's only
         variant, else its first in-stock one. Sets record products, not
         variants, so a multi-variant candidate is never auto-picked into the
         tray (see nearestPlacement) — this is only used for links. */
      defaultVariant(c) {
        var variants = (c && c.variants) || [];
        var v = variants.length === 1 ? variants[0] : variants.find(function (x) {
          return x.available;
        });
        return v && v.available ? v : null;
      }

      /* Where a ready-set component lives in this builder, for the
         `?set=<adım>.<varyant>` link: a skeleton step first, the extras pool
         only when nothing else has it. Null when it is no candidate at all —
         the link then simply leaves it out. */
      linkTarget(productId) {
        var hits = (this.manifestByProduct && this.manifestByProduct[String(productId)]) || [];
        var self = this;
        var ordered = hits
          .filter(function (h) {
            return !h.step.gizli && self.defaultVariant(h.c);
          })
          .sort(function (a, b) {
            return (b.step.tepside ? 1 : 0) - (a.step.tepside ? 1 : 0);
          });
        var hit = ordered[0];
        return hit ? { key: hit.key, variantId: String(this.defaultVariant(hit.c).id) } : null;
      }


      /* Where the one-tap "ekle" of the nearest-set suggestion would put a
         product, and what it would put there — or null when it cannot be done
         without asking the shopper something: no step has it, its category is
         full, or it has more than one variant (commit() never auto-picks a
         variant, and neither does this). Preference: an empty skeleton box of
         its category, then a new box of a skeleton category, then the extras
         pool. */
      nearestPlacement(productId) {
        var self = this;
        var hits = ((this.manifestByProduct && this.manifestByProduct[String(productId)]) || []).filter(function (h) {
          return !h.step.gizli && h.c.available && (h.c.variants || []).length === 1 && h.c.variants[0].available;
        });
        if (!hits.length) return null;

        var emptyBox = null;
        var hit = null;
        hits.some(function (h) {
          var slot = self.state.slots.find(function (s) {
            return s.categoryKey === h.key && !s.selection;
          });
          if (slot) {
            emptyBox = slot;
            hit = h;
            return true;
          }
          return false;
        });
        if (!hit) {
          hit = hits
            .filter(function (h) {
              return !self.categoryFull(h.key);
            })
            .sort(function (a, b) {
              return (b.step.tepside ? 1 : 0) - (a.step.tepside ? 1 : 0);
            })[0];
        }
        if (!hit) return null;

        var sel = this.selectionFor(hit.key, hit.c.ana || hit.c.handle, hit.c.handle, hit.c.variants[0].id);
        if (!sel) return null;
        return { key: hit.key, step: hit.step, slot: emptyBox, selection: sel };
      }

      /* Whether a product (by id) is a candidate with more than one variant
         — the case the ready-set match cannot vouch for. */
      multiVariant(productId) {
        var hits = (this.manifestByProduct && this.manifestByProduct[String(productId)]) || [];
        return hits.some(function (h) {
          return ((h.c && h.c.variants) || []).length > 1;
        });
      }

      trayProductIds() {
        return this.filledSlots().map(function (s) {
          return String(s.selection.productId);
        });
      }

      /* The ready-set half of the price engine for ANY list of selections —
         the tray, or a tray that does not exist yet (a strip chip, a roll of
         the dice).

         match: among the in-stock sets whose every product is in the
         selections (one unit each) and that cost no more than those products
         do: the set that is exactly the selections, else the biggest saving. The set need not be ALL of them: what
         is left over (`rest` — an extra, a care product, a second unit) goes
         to the cart as its own lines beside the set, at its own price
         (owner, 2026-10-14: "set indirimi korunsun"). One set at most.
         soldOut: the selections are EXACTLY a ready set, but no copy is in
         stock. Sade mode only.

         A product that comes in several variants can sit in `rest` but never
         inside the set: a ready set records PRODUCTS and sells its own one
         variant, so the shopper's size or colour would be swapped for the
         set's. */
      readyMatchFor(sels) {
        var out = { match: null, soldOut: null, rest: [], restSum: 0, compSum: 0, taken: [] };
        if (this.ladderOn || !this.readySets || !this.readySets.length) return out;
        sels = (sels || []).filter(function (x) {
          return x && x.productId;
        });
        if (sels.length < 2) return out;
        var self = this;
        var ids = sels.map(function (x) {
          return String(x.productId);
        });
        var uniq = ids.filter(function (id, i) {
          return ids.indexOf(id) === i;
        });
        var exactKey =
          uniq.length === ids.length && !ids.some(this.multiVariant, this) ? uniq.slice().sort().join(',') : '';
        var best = null;
        var soldOut = null;
        var exactInStock = false;
        this.readySets.forEach(function (set) {
          if (!set.ids || set.ids.length < 2) return;
          var taken = [];
          for (var k = 0; k < set.ids.length; k += 1) {
            var pid = String(set.ids[k]);
            var at = -1;
            for (var i = 0; i < ids.length; i += 1) {
              if (ids[i] === pid && taken.indexOf(i) === -1) {
                at = i;
                break;
              }
            }
            if (at === -1) return;
            taken.push(at);
          }
          if (set.ids.some(self.multiVariant, self)) return;
          if (!set.available) {
            if (exactKey && set.key === exactKey && !soldOut) soldOut = set;
            return;
          }
          if (exactKey && set.key === exactKey) exactInStock = true;
          var compSum = taken.reduce(function (n, i) {
            return n + (sels[i].price || 0);
          }, 0);
          // A ready set dearer than its own parts is not worth selling.
          if (set.price > compSum) return;
          var gain = compSum - set.price;
          // A tray that IS a ready set sells as that set (the merchant's own
          // set for exactly these products), never as a smaller one plus
          // loose products. Otherwise the biggest saving — the lowest total
          // for this tray — then the bigger set, then the cheaper one.
          var exact = !!exactKey && set.key === exactKey;
          var cover = set.ids.length;
          var better =
            !best ||
            (exact && !best.exact) ||
            (exact === best.exact &&
              (gain > best.gain ||
                (gain === best.gain && cover > best.cover) ||
                (gain === best.gain && cover === best.cover && set.price < best.set.price)));
          if (better) best = { set: set, taken: taken, compSum: compSum, gain: gain, cover: cover, exact: exact };
        });
        if (best) {
          out.match = best.set;
          out.taken = best.taken;
          out.compSum = best.compSum;
          sels.forEach(function (x, i) {
            if (best.taken.indexOf(i) !== -1) return;
            out.rest.push(x);
            out.restSum += x.price || 0;
          });
        } else if (soldOut && exactKey && !exactInStock) {
          out.soldOut = soldOut;
        }
        return out;
      }

      /* THE price of a list of selections in tray order — what the CTA
         would show for that tray: the ready set's own price when it sells as
         one, else the sum less any ladder saving. displayTotal() is this for
         the tray on screen. */
      engineTotal(sels) {
        var sum = sels.reduce(function (n, s) {
          return n + (s.price || 0);
        }, 0);
        var r = this.readyMatchFor(sels);
        if (r.match) return { total: r.match.price + r.restSum, ready: r.match, sum: sum };
        return { total: sum - this.savingsFor(sels), ready: null, sum: sum };
      }

      /* What the tray is, measured against the ready sets. Sade mode only —
         with the ladder on, pricing belongs to the Discount Function and the
         tray always goes to the cart as its own products.

         - match: an IN-STOCK set whose components are exactly the tray's
           products, one each, and that costs no more than the tray. The CTA
           then sells that set. A ready set dearer than its own parts is not a
           match worth making: the parts are the better deal.
         - soldOut: the tray IS a ready set, but no copy of it is in stock.
         - nearest: not a match, but one product short of an in-stock set that
           costs less than the tray plus that product. The best saving wins;
           there is never more than one.

         Memoised on the tray signature: render() runs on every interaction
         and the scan is over every set. */
      readyState() {
        var sig = this.stateSig() + '#' + this.readyVersion;
        if (this.readyCache && this.readyCache.sig === sig) return this.readyCache;

        var out = { sig: sig, match: null, soldOut: null, nearest: null, rest: [], restSlots: [], restSum: 0, compSum: 0 };
        this.readyCache = out;
        if (this.ladderOn || !this.readySets || !this.readySets.length) return out;

        var self = this;
        var slots = this.filledSlots();
        if (!slots.length) return out;
        var sels = slots.map(function (sl) {
          return sl.selection;
        });

        var r = this.readyMatchFor(sels);
        out.match = r.match;
        out.soldOut = r.soldOut;
        if (out.match) {
          out.rest = r.rest;
          out.restSum = r.restSum;
          out.compSum = r.compSum;
          out.restSlots = slots.filter(function (sl, i) {
            return r.taken.indexOf(i) === -1;
          });
        }
        var current = out.match ? out.match.price + out.restSum : this.total();

        // One product short of an in-stock set. Offered when adding it costs
        // less than its own price — measured as what the CTA would really
        // move by, so the line's figures are the ones the shopper then sees
        // (a set already matched counts: a bigger set can still beat it).
        // Never a second box of a tray step (a second fountain beside the
        // one in the su box), never past the line cap.
        out.nearest = null;
        if (this.lineFull()) return out;
        var ids = sels.map(function (x) {
          return String(x.productId);
        });
        var best = null;
        this.readySets.forEach(function (s) {
          if (!s.available || !s.ids || s.ids.length < 2) return;
          if (out.match && s.handle === out.match.handle) return;
          if (s.ids.some(self.multiVariant, self)) return;
          // As a multiset: a pack of three cameras with two on the tray is
          // one camera short.
          var pool = ids.slice();
          var missing = [];
          s.ids.forEach(function (id) {
            var at = pool.indexOf(String(id));
            if (at === -1) missing.push(String(id));
            else pool.splice(at, 1);
          });
          if (missing.length !== 1) return;
          var place = self.nearestPlacement(missing[0]);
          if (!place) return;
          // A second device in a filled tray box is not offered (a second
          // fountain beside the first) — another unit of the same product is
          // (the third camera of a pack).
          if (!place.slot && place.step && place.step.tepside && ids.indexOf(String(missing[0])) === -1) return;
          var after = self.readyMatchFor(sels.concat([place.selection]));
          if (!after.match || after.match.handle !== s.handle) return;
          var delta = after.match.price + after.restSum - current;
          var own = place.selection.price || 0;
          if (!(delta < own)) return;
          var gain = own - delta;
          if (!best || gain > best.gain || (gain === best.gain && s.price < best.set.price)) {
            best = { set: s, place: place, gain: gain, delta: delta, own: own, total: current };
          }
        });
        out.nearest = best;
        return out;
      }

      /* The match line, the suggestion line and the price note. */
      renderReady() {
        var state = this.readyState();

        if (this.priceNoteEl) {
          // The whole gain of the set in one figure (the free product and the
          // discount on top together); no "hazır set" wording (owner,
          // 2026-10-14).
          var gain = state.match ? state.compSum - state.match.price : 0;
          var note = gain > 0 ? 'Set kazancın: ' + money(gain) : this.priceNoteText;
          this.priceNoteEl.textContent = note;
          this.priceNoteEl.hidden = !note;
          this.priceNoteEl.classList.toggle('is-gain', gain > 0);
        }

        if (this.matchEl) {
          var matchSig = state.match
            ? 'm:' +
              state.match.handle +
              ':' +
              state.match.price +
              ':' +
              this.total() +
              ':' +
              state.rest
                .map(function (x) {
                  return x.variantId;
                })
                .join(',')
            : state.soldOut && !state.nearest
              ? 's:' + state.soldOut.handle
              : '';
          if (matchSig !== this.matchSig) {
            this.matchSig = matchSig;
            this.matchEl.innerHTML = '';
            this.matchEl.classList.toggle('is-soldout', !state.match && !!matchSig);
            if (state.match) this.fillMatch(state);
            else if (matchSig) {
              var t = document.createElement('span');
              t.className = 'xset__match-text';
              t.textContent = 'Bu seçimin seti şu an stokta değil; ürünler kendi fiyatından eklenir.';
              this.matchEl.appendChild(t);
            }
            this.matchEl.hidden = !matchSig;
            if (matchSig) this.speak(this.matchEl.textContent.replace(/\s+/g, ' ').trim(), 2);
          }
        }

        if (this.nearestEl) {
          var n = state.nearest;
          var nearSig = n ? n.set.handle + ':' + n.place.selection.handle + ':' + n.set.price + ':' + n.total : '';
          if (nearSig !== this.nearestSig) {
            this.nearestSig = nearSig;
            this.nearestEl.innerHTML = '';
            if (n) this.fillNearest(n);
            this.nearestEl.hidden = !n;
          }
        }
      }

      /* The tray IS a set: said as what the shopper won, not as "hazır set"
         arithmetic (owner, 2026-10-14: "hazır set vardı falan değil,
         ücretsiz ürün ve şu kadar indirim aldın gibi"):

           ✓  Kedi Su Pınarı Filtresi X8780 ücretsiz geldi, üstüne 198,00 TL
              set indirimi kazandın
              Sepete tek set olarak eklenir, toplam 25.499,00 TL.

         The gain is the boxes' own prices less the set price. A product whose
         price fits inside it is "the free one" — the one the suggestion line
         just added for free when there is one (so the figures match what that
         line promised), else the cheapest — and the rest of the gain is the
         discount on top. Every figure is one of today's prices, the same
         bundle comparison as the suggestion line (plan §5.4 / E12). */
      fillMatch(state) {
        var set = state.match;
        var gain = state.compSum - set.price;
        var free = gain > 0 ? this.freeItem(gain, set) : null;
        var onTop = free ? gain - free.price : gain;

        var icon = document.createElement('span');
        icon.className = 'xset__match-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML =
          '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M3.5 8.5l3 3 6-7"/></svg>';
        var text = document.createElement('span');
        text.className = 'xset__match-text';

        var lead = document.createElement('strong');
        lead.className = 'xset__match-lead';
        if (free) {
          lead.appendChild(document.createTextNode(free.name + ' '));
          var em = document.createElement('span');
          em.className = 'xset__match-win';
          em.textContent = 'ücretsiz';
          lead.appendChild(em);
          lead.appendChild(document.createTextNode(' geldi'));
          if (onTop > 0) {
            lead.appendChild(document.createTextNode(', üstüne '));
            var amount = document.createElement('span');
            amount.className = 'xset__match-win';
            amount.textContent = money(onTop) + ' set indirimi';
            lead.appendChild(amount);
            lead.appendChild(document.createTextNode(' kazandın'));
          }
        } else if (gain > 0) {
          lead.appendChild(document.createTextNode('Set olarak aldığın için '));
          var win = document.createElement('span');
          win.className = 'xset__match-win';
          win.textContent = money(gain) + ' indirim';
          lead.appendChild(win);
          lead.appendChild(document.createTextNode(' kazandın'));
        } else {
          lead.textContent = 'Bu ürünler birlikte set olarak satılıyor';
        }
        text.appendChild(lead);
        // Read aloud as one line (renderReady speaks textContent): a space,
        // which the column layout does not draw.
        text.appendChild(document.createTextNode(' '));

        var sub = document.createElement('span');
        sub.className = 'xset__match-sub';
        // With something beside the set (an extra, a care product): the set
        // is one line and the rest go as their own, so the discount stays.
        var rest = state.restSlots || [];
        sub.textContent = rest.length
          ? 'Sepete set ve ' +
            (rest.length === 1 ? this.shortName(rest[0].categoryKey, rest[0].selection) : rest.length + ' ürün') +
            ' ayrı eklenir, toplam ' +
            money(set.price + state.restSum) +
            '.'
          : 'Sepete tek set olarak eklenir, toplam ' + money(set.price) + '.';
        text.appendChild(sub);

        this.matchEl.appendChild(icon);
        this.matchEl.appendChild(text);
      }

      /* The product a set's gain makes free: the one the suggestion line
         last added for free, while it is on the tray and fits inside the
         gain; else the cheapest product that fits. Null when none does. */
      freeItem(gain, set) {
        var self = this;
        var setIds = ((set && set.ids) || []).map(String);
        var fits = this.filledSlots()
          .filter(function (s) {
            if (!s.selection || setIds.indexOf(String(s.selection.productId)) === -1) return false;
            return (s.selection.price || 0) > 0 && s.selection.price <= gain;
          })
          .sort(function (a, b) {
            return a.selection.price - b.selection.price;
          });
        if (!fits.length) return null;
        var hint = this.freeHint;
        var pick =
          (hint &&
            fits.filter(function (s) {
              return String(s.selection.productId) === hint;
            })[0]) ||
          fits[0];
        return { name: this.shortName(pick.categoryKey, pick.selection), price: pick.selection.price };
      }

      /* A product as the money lines name it: the short name without its
         "(X8652 ve X8653 Uyumlu)", then the model code — two bowls share
         "Akıllı Mama Kabı" (plan İ5). */
      shortName(key, sel) {
        var c = this.candidate(key, sel.handle);
        var base = (c && c.kisaAd) || nameOf(sel);
        var clean = base
          .replace(/\s*\([^)]*\)/g, '')
          // "Kapı Kepenk Sensörü X7570 ile uyumlu", "Alarm Tuş Takımı, X7570
          // İçin Uygundur": what it fits is said elsewhere on the line.
          .replace(/,?\s*X\d{4}\s+(?:ile\s+uyumlu|[İi]çin\s+[Uu]ygundur)/g, '')
          .replace(/\s*X\d{4}\s*$/, '')
          .trim();
        return (clean || base) + (c && c.kod ? ' ' + c.kod : '');
      }

      /* "One more and it is a ready set", said the way a shopper feels it:
         first the product comes free, then the set discount on top (owner,
         2026-10-14: "Bu ürünü ücretsiz sepetine ekle. Ücretsiz ürünü
         ekledikten sonra ekstradan da set indirimi…" — not set arithmetic,
         and not the earlier "Bir ürün daha ekle, bedava gelsin"):

           [img]  Bu ürünü setine ücretsiz ekle               [Ücretsiz ekle]
                  Kedi Su Pınarı Filtresi X8783 · normalde 799,00 TL
                  Üstüne 298,00 TL set indirimi de kazanırsın

         "setine", not "sepetine": the button fills a box of the set; the
         cart is the green button's job. The photo is what "Bu ürünü" points
         at.
         The product's cost in the set is the set price less today's tray:
         ≤ 0 → it is free (and below 0 the basket also drops by that much —
         the "set indirimi" line); > 0 → it comes that much under its own
         price. Every figure is one of TODAY's prices — a bundle comparison,
         no struck or earlier price (plan §5.4 / E12). No "hazır set"
         wording: the shopper is adding a product, not buying a set they did
         not ask for. */
      fillNearest(n) {
        var c = this.candidate(n.place.key, n.place.selection.handle);
        var name = this.shortName(n.place.key, n.place.selection);
        var own = n.place.selection.price || 0;
        // What the total moves by when it is added (readyState): ≤ 0 → free.
        var inSet = n.delta;
        var free = inSet <= 0;
        var bonus = free ? -inSet : own - inSet;

        var imgSrc = (c && c.img) || n.place.selection.image || '';
        if (imgSrc) {
          var media = document.createElement('span');
          media.className = 'xset__nearest-media';
          var img = document.createElement('img');
          img.className = 'xset__nearest-image';
          img.src = imgSrc;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 48;
          img.height = 48;
          media.appendChild(img);
          this.nearestEl.appendChild(media);
        }

        var text = document.createElement('span');
        text.className = 'xset__nearest-text';

        var head = document.createElement('strong');
        head.className = 'xset__nearest-head';
        head.textContent = free ? 'Bu ürünü setine ücretsiz ekle' : 'Bu ürünü setine ' + money(inSet) + "'ye ekle";
        text.appendChild(head);

        var what = document.createElement('span');
        what.className = 'xset__nearest-what';
        what.textContent = name + ' · normalde ' + money(own);
        text.appendChild(what);

        // The bonus on top of the product: below a free product, what the
        // basket drops by besides; below a discounted one, the discount.
        if (bonus > 0) {
          var extra = document.createElement('span');
          extra.className = 'xset__nearest-extra';
          var amount = document.createElement('strong');
          amount.textContent = money(bonus) + ' set indirimi';
          extra.appendChild(document.createTextNode(free ? 'Üstüne ' : 'Böylece '));
          extra.appendChild(amount);
          extra.appendChild(document.createTextNode(free ? ' de kazanırsın' : ' kazanırsın'));
          text.appendChild(extra);
        }

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'xset__nearest-add';
        btn.setAttribute('data-xset-nearest-add', '');
        btn.setAttribute(
          'aria-label',
          name +
            (free ? ' setine ücretsiz ekle' : ' setine ' + money(inSet) + "'ye ekle") +
            (bonus > 0 ? ', ' + (free ? 'üstüne ' : '') + money(bonus) + ' set indirimi' : '')
        );
        btn.textContent = free ? 'Ücretsiz ekle' : 'Ekle';

        this.nearestEl.appendChild(text);
        this.nearestEl.appendChild(btn);
      }

      /* "Setindeki Akıllı Kapı Kilidi X8052, Zigbee Gateway X7964 ile
         çalışır · [Sete ekle]" — families with data-compat only. The pairs
         are the merchant's own custom.uyumlu_modeller: a tray product whose
         list holds nothing on the tray gets one line for the first listed
         product this page can add. Never added for the shopper. */
      compatNeed() {
        if (!this.compatOn || this.lineFull()) return null;
        var self = this;
        var slots = this.filledSlots();
        var trayIds = slots.map(function (sl) {
          return String(sl.selection.productId);
        });
        for (var i = 0; i < slots.length; i += 1) {
          var sl = slots[i];
          var hits = (this.manifestByProduct && this.manifestByProduct[String(sl.selection.productId)]) || [];
          var list = hits.length && hits[0].c.uyumlu ? hits[0].c.uyumlu.map(String) : [];
          if (!list.length) continue;
          var met = list.some(function (id) {
            return trayIds.indexOf(id) !== -1;
          });
          if (met) continue;
          // The "one more and it is a set" line already offers it, with its
          // price: one line per product, the one with the saving.
          var near = this.readyState().nearest;
          var offered = near ? String(near.place.selection.productId) : '';
          if (offered && list.indexOf(offered) !== -1) continue;
          for (var k = 0; k < list.length; k += 1) {
            var place = this.nearestPlacement(list[k]);
            if (place) return { pid: list[k], place: place, forName: self.shortName(sl.categoryKey, sl.selection) };
          }
          // Nowhere to add it: its box holds something this product does not
          // work with (a lock beside the Ethernet gateway). Offer the swap.
          for (var j = 0; j < list.length; j += 1) {
            var swap = this.compatSwap(list[j], list);
            if (swap) return { pid: list[j], place: swap, forName: self.shortName(sl.categoryKey, sl.selection), swap: true };
          }
        }
        return null;
      }

      /* The box `pid` would go in, when every box of its step is taken by
         something not in `list` — the swap the compatibility line offers. */
      compatSwap(pid, list) {
        var self = this;
        var hits = ((this.manifestByProduct && this.manifestByProduct[String(pid)]) || []).filter(function (h) {
          return !h.step.gizli && h.c.available && (h.c.variants || []).length === 1 && h.c.variants[0].available;
        });
        for (var i = 0; i < hits.length; i += 1) {
          var h = hits[i];
          var taken = this.state.slots.filter(function (s) {
            return s.categoryKey === h.key && s.selection;
          });
          if (!taken.length || !this.categoryFull(h.key)) continue;
          var other = taken.find(function (s) {
            return list.indexOf(String(s.selection.productId)) === -1;
          });
          if (!other) continue;
          var sel = this.selectionFor(h.key, h.c.ana || h.c.handle, h.c.handle, h.c.variants[0].id);
          if (sel) return { key: h.key, step: h.step, slot: other, selection: sel, replaces: self.shortName(h.key, other.selection) };
        }
        return null;
      }

      renderCompat() {
        if (!this.compatEl) return;
        var hint = this.compatNeed();
        var sig = hint ? hint.pid + ':' + hint.forName + ':' + (hint.swap ? 'swap' : '') : '';
        if (sig === this.compatSig) return;
        this.compatSig = sig;
        this.compatEl.innerHTML = '';
        this.compatEl.hidden = !hint;
        if (!hint) return;
        var sel = hint.place.selection;
        var name = this.shortName(hint.place.key, sel);
        var c = this.candidate(hint.place.key, sel.handle);
        var imgSrc = (c && c.img) || sel.image || '';
        if (imgSrc) {
          var media = document.createElement('span');
          media.className = 'xset__nearest-media';
          var img = document.createElement('img');
          img.className = 'xset__nearest-image';
          img.src = imgSrc;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 48;
          img.height = 48;
          media.appendChild(img);
          this.compatEl.appendChild(media);
        }
        var text = document.createElement('span');
        text.className = 'xset__nearest-text';
        var head = document.createElement('strong');
        head.className = 'xset__nearest-head';
        head.textContent = 'Setindeki ' + hint.forName + ', ' + name + ' ile çalışır';
        text.appendChild(head);
        var what = document.createElement('span');
        what.className = 'xset__nearest-what';
        what.textContent = hint.swap
          ? 'Setindeki ' + hint.place.replaces + ' bununla çalışmıyor · ' + name + ' ' + money(sel.price || 0)
          : name + ' · ' + money(sel.price || 0);
        text.appendChild(what);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'xset__nearest-add';
        btn.setAttribute('data-xset-compat-add', '');
        btn.setAttribute('data-product-id', hint.pid);
        btn.setAttribute('aria-label', name + (hint.swap ? ' ile değiştir' : ' sete ekle'));
        btn.textContent = hint.swap ? 'Değiştir' : 'Sete ekle';
        this.compatEl.appendChild(text);
        this.compatEl.appendChild(btn);
      }

      /* The compatibility line's button: re-derived at click time, like the
         nearest line's, and put in a box under the set's usual limits. */
      addCompat(pid) {
        var hint = this.compatNeed();
        if (!hint || hint.pid !== String(pid)) return;
        this.cancelAdvance();
        var place = hint.place;
        var slot = place.slot;
        if (!hint.swap && (!slot || slot.selection)) {
          if (this.categoryFull(place.key)) return;
          var panel = this.panelFor(place.key);
          slot = this.createExtraSlot(place.key, (panel && panel.dataset.categoryTitle) || place.step.baslik);
        }
        place.selection.kaynak = 'secim';
        slot.selection = place.selection;
        slot.el.classList.remove('is-stale');
        this.hideError();
        this.renderSlot(slot);
        this.announce(
          hint.swap
            ? place.replaces + ' yerine ' + this.shortName(place.key, slot.selection) + ' seçildi.'
            : this.shortName(place.key, slot.selection) + ' sete eklendi.'
        );
        this.persist();
        this.render();
        this.highlight(slot);
      }

      /* The one tap. Re-derived at click time rather than read from what was
         rendered, so a tray that changed underneath the line cannot receive a
         stale product. */
      addNearest() {
        var n = this.readyState().nearest;
        if (!n || this.lineFull()) return;
        this.cancelAdvance();
        var slot = n.place.slot;
        if (!slot || slot.selection) {
          if (this.categoryFull(n.place.key)) return;
          var panel = this.panelFor(n.place.key);
          slot = this.createExtraSlot(n.place.key, (panel && panel.dataset.categoryTitle) || n.place.step.baslik);
        }
        slot.selection = n.place.selection;
        slot.el.classList.remove('is-stale');
        this.hideError();
        this.renderSlot(slot);
        // The match line then names THIS product as the free one, so its
        // figures are the ones this line promised.
        this.freeHint = String(slot.selection.productId);

        // stateSig() moved with the assignment above, so this is a fresh scan.
        var after = this.readyState();
        var gain = after.match ? after.compSum - after.match.price : 0;
        var free = gain > 0 ? this.freeItem(gain, after.match) : null;
        this.announce(
          free
            ? free.name +
                ' setine ücretsiz eklendi' +
                (gain - free.price > 0 ? ', üstüne ' + money(gain - free.price) + ' set indirimi kazandın' : '') +
                '. Toplam ' +
                money(after.match.price + after.restSum) +
                '.'
            : nameOf(slot.selection) + ' sete eklendi.'
        );
        this.persist();
        this.render();
        this.highlight(slot);
        // The line that held the button is gone; the next thing to press is
        // the CTA, which now sells the set. preventScroll: on desktop it sits
        // in the panel, on mobile in the fixed bar — neither should jump.
        if (this.submitEl) this.submitEl.focus({ preventScroll: true });
      }

      /* "Hazır setlerimiz". In-stock sets only, the ones sharing the most
         products with the tray first, then the ones the builder can rebuild in
         full, then the collection's own order; eight at most. Rebuilt only
         when that order changes, so a re-render does not throw away a
         carousel the shopper is scrolling. */
      renderReadyCarousel() {
        if (!this.readyTrack) return;
        // Below the tray, and its skeleton holds the cards' exact size: the
        // first fill waits for an idle moment rather than lengthening the
        // page's first long task (~14 ms of a 4×-throttled ~50 ms init).
        if (this.carouselDeferred) return;
        var self = this;
        var tray = this.trayProductIds();
        var seenGroup = {};
        var ranked = this.readySets
          .filter(function (s) {
            return s.available;
          })
          .map(function (s) {
            var overlap = s.ids.filter(function (id) {
              return tray.indexOf(id) !== -1;
            }).length;
            var cover = s.ids.filter(function (id) {
              return !!self.linkTarget(id);
            }).length;
            return { s: s, overlap: overlap, cover: cover, full: cover === s.ids.length };
          })
          // A card loads its set into the tray (loadReadySet), so a card is
          // only a set that, loaded, IS that set at its printed price
          // (readyLoadable): held whole (a dog set's bowl is excluded here),
          // within the tray's limits, no variant to choose, and the set the
          // engine would sell — not a dearer twin of a cheaper set with the
          // same contents, not a set dearer than its own parts.
          .filter(function (r) {
            return r.full && self.readyLoadable(r.s);
          })
          .sort(function (a, b) {
            // Most shared products first; among those, the set with the
            // fewest products the tray does NOT have — the closest one, so an
            // exact match leads and a "one more" set follows it.
            var closer = a.overlap && b.overlap ? a.s.ids.length - a.overlap - (b.s.ids.length - b.overlap) : 0;
            return (
              b.overlap - a.overlap ||
              closer ||
              (b.full ? 1 : 0) - (a.full ? 1 : 0) ||
              b.cover - a.cover ||
              a.s.order - b.s.order
            );
          })
          // Colour twins (Uyku Takip Premium in white, pink and blue) are
          // one card — the first, the one the tray is closest to — so eight
          // cards are eight different sets.
          .filter(function (r) {
            var sig = r.s.ids
              .map(function (id) {
                var hits = (self.manifestByProduct && self.manifestByProduct[String(id)]) || [];
                return hits.length ? hits[0].c.ana || hits[0].c.handle : String(id);
              })
              .sort()
              .join(',');
            if (seenGroup[sig]) return false;
            seenGroup[sig] = true;
            return true;
          })
          .slice(0, 8);

        // "Kutucuklarda ✓" is the set the tray actually sells as, not any
        // set with the same contents.
        // With products beside the set it is not "the same set" any more.
        var rs = this.readyState();
        var currentHandle = rs.match && !rs.rest.length ? rs.match.handle : '';
        var sig = ranked
          .map(function (r) {
            return r.s.handle + ':' + r.overlap + (r.s.handle === currentHandle ? '=' : '');
          })
          .join('|');
        if (sig === this.readySig) return;
        this.readySig = sig;

        if (this.readyEl) this.readyEl.hidden = !ranked.length;
        var frag = document.createDocumentFragment();
        ranked.forEach(function (r) {
          // A count is only news when there is more than one thing to count:
          // with a single product on the tray every card would say "1".
          var shown = tray.length >= 2 ? r.overlap : 0;
          frag.appendChild(self.readyCard(r.s, shown, r.s.handle === currentHandle));
        });
        this.readyTrack.innerHTML = '';
        this.readyTrack.appendChild(frag);
        this.readyTrack.scrollLeft = 0;
        this.syncReadyNav();
      }

      /* A card is ONE button: pressing it puts this set's products in the
         boxes above (loadReadySet) — the owner's rule, 2026-10-13: "hazır
         setlerden tıklayınca yukarıdaki ürünleri değiştirsin, set sayfasına
         gitmesin". No link to the set's page. The button is stretched over
         the card (the tray boxes' own pattern, xenon-set-slot.liquid), so the
         picture, the name and the price stay plain content and the card has
         one tab stop and one accessible name. */
      readyCard(set, overlap, exact) {
        var li = document.createElement('li');
        li.className = 'xset-ready__card' + (exact ? ' is-current' : '');
        var name = set.kisaAd || shortTitle(set.title);

        var media = document.createElement('span');
        media.className = 'xset-ready__media';
        if (set.img) {
          var img = document.createElement('img');
          img.src = set.img;
          if (set.img2x) img.srcset = set.img + ' 360w, ' + set.img2x + ' 720w';
          img.sizes = '(max-width: 767px) 58vw, 220px';
          img.width = 360;
          img.height = 360;
          img.alt = '';
          img.loading = 'lazy';
          img.decoding = 'async';
          media.appendChild(img);
        }
        if (overlap > 0) {
          var chip = document.createElement('span');
          chip.className = 'xset-ready__chip' + (exact ? ' is-exact' : '');
          chip.textContent = exact ? 'Seçtiğin setin aynısı' : 'Seçtiğin ' + overlap + ' ürün var';
          media.appendChild(chip);
        }
        li.appendChild(media);

        var body = document.createElement('span');
        body.className = 'xset-ready__body';

        var nameEl = document.createElement('span');
        nameEl.className = 'xset-ready__name';
        var nameText = document.createElement('span');
        nameText.className = 'xset-ready__name-text';
        nameText.textContent = name;
        nameEl.appendChild(nameText);
        body.appendChild(nameEl);

        var meta = document.createElement('span');
        meta.className = 'xset-ready__meta';
        var price = document.createElement('span');
        price.className = 'xset-ready__price';
        price.textContent = money(set.price);
        meta.appendChild(price);
        meta.appendChild(document.createTextNode(' · ' + sizeLabel(set.ids.length)));
        body.appendChild(meta);

        var action = document.createElement('span');
        action.className = 'xset-ready__action';
        action.textContent = exact ? 'Kutucuklarda ✓' : 'Kutucuklara yükle';
        body.appendChild(action);
        li.appendChild(body);

        var hit = document.createElement('button');
        hit.type = 'button';
        hit.className = 'xset-ready__hit';
        hit.setAttribute('data-xset-ready-load', set.handle);
        hit.setAttribute(
          'aria-label',
          name + ', ' + money(set.price) + ', ' + sizeLabel(set.ids.length) + (exact ? ': kutucuklarda' : ': kutucuklara yükle')
        );
        li.appendChild(hit);
        return li;
      }

      /* A ready set's component as a tray pick: the step that offers it (a
         skeleton step first, else the extras step — set parts included),
         its single or first in-stock variant. Null when nothing here can
         hold it. Same choice as linkTarget(), so a card and its ?set= link
         build the same tray. */
      loadTarget(productId) {
        var t = this.linkTarget(productId);
        if (!t) return null;
        var entry = this.manifestIndex && this.manifestIndex[t.key];
        var c = entry && entry.byVariant[t.variantId];
        if (!c) return null;
        var v = (c.variants || []).find(function (x) {
          return String(x.id) === t.variantId;
        });
        return v ? { key: t.key, c: c, v: v, kaynak: 'secim' } : null;
      }

      /* What loading `set` would lay on the tray, priced — or null when it
         cannot be laid whole: a component nothing here holds or that comes
         in several variants (the builder never picks a variant for the
         shopper), or the tray's limits. */
      readyPlanFor(set) {
        var self = this;
        var picks = [];
        var whole = set.ids.every(function (id) {
          var t = self.loadTarget(id);
          if (t && (t.c.variants || []).length === 1) picks.push(t);
          return !!t && (t.c.variants || []).length === 1;
        });
        if (!whole) return null;
        var placed = this.placePicks(picks, this.state.setId);
        if (placed.overflow) return null;
        return this.pricePlan({ snapshot: placed.snapshot });
      }

      /* Whether a card can promise what it prints: loaded, the tray sells as
         THIS set. Memoised per set and availability version — the manifest
         and the set map do not change during a page view. */
      readyLoadable(set) {
        var memo = (this.readyLoadableMemo = this.readyLoadableMemo || {});
        var key = set.handle + '@' + (this.readyVersion || 0);
        if (key in memo) return memo[key];
        var plan = set.available ? this.readyPlanFor(set) : null;
        memo[key] = !!(plan && plan.ready && plan.ready.handle === set.handle);
        return memo[key];
      }

      /* A carousel card: the tray becomes exactly this set — every box
         replaced, boxes the set does not fill emptied — so it IS the ready
         set and sells at the set's own price. Undoable like a suggestion
         ("Geri al"). The page then shows the boxes with the sentence that
         says what happened, and focus goes to "Geri al": the pressed card is
         rebuilt (its "Kutucuklarda ✓" state) and must not drop focus on
         <body>. */
      loadReadySet(handle) {
        var set = (this.readySets || []).find(function (s) {
          return s.handle === handle;
        });
        if (!set) return;
        this.cancelAdvance();
        var name = set.kisaAd || shortTitle(set.title);
        var plan = set.available ? this.readyPlanFor(set) : null;
        if (!plan || !plan.ready || plan.ready.handle !== set.handle) {
          this.announce(name + ' şu an kutucuklara yüklenemiyor.');
          return;
        }
        var inTray = this.readyState().match;
        if (plan.sig === this.contentSig(this.snapshot()) || (inTray && inTray.handle === set.handle)) {
          this.announce(name + ' zaten kutucuklarda.');
          this.scrollToTray();
          return;
        }
        this.runAuto(plan, { kind: 'hazir', set: set });
        this.scrollToTray();
        var undo = this.autoActionsEl && !this.autoActionsEl.hidden && this.autoActionsEl.querySelector('[data-xset-auto-undo]');
        var back = undo && !undo.hidden ? undo : this.readyTrack && this.readyTrack.querySelector('[data-xset-ready-load="' + handle.replace(/"/g, '') + '"]');
        if (back) back.focus({ preventScroll: true });
      }

      // The top of what a tray change is about: the notice band when it
      // speaks, else the Otomatik row right above the boxes — scroll-margin
      // in the CSS clears the sticky header.
      trayAnchor() {
        return (
          this.querySelector('[data-xset-notice]:not([hidden])') ||
          (this.pageRandomEl && this.pageRandomEl.closest('.xset__lead-actions')) ||
          this.trayWrapEl ||
          this.trayEl
        );
      }

      // Up to the sentence that says what changed ("Setin hazır", the set's
      // name, the status line with "Geri al") and the boxes under it —
      // unless that is already in the top half of the screen.
      scrollToTray() {
        var target = this.trayAnchor();
        if (!target) return;
        var top = target.getBoundingClientRect().top;
        if (top >= 0 && top < window.innerHeight / 2) return;
        target.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
      }

      /* The arrows exist for a mouse; touch scrolls the row. Shown only when
         there is somewhere to go, and each one only while its direction has. */
      syncReadyNav() {
        if (!this.readyNav || !this.readyTrack) return;
        var t = this.readyTrack;
        var max = t.scrollWidth - t.clientWidth;
        this.readyNav.hidden = max <= 4;
        var prev = this.readyNav.querySelector('[data-xset-ready-scroll="prev"]');
        var next = this.readyNav.querySelector('[data-xset-ready-scroll="next"]');
        if (prev) prev.disabled = t.scrollLeft <= 4;
        if (next) next.disabled = t.scrollLeft >= max - 4;
      }

      scrollReady(direction) {
        if (!this.readyTrack) return;
        var card = this.readyTrack.querySelector('.xset-ready__card');
        var step = card ? Math.round(card.getBoundingClientRect().width + 16) * 2 : Math.round(this.readyTrack.clientWidth * 0.8);
        this.readyTrack.scrollBy({ left: direction === 'prev' ? -step : step, behavior: reducedMotion() ? 'auto' : 'smooth' });
      }

      /* The ready set is the one thing preflight cannot learn from the tray:
         its own availability, from its own product JSON. A failed check is not
         evidence of a sold-out set, same rule as preflight(). */
      async readyStillAvailable(set) {
        try {
          var root = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.root_url) || '/';
          var r = await fetch(root.replace(/\/$/, '') + '/products/' + set.handle + '.js');
          if (!r.ok) return true;
          var data = await r.json();
          var v = (data.variants || []).find(function (x) {
            return String(x.id) === String(set.variantId);
          });
          return !!(v && v.available);
        } catch (e) {
          return true;
        }
      }

      /* One line, quantity 1, the set's own variant. `_set_kaynak` says the
         builder sold it (analytics: a tray that became a ready set), `_set_id`
         keeps it groupable like any builder line; no visible "Set" property —
         the line IS the set. */
      buildReadyItems(set, setId) {
        return [
          {
            id: parseInt(set.variantId, 10),
            quantity: 1,
            properties: {
              _set_id: setId || this.state.setId,
              _set_kaynak: 'builder',
              _set_hazir: set.handle,
            },
          },
        ];
      }

      /* ---------------------------------------------------------------- render */

      render() {
        var self = this;
        this.state.slots.forEach(function (slot) {
          self.renderSlot(slot);
        });
        this.renderCards();
        this.renderState();
        this.renderAddTile();
        this.renderSummary();
        this.renderReady();
        this.renderCompat();
        this.renderReadyCarousel();
        this.renderStrip();
        this.renderStatus();
        this.renderPromise();
        this.measureBar();
      }

      /* The completion moment, and the two controls that frame an empty tray. */
      renderState() {
        var complete = this.isComplete();

        this.renderSteps();

        // No visible "Setin hazır" heading any more (the status block above
        // the tray was removed), but the moment is still spoken, once per
        // completion, through the live region.
        if (complete && !this.wasComplete) this.speak(this.dataset.completeHeading || 'Setin hazır', 1);
        this.wasComplete = complete;
        this.classList.toggle('is-set-complete', complete);

        // Once per completion, and re-armed the moment the set stops being
        // complete — so a shopper who removes a box and refills it gets the
        // moment again, but adding a fourth product to a finished set does not
        // replay it.
        if (complete && !this.celebrated) this.celebrate();
        if (!complete) this.celebrated = false;

        // Nothing is announced from here. The heading element is its own
        // aria-live region, so unhiding it is what reaches a screen reader —
        // pushing the same words through the status line as well put "Setin
        // hazır" on screen twice, once bold and once muted, on the very roll
        // that was supposed to feel like an answer.

        // "Sıfırla" with nothing to clear is a dead control.
        if (this.resetEl) this.resetEl.hidden = this.pieceCount() === 0;
      }

      /* The ladder, and the progress bar, as one thing.

         It used to be one segment per default box, bound to that box. The
         discount is positional now — the second product you have earns 5%
         whatever category it came from — so position is what the track has to
         show, and the rungs carry their own rates. That is the Blueland idea the
         merchant liked: an empty rung labelled with what filling it unlocks.

         Length is the longer of the skeleton and the ladder: three boxes but a
         five-rung ladder means five rungs, because the last two are reachable
         through "+ Yeni ürün". */
      renderSteps() {
        var defaults = this.defaultSlots();
        // Feeds the stagger on the completion halo, so it follows the tray
        // rather than the DOM's incidental order. Set even when the track is
        // not rendered (Sade mod has no ladder track, but still celebrates).
        defaults.forEach(function (slot, i) {
          slot.el.style.setProperty('--xsb-step-i', i);
        });
        if (!this.stepsEl) return;
        var count = Math.max(defaults.length, this.ladderMax());
        var n = this.pieceCount();

        if (this.stepsEl.children.length !== count) {
          this.stepsEl.innerHTML = '';
          for (var i = 1; i <= count; i += 1) {
            var rung = document.createElement('span');
            rung.className = 'xset__steps-rung';
            var seg = document.createElement('span');
            seg.className = 'xset__steps-seg';
            var tier = document.createElement('span');
            tier.className = 'xset__steps-tier';
            var pct = this.tierFor(i);
            // The first rung buys nothing, and an empty label keeps the rungs
            // the same height so the track does not step up and down.
            tier.textContent = pct ? '%' + pct : '';
            rung.appendChild(seg);
            rung.appendChild(tier);
            this.stepsEl.appendChild(rung);
          }
        }

        Array.prototype.forEach.call(this.stepsEl.children, function (rung, i) {
          var seg = rung.querySelector('.xset__steps-seg');
          if (seg) seg.classList.toggle('is-done', n >= i + 1);
          rung.classList.toggle('is-next', n === i);
        });
      }

      /* The completion beat. Everything visible about it is CSS reacting to
         is-set-complete; this only adds the one-shot halo pass and the haptic. */
      celebrate() {
        this.celebrated = true;
        if (reducedMotion()) return;

        var self = this;
        this.classList.add('is-celebrating');
        clearTimeout(this.celebrateTimer);
        this.celebrateTimer = setTimeout(function () {
          self.classList.remove('is-celebrating');
        }, 1400);

        // One short pulse, on the phone in the hand that just finished a set.
        // Progressive enhancement in the strictest sense: iOS Safari has no
        // Vibration API at all and desktop browsers ignore it.
        if (navigator.vibrate) {
          try {
            navigator.vibrate(18);
          } catch (e) {
            /* some browsers throw without a user gesture */
          }
        }
      }

      /* Every card's controls for the state the picker is in.

         Three modes, decided per card:
         - EDITING (a filled box was opened, and this card is of its step):
           the product already in the box reads "Setinde kalsın", wears the
           "Setinde" chip and offers "+ 1 tane daha ekle"; every other column
           reads "Bununla değiştir" and says what the swap does to the set's
           total ("Setin +3.230,00 TL") — plan §4.2 F.
         - EXTRAS LIST rows read "Ekle", with "Sette N var" once the product
           is in the set.
         - otherwise "Seç", or "Bir daha ekle" for a product already in the
           set — which, in this mode, really does add a second box.
         A pick that would break a limit (2 per product) is marked
         aria-disabled and refused with a note on press, not hidden: the
         column still has to be readable. */
      /* Only the panel on screen. The picker holds every step's columns
         (≈1,500 nodes for Kedi) and this ran over all of them on every tray
         change — while the sheet was closed and nobody could see a card.
         showPanel() renders the panel it is about to show, so a closed
         picker skips the work and an open one touches one panel. */
      renderCards(scope) {
        if (!this.picker) return;
        var root = scope || (this.picker.open ? this.visiblePanel() : null);
        if (!root) return;
        var self = this;
        var chosen = {};
        this.state.slots.forEach(function (s) {
          if (s.selection) chosen[s.selection.uid] = (chosen[s.selection.uid] || 0) + 1;
        });
        var tentative = this.tentativeCard;
        var tierPct = this.tierFor(this.pendingPosition());
        var editing = this.editingSlot();
        // The total the page shows now — the base every "Setin ±X TL" is
        // measured from (plan §6: the engine's marginal change, not the
        // difference of two shelf prices).
        var shownTotal = editing ? this.displayTotal() : 0;

        Array.prototype.forEach.call(root.querySelectorAll('[data-xset-card]'), function (card) {
          var isRow = card.hasAttribute('data-xset-row');
          // An extras box opened from the tray edits through the list, so a
          // row can be "the one in the box" too.
          var inEdit = !!(editing && card.dataset.stepKey === editing.categoryKey);
          var isCurrent = inEdit && self.isCurrentCard(card, editing);
          var price = self.cardPrice(card);
          var pid = card.dataset.activeProductId || card.dataset.productId;

          var saveEl = card.querySelector('[data-xset-card-save]');
          if (saveEl) {
            var line = '';
            if (inEdit) {
              // A column at the product cap ("Sette var") cannot be swapped
              // in: no "Setin +X TL" for a swap that will be refused.
              var capped = !isRow && self.productUsed(pid, editing) >= MAX_PER_PRODUCT;
              if (!isCurrent && !capped) {
                var delta = self.totalWithSwap(editing, self.swapCandidate(card, price)) - shownTotal;
                if (delta) line = 'Setin ' + (delta > 0 ? '+' : '−') + money(Math.abs(delta));
              }
            } else if (tierPct && price) {
              var save = Math.round((price * tierPct) / 100);
              line = isRow
                ? 'Sete +' + money(price - save) + ' (%' + tierPct + ' indirimle)'
                : 'Set kazancı −' + money(save);
            }
            setHidden(saveEl, !line);
            setText(saveEl, line);
            saveEl.classList.toggle('is-delta', inEdit);
          }

          // How many boxes hold this card's product: by column identity for
          // a column, by any colour of the row's group for a list row.
          var count = 0;
          if (isRow) {
            var ids = (card.dataset.groupIds || pid || '').split(' ');
            count = self.state.slots.filter(function (s) {
              return s.selection && ids.indexOf(String(s.selection.productId)) !== -1;
            }).length;
          } else {
            count = chosen[card.dataset.productHandle] || 0;
          }
          var isChosen = count > 0;
          var isTentative = card === tentative;
          card.classList.toggle('xset-card--committed', inEdit ? isCurrent : isChosen && !isRow);
          card.classList.toggle('xset-card--tentative', isTentative);

          var currentChip = card.querySelector('[data-xset-current]');
          setHidden(currentChip, !isCurrent);
          var owned = card.querySelector('[data-xset-row-owned]');
          if (owned) {
            setHidden(owned, !count);
            setText(owned, count ? 'Sette ' + count + ' var' : '');
          }

          // NOT aria-pressed. This was a toggle that never untoggles: pressing
          // a chosen card again adds a SECOND copy, it does not remove the
          // first. Announcing it as a pressed switch told screen-reader users
          // the opposite of what the button does, and the matching "already
          // selected" styling told everyone else the same thing — which is why
          // picking the same product twice looked impossible when it was not.
          var toggle = card.querySelector('[data-xset-toggle]');
          if (toggle) {
            dropAttr(toggle, 'aria-pressed');
            var label;
            if (inEdit) label = isCurrent ? 'Setinde kalsın' : 'Bununla değiştir';
            else if (isRow) label = 'Ekle';
            else label = isChosen ? 'Bir daha ekle' : 'Seç';
            var blocked = !isCurrent && self.productUsed(pid, inEdit ? editing : null) >= MAX_PER_PRODUCT;
            // At the product cap the column says so instead of offering a
            // press that is only refused ("Bir daha ekle" beside a one-per-set
            // family's other box) — and reads as unavailable.
            var taken = blocked && !isRow;
            if (taken) label = 'Sette var';
            card.classList.toggle('xset-card--taken', taken);
            var text = toggle.querySelector('.btn__text') || toggle;
            if (text.textContent !== label) text.textContent = label;
            // A list row adds a box, so it answers to every limit the add
            // itself checks — the category's too. Marked by the product cap
            // alone, a second litter box's row looked addable beside a full
            // "Kedi Tuvaleti" that the chooser already greyed out, and
            // pressing it only produced the refusal note.
            if (!blocked && isRow && !inEdit) blocked = !!self.rowRefusal(card);
            if (blocked) setAttr(toggle, 'aria-disabled', 'true');
            else dropAttr(toggle, 'aria-disabled');
            var name = card.dataset.productTitle ? shortTitle(card.dataset.productTitle) : '';
            if (name) setAttr(toggle, 'aria-label', label + ': ' + name);
          }

          // "+ 1 tane daha ekle" (modal eki 6) — the current column only, and
          // the reason in its place when a limit says no.
          var again = card.querySelector('[data-xset-again]');
          var limitEl = card.querySelector('[data-xset-again-limit]');
          if (again) {
            var why = isCurrent ? self.againRefusal(editing) : '';
            setHidden(again, !isCurrent || !!why);
            if (limitEl) {
              setHidden(limitEl, !isCurrent || !why);
              setText(limitEl, isCurrent ? why : '');
            }
          }

          var expand = card.querySelector('[data-xset-expand]');
          if (expand) setAttr(expand, 'aria-hidden', isTentative ? 'false' : 'true');

          Array.prototype.forEach.call(card.querySelectorAll('[data-xset-variant]'), function (chip) {
            var on = card.dataset.activeVariantId === chip.dataset.variantId;
            setAttr(chip, 'aria-checked', on ? 'true' : 'false');
            chip.classList.toggle('is-selected', on);
            // The row collapses with grid-template-rows: 0fr, which clips but
            // does not un-focus. commit() and onKeydown promote a chip to
            // tabindex 0; nothing demoted it again, so a collapsed card kept a
            // tabbable, invisible, aria-hidden button.
            if (!isTentative) setAttr(chip, 'tabindex', '-1');
          });
        });

        // Labels, the delta line, the +1 link and opened variant chips all
        // change the height of the header's pinned slice; re-measure once
        // painted, and again after the chips' 0.3s unfold.
        if (this.picker.open) {
          var panel = root;
          requestAnimationFrame(function () {
            self.measureHead(panel);
          });
          clearTimeout(this.measureTimer);
          this.measureTimer = setTimeout(function () {
            self.measureHead(panel);
          }, 360);
        }
      }

      /* "+ Yeni ürün" at the set's line cap (plan §4.2 G): a solid frame
         instead of the dashed one, "Setin dolu: en fazla 6 ürün" under it,
         and aria-disabled — still focusable, and pressing it says why. */
      renderAddTile() {
        if (!this.addTileEl) return;
        var full = this.lineFull();
        this.addTileEl.classList.toggle('is-full', full);
        var trigger = this.addTileEl.querySelector('[data-xset-add-trigger]');
        if (trigger) {
          if (full) trigger.setAttribute('aria-disabled', 'true');
          else trigger.removeAttribute('aria-disabled');
          trigger.setAttribute('aria-label', full ? this.lineFullText() : 'Sete yeni ürün ekle');
        }
        setText(
          this.addTileEl.querySelector('.xset-slot__label'),
          full ? 'Setin dolu: en fazla ' + MAX_LINES + ' ürün' : this.addLabelText
        );
      }

      renderSummary() {
        var n = this.pieceCount();
        var target = this.defaultSlots().length;
        // "2/3 ürün" while the skeleton is unfinished, plain "4 ürün" once it is.
        // A fraction is a progress device and progress ends at completion —
        // carrying it past that point would render extras as "4/3". A tray
        // that IS a set (a fountain + filter set leaves the litter box empty)
        // is complete too: "2/3" there read as unfinished.
        var label;
        if (!n) {
          label = target ? '0/' + target + ' ürün' : '0 ürün';
        } else if (n < target && !this.readyState().match) {
          label = n + '/' + target + ' ürün';
        } else {
          label = n + ' ürün' + (n >= this.targetPieces ? ' · ' + sizeLabel(n) : '');
        }
        var gross = this.total();
        var saved = this.savings();
        var totalText = n ? money(gross - saved) : '';
        // Sade mode, tray == an in-stock ready set: what the cart will charge
        // is the set's own price, so that is the total (plan §4.2 E).
        if (this.readyState().match) totalText = money(this.displayTotal());

        if (this.countEl) this.countEl.textContent = label;
        if (this.totalEl) this.totalEl.textContent = totalText;
        if (this.wasEl) {
          this.wasEl.hidden = !saved;
          this.wasEl.textContent = saved ? money(gross) : '';
        }

        this.renderReward(n, saved);
        this.renderCta();
        this.renderDesktopBar(label, n ? totalText : '');
      }

      /* The desktop bar's left half: the tray's pictures, its count and —
         when the CTA copy does not already carry it — the total. The button
         itself is written by renderCta, from the same state as the page's. */
      renderDesktopBar(countText, totalText) {
        if (!this.dbarEl) return;
        if (this.dbarThumbsEl) {
          var sels = this.filledSlots().map(function (s) {
            return s.selection;
          });
          var sig = sels
            .map(function (sel) {
              return sel.image;
            })
            .join('|');
          if (sig !== this.dbarThumbSig) {
            this.dbarThumbSig = sig;
            this.dbarThumbsEl.innerHTML = '';
            sels.slice(0, MAX_LINES).forEach(function (sel) {
              var cell = document.createElement('span');
              cell.className = 'xset-dbar__thumb';
              if (sel.image) {
                var img = document.createElement('img');
                img.src = sel.image;
                img.alt = '';
                img.width = 40;
                img.height = 40;
                img.loading = 'lazy';
                cell.appendChild(img);
              }
              this.dbarThumbsEl.appendChild(cell);
            }, this);
          }
        }
        var inline = this.submitEl && this.submitEl.classList.contains('is-price-inline');
        setText(this.dbarInfoEl, totalText && !inline ? countText + ' · ' + totalText : countText);
      }

      /* Desktop (plan §4.1, owner's table: "Masaüstüne ince bir yapışkan bar
         eklenir"). The bar is up while some of the builder is on screen and
         the page's own CTA is not — below the fold on first load with a
         filled tray, or scrolled past on the way to the ready sets. Never on
         a phone: there the CTA row itself is the fixed bar. Without
         IntersectionObserver the bar simply never shows; the CTA is a
         scroll away. */
      observeDesktopBar() {
        if (!this.dbarEl || !this.summaryEl || !('IntersectionObserver' in window)) return;
        var self = this;
        this.dbarSeen = { builder: false, cta: true };
        // The CTA counts as seen only when ALL of it is on screen and clear of
        // the strip the bar itself takes (rootMargin): a button cut by the
        // top edge — under the sticky header — or sitting where the bar would
        // cover it is not one the shopper can press.
        this.dbarObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.target === self.summaryEl) self.dbarSeen.cta = entry.isIntersecting && entry.intersectionRatio >= 0.99;
              else self.dbarSeen.builder = entry.isIntersecting;
            });
            self.syncDesktopBar();
          },
          { threshold: [0, 1], rootMargin: '0px 0px -72px 0px' }
        );
        this.dbarObserver.observe(this.summaryEl);
        this.dbarObserver.observe(this);
      }

      syncDesktopBar() {
        if (!this.dbarEl || !this.dbarSeen) return;
        var desktop = !(window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches);
        var up = desktop && this.dbarSeen.builder && !this.dbarSeen.cta;
        if (this.dbarEl.hidden === !up) return;
        this.dbarEl.hidden = !up;
        document.body.classList.toggle('xsb-dbar-up', up);
        if (up) {
          document.documentElement.style.setProperty('--xsb-dbar-height', (this.dbarEl.offsetHeight || 64) + 'px');
        } else {
          document.documentElement.style.removeProperty('--xsb-dbar-height');
        }
      }

      /* The line that makes the segments worth filling.

         The segments say how far along you are; this says what is at the end of
         it. Blueland's ladder works because every empty slot is labelled with
         what it unlocks — the percentages ARE the reason to keep going. This is
         the same idea in one sentence, which is what fits a tray that is already
         on screen.

         Counted on pieces, not on completion: two default boxes plus one extra
         is three products and earns the discount, even though the skeleton is
         not finished. The discount is a promise about the basket. */
      renderReward(n, saved) {
        if (!this.rewardEl) return;

        if (!this.ladder.length) {
          this.rewardEl.hidden = true;
          this.classList.remove('is-discount-earned');
          return;
        }

        var earned = saved > 0;
        this.classList.toggle('is-discount-earned', earned);
        this.rewardEl.hidden = false;

        // The badge always shows what is NEXT, because that is the only number
        // that can still change the shopper's behaviour. Once the ladder is
        // topped out it shows what they are on.
        var next = this.nextTier();
        var badgePct = next ? next.pct : this.tierFor(n);
        if (this.rewardBadgeEl) this.rewardBadgeEl.textContent = '%' + badgePct;

        var text;
        if (next && !earned) {
          text = next.pos + '. ürününde %' + next.pct + ' indirim — ' + (next.pos - n) + ' ürün daha ekle';
        } else if (next) {
          text =
            money(saved) + ' kazandın · ' + (next.pos - n) + ' ürün daha ekle, ' + next.pos + '. üründe %' + next.pct;
        } else {
          text = money(saved) + ' kazandın — en üst basamaktasın';
        }

        if (this.rewardTextEl) this.rewardTextEl.textContent = text;
        else this.rewardEl.textContent = '%' + badgePct + ' ' + text;
      }

      /* The mobile bar carries a copy of the Otomatik button, shown only while
         the page's own one is off screen (plan §4.1, §7) — two identical
         buttons on one screen would be one too many. The class drives the CSS;
         on desktop the copy never shows. Without IntersectionObserver the copy
         simply stays hidden: the original is one scroll away. */
      observeRandom() {
        if (!this.barRandomEl || !this.pageRandomEl || !('IntersectionObserver' in window)) return;
        var self = this;
        this.randomObserver = new IntersectionObserver(
          function (entries) {
            var entry = entries[entries.length - 1];
            self.classList.toggle('is-random-offscreen', !entry.isIntersecting);
          },
          { threshold: 0 }
        );
        this.randomObserver.observe(this.pageRandomEl);
      }

      measureBar() {
        if (!this.summaryEl) return;
        // Mirrors compare.js:436 — the bar is fixed on mobile, so the page needs
        // to know how much of the viewport it took or the tray sits under it.
        var h = this.summaryEl.offsetHeight || 0;
        document.documentElement.style.setProperty('--xsb-bar-height', h + 'px');
        document.body.classList.toggle('xsb-bar-enabled', h > 0);
      }

      /* Reports an action. The sentence is tied to the tray state it
         describes (statusSig): renderStatus() keeps it only while that state
         is still the current one. */
      announce(message) {
        if (!this.statusEl || !message) return;
        this.statusMsg = message;
        this.statusSig = this.stateSig();
        // A new sentence retires the extras list's "Bir ürün daha ekle";
        // commit() re-arms it right after its own announcement.
        this.statusMore = false;
        this.writeStatus(message);
      }

      writeStatus(text) {
        // Never re-write the same words. A live region that repeats itself
        // trains people to stop listening to it.
        if (!this.statusEl || this.statusEl.textContent === text) return;
        this.statusEl.textContent = text;
        this.speak(text, 0);
      }

      /* ONE SENTENCE PER ACTION (plan §10). The visible lines — the status,
         "Setin hazır", the ready-set strip, the notice band — are plain
         text; what a screen reader hears goes through this one polite
         region instead. Everything an action says within 400 ms is merged
         into one utterance, in a fixed order (what happened, then "Setin
         hazır", then the ready-set match, then the notice), so a pick that
         completes a set that matches a ready set is heard once, not as
         three regions talking over each other. Nothing is spoken while the
         page is still putting itself together (`quiet`), except a notice —
         "Kaldığın yerden devam…" is news on arrival. */
      speak(text, order, force) {
        if (!this.liveEl || !text || (this.quiet && !force)) return;
        var pending = this.livePending || (this.livePending = []);
        if (pending.some(function (p) {
          return p.text === text;
        })) return;
        pending.push({ text: String(text).trim(), order: order || 0, seq: pending.length });
        var self = this;
        clearTimeout(this.liveTimer);
        this.liveTimer = setTimeout(function () {
          self.flushLive();
        }, 400);
      }

      flushLive() {
        var parts = (this.livePending || []).slice().sort(function (a, b) {
          return a.order - b.order || a.seq - b.seq;
        });
        this.livePending = [];
        if (!this.liveEl || !parts.length) return;
        this.liveEl.textContent = parts
          .map(function (p) {
            return /[.!?…]$/.test(p.text) ? p.text : p.text + '.';
          })
          .join(' ');
      }

      /* ------------------------------------------------------------ analytics */

      /* Builder events (plan §11). Published three ways, each optional:
         Shopify.analytics.publish — a custom event any Customer Events pixel
         can subscribe to (analytics.subscribe('xset_pick', …)) and forward to
         GA4 / Meta; window.dataLayer when a tag manager put one on the page;
         and a bubbling `xset:track` DOM event for in-page listeners. No
         personal data: the family, the step, product handles and variant
         ids, counts and lira totals. Never from the theme editor, never
         allowed to throw into the tray. */
      track(name, data) {
        if (window.Shopify && window.Shopify.designMode) return;
        var payload = { aile: this.familyName || '', mod: this.ladderOn ? 'merdiven' : 'sade' };
        try {
          var st = this.trackState();
          Object.keys(st).forEach(function (k) {
            payload[k] = st[k];
          });
        } catch (e) {
          /* a payload without totals is still an event */
        }
        Object.keys(data || {}).forEach(function (k) {
          payload[k] = data[k];
        });
        try {
          if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
            Shopify.analytics.publish(name, payload);
          }
        } catch (e) {
          /* analytics never breaks the tray */
        }
        try {
          if (window.dataLayer && typeof window.dataLayer.push === 'function') {
            window.dataLayer.push(Object.assign({ event: name }, payload));
          }
        } catch (e) {
          /* ditto */
        }
        try {
          this.dispatchEvent(new CustomEvent('xset:track', { bubbles: true, detail: { name: name, data: payload } }));
        } catch (e) {
          /* ditto */
        }
      }

      /* The tray as every event reports it. */
      trackState() {
        var ready = this.readyState();
        return {
          adet: this.pieceCount(),
          toplam: lira(this.displayTotal()),
          eslesen_hazir_set: ready && ready.match ? ready.match.handle : null,
        };
      }

      /* How the shopper got here, for xset_view: the page's own link
         parameters first, then an ad click id, then where the referrer sits
         on this store. Only the kind of page, never its address. */
      entrySource(params, restored) {
        if (params.set) return 'paylasim';
        if (params.oneri) return 'oneri';
        if (params.bas) return 'pdp';
        var q = window.location.search || '';
        if (/[?&](gclid|gbraid|wbraid|fbclid|ttclid|msclkid)=|[?&]utm_medium=(cpc|paid|ppc)/i.test(q)) return 'reklam';
        var ref = '';
        try {
          var r = document.referrer ? new URL(document.referrer) : null;
          if (r && r.host === window.location.host) ref = r.pathname;
        } catch (e) {
          ref = '';
        }
        if (/^\/cart/.test(ref)) return 'sepet';
        if (/^\/collections/.test(ref)) return 'hub';
        if (/^\/products/.test(ref)) return 'pdp';
        if (restored) return 'kayit';
        return 'dogrudan';
      }

      /* What is in which box — the only state the status line talks about. */
      stateSig() {
        return this.state.slots
          .map(function (s) {
            return s.uid + '=' + (s.selection ? s.selection.handle + ':' + s.selection.variantId : '');
          })
          .join('|');
      }

      /* Derived, never remembered. Called on every render: the last action's
         sentence stands while the tray is still the tray it described; the
         moment the tray changes without a new sentence (a successful add
         emptied it, a sold-out restore dropped a box, a cart update) the line
         falls back to what is true now. This is what retired "Set kuruldu."
         staying on screen over an empty tray. */
      renderStatus() {
        if (!this.statusEl) return;
        if (!(this.statusMsg && this.statusSig === this.stateSig())) {
          this.statusMsg = null;
          this.statusMore = false;
          this.writeStatus(this.stateLine());
        }
        if (this.statusMoreEl) {
          this.statusMoreEl.hidden = !(this.statusMore && this.lastExtraKey && !this.lineFull());
        }
        // "Geri al" stands — no timeout — for as long as the tray is the one
        // the suggestion made; any other change retires it (plan §7).
        if (this.autoUndo && this.autoUndo.sig !== this.stateSig()) this.autoUndo = null;
        if (this.autoActionsEl) {
          this.autoActionsEl.hidden = !this.autoUndo;
          if (this.autoNextEl) this.autoNextEl.hidden = !(this.autoUndo && this.autoUndo.next);
        }
      }

      stateLine() {
        var n = this.pieceCount();
        if (!n) return this.statusEl.getAttribute('data-xset-idle') || '';
        var next = this.defaultSlots().find(function (s) {
          return !s.selection;
        });
        return next ? n + ' ürün seçildi. Sırada ' + next.label + '.' : n + ' ürün seçildi.';
      }

      /* ------------------------------------------------------------ persistence */

      /* The tray as a plain record: what storage keeps, what a link or a
         preset is turned into, and what applySnapshot() lays back onto the
         boxes. Handle + variant + how it got there, nothing else — titles,
         prices and images are re-read from the manifest, so a price that moved
         between visits can never render stale out of storage. */
      snapshot() {
        return {
          setId: this.state.setId,
          slots: this.state.slots.map(function (s) {
            return {
              uid: s.uid,
              categoryKey: s.categoryKey,
              label: s.label,
              isExtra: s.isExtra,
              pick: s.selection
                ? {
                    uid: s.selection.uid,
                    handle: s.selection.handle,
                    variantId: s.selection.variantId,
                    kaynak: s.selection.kaynak || 'secim',
                  }
                : null,
            };
          }),
        };
      }

      writeStorage() {
        var snap = this.snapshot();
        snap.v = STORAGE_VERSION;
        snap.t = Date.now();
        storageSet(this.storageKey, JSON.stringify(snap));
      }

      /* Every change the shopper makes lands here. Writes the record, retires
         whatever the notice band was saying about the tray as it used to be
         (and with it the "Kendi setime dön" offer: the plan keeps the saved
         set restorable only until the first change), and moves the address
         along. */
      persist() {
        this.writeStorage();
        if (this.hydrating) return;
        this.altSaved = null;
        this.clearNotice();
        this.syncUrl();
      }

      /* The saved record, or null when there is none, it is older than seven
         days, it is from an older format, or storage is unreadable. */
      readSaved() {
        var raw = storageGet(this.storageKey);
        if (!raw) return null;
        var saved;
        try {
          saved = JSON.parse(raw);
        } catch (e) {
          return null;
        }
        if (!saved || saved.v !== STORAGE_VERSION || !Array.isArray(saved.slots)) return null;
        if (!(saved.t > 0) || Date.now() - saved.t > STORAGE_TTL) {
          storageRemove(this.storageKey);
          return null;
        }
        return saved;
      }

      clearState() {
        storageRemove(this.storageKey);
      }

      /* Restore order (plan §2.2): the URL, then storage, then nothing.

         ?set= is a shared tray, ?oneri= a named preset, ?bas= the product
         page the shopper came from. An unknown ?oneri= is ignored and the
         page behaves as if it were not there. An unusable ?bas= says so and
         the page otherwise opens normally — saved tray included.

         When the link and the saved tray hold the same products the saved
         record is used: it carries the set id (which is how the page knows
         the set is already in the cart) and the "Önerimiz" chips. When they
         differ the link wins, and the saved tray stays one tap away until the
         first change. */
      hydrate() {
        if (window.Shopify && window.Shopify.designMode) return;

        var saved = this.readSaved();
        var savedPicks = saved ? this.snapshotPicks(saved) : 0;
        var params = this.readParams();
        this.entry = this.entrySource(params, savedPicks > 0);
        var incoming = null;
        if (params.set) incoming = this.snapshotFromLink(params.set);
        else if (params.oneri) incoming = this.snapshotFromPreset(params.oneri);
        if (!incoming && params.bas) incoming = this.snapshotFromBas(params.bas, params.v);

        var notes = incoming ? incoming.notes.slice() : [];
        var actions = incoming && incoming.actions ? incoming.actions.slice() : [];
        var tone = notes.length ? 'warn' : 'info';

        // ?bas= over a saved tray: the product page's "Kendi setini kur" is
        // not a reason to throw away the shopper's own picks. The product
        // joins the saved tray in its own box; when the tray already holds it
        // the saved tray simply comes back ("Kaldığın yerden devam").
        if (incoming && incoming.source === 'bas' && incoming.hit && savedPicks) {
          var merged = this.mergeBas(saved, incoming.hit);
          if (merged && !merged.changed) {
            incoming = null;
          } else if (merged) {
            var mergedResult = this.applySnapshot(merged.snapshot);
            this.altSaved = saved;
            // Stored at once: the address is about to become ?set=<merged>,
            // and a reload that found the old record beside it would call
            // the shopper's own tray "paylaşılan set" (İ6). "Kendi setime
            // dön" works from memory until the first change, as before.
            this.writeStorage();
            if (mergedResult.dropped) {
              notes.push('Kayıtlı setindeki ' + mergedResult.dropped + ' ürün artık satışta değil.');
              tone = 'warn';
            }
            notes.push(
              merged.replaced
                ? 'Kayıtlı setinde ' + merged.replaced + ' yerine ' + incoming.name + ' seçildi.'
                : 'Kayıtlı setine ' + incoming.name + ' eklendi.'
            );
            actions.push({ label: 'Kendi setime dön', act: 'kendi-setim' });
            this.showNotice({ tone: tone, text: notes.join(' '), actions: actions, closable: true });
            return;
          }
        }

        if (incoming && incoming.snapshot && this.snapshotPicks(incoming.snapshot)) {
          if (saved && this.sameContent(saved, incoming.snapshot)) {
            this.applySnapshot(saved);
          } else {
            this.applySnapshot(incoming.snapshot);
            if (savedPicks) {
              this.altSaved = saved;
              // "önerdiğimiz" only when something was in fact suggested (İ6).
              notes.push(
                incoming.source === 'set'
                  ? 'Kayıtlı setinin yerine paylaşılan set açıldı.'
                  : incoming.source === 'oneri' || incoming.suggested
                    ? 'Kayıtlı setinin yerine önerdiğimiz set açıldı.'
                    : 'Kayıtlı setinin yerine ' + incoming.name + ' ile yeni bir set açıldı.'
              );
              actions.push({ label: 'Kendi setime dön', act: 'kendi-setim' });
              // A shared ?set= stays in the address, so the saved record
              // stays too and a reload says the same thing again. ?bas= and
              // ?oneri= do not: the address turns into ?set=<this tray>, and
              // only a stored copy of it keeps a reload from presenting the
              // shopper's own tray as a shared link.
              if (incoming.source !== 'set') this.writeStorage();
            } else {
              this.writeStorage();
            }
          }
          if (incoming.announce) this.announce(incoming.announce());
        } else if (savedPicks) {
          var result = this.applySnapshot(saved);
          if (result.dropped) {
            notes.push(
              'Kayıtlı setindeki ' + result.dropped + ' ürün artık satışta değil.'
            );
            tone = 'warn';
          }
          if (result.applied) {
            // Plan §4.2 K: the returning visitor finds their tray, is told so,
            // and can start over in one tap.
            notes.push(
              'Kaldığın yerden devam: ' + result.applied + ' ürün · ' + money(this.displayTotal()) + '.'
            );
            actions.push({ label: 'Temizle', act: 'temizle' });
          }
          // The record is rewritten without whatever was dropped.
          if (result.dropped) this.writeStorage();
        }

        if (notes.length) this.showNotice({ tone: tone, text: notes.join(' '), actions: actions, closable: true });

        // ?bas= that matched nothing: the manifest only lists in-stock
        // candidates, so "not in this set" and "sold out" look the same from
        // here. The product's own JSON tells them apart; a sold-out product is
        // then named as such instead of being called a stranger (plan §2.2).
        if (params.bas && incoming && incoming.source === 'bas' && !incoming.snapshot) this.explainBas(params.bas);
      }

      /* hydrate() threw. The tray goes back to empty, the record that may
         have caused it is dropped (or the next visit would throw the same
         way), and a link is told it could not be read. */
      recoverFromBadState() {
        var params = {};
        try {
          params = this.readParams();
        } catch (e) {
          params = {};
        }
        var fromLink = !!(params.set || params.oneri || params.bas);
        try {
          this.clearTray();
          this.clearNotice();
          // A link is the likelier culprit and the shopper's own saved tray is
          // kept; with no link, the saved record is what failed.
          if (!fromLink) this.clearState();
        } catch (e) {
          /* nothing left to undo */
        }
        if (fromLink) {
          try {
            this.showNotice({
              tone: 'warn',
              text: 'Bu link okunamadı. Setini kutucuklardan kurabilirsin.',
              closable: true,
            });
          } catch (e) {
            /* the empty tray speaks for itself */
          }
        }
      }

      explainBas(handle) {
        var self = this;
        var h = String(handle).trim().toLowerCase();
        if (!/^[a-z0-9][a-z0-9-]*$/.test(h)) return;
        fetch(this.cartRoot() + 'products/' + h + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) {
            return r.ok ? r.json() : null;
          })
          .then(function (product) {
            if (!product || product.available !== false || !self.noticeEl) return;
            var text = self.noticeEl.querySelector('.xset__notice-text');
            if (!text || self.noticeEl.hidden) return;
            text.textContent = text.textContent.replace(
              'Bu ürün bu sette yok; benzerini seçebilirsin.',
              shortTitle(product.title) + ' şu an stokta yok; benzerini seçebilirsin.'
            );
          })
          .catch(function () {
            /* the generic sentence stays */
          });
      }

      readParams() {
        var out = {};
        var search = window.location.search || '';
        if (!search) return out;
        try {
          var params = new URLSearchParams(search);
          // `v`: the variant chosen on the product page a ?bas= came from.
          ['set', 'bas', 'oneri', 'v'].forEach(function (k) {
            var v = params.get(k);
            if (v) out[k] = String(v).trim();
          });
        } catch (e) {
          /* a malformed query is no query */
        }
        return out;
      }

      snapshotPicks(snap) {
        return ((snap && snap.slots) || []).filter(function (s) {
          return s && s.pick;
        }).length;
      }

      /* Same products, same number of each, in any boxes. */
      sameContent(a, b) {
        var ids = function (snap) {
          return ((snap && snap.slots) || [])
            .filter(function (s) {
              return s && s.pick && s.pick.variantId;
            })
            .map(function (s) {
              return String(s.pick.variantId);
            })
            .sort()
            .join(',');
        };
        var x = ids(a);
        return !!x && x === ids(b);
      }

      /* A candidate by variant id: the named step first, then any other
         drawn step (a link built before a product moved between collections
         still lands). Null when no step offers it in stock. */
      resolveVariant(key, variantId) {
        var vid = String(variantId || '');
        if (!/^\d+$/.test(vid)) return null;
        var self = this;
        var order = [];
        if (key && this.manifestIndex[key]) order.push(key);
        this.manifest.adimlar
          .slice()
          .sort(function (a, b) {
            return (b.tepside ? 1 : 0) - (a.tepside ? 1 : 0);
          })
          .forEach(function (s) {
            if (order.indexOf(s.anahtar) === -1) order.push(s.anahtar);
          });
        for (var i = 0; i < order.length; i += 1) {
          var entry = self.manifestIndex[order[i]];
          if (!entry || entry.step.gizli) continue;
          var c = entry.byVariant[vid];
          if (!c || !c.available) continue;
          var v = (c.variants || []).find(function (x) {
            return String(x.id) === vid;
          });
          if (!v || !v.available) continue;
          return { key: order[i], c: c, v: v };
        }
        return null;
      }

      /* The same, by handle — what ?bas= and a handle-based preset carry.
         The variant is `variantId` when that is an in-stock variant of the
         product (?bas=…&v=, the one chosen on the product page), else the
         product's only variant. A product with several variants and none
         named comes back with `v: null`: commit() never auto-picks between
         variants a shopper must choose, and neither does a link — the caller
         leaves that box for the shopper. */
      resolveHandle(key, handle, variantId) {
        var h = String(handle || '').trim().toLowerCase();
        if (!h) return null;
        var self = this;
        var order = [];
        if (key && this.manifestIndex[key]) order.push(key);
        this.manifest.adimlar
          .slice()
          .sort(function (a, b) {
            return (b.tepside ? 1 : 0) - (a.tepside ? 1 : 0);
          })
          .forEach(function (s) {
            if (order.indexOf(s.anahtar) === -1) order.push(s.anahtar);
          });
        for (var i = 0; i < order.length; i += 1) {
          var entry = self.manifestIndex[order[i]];
          if (!entry || entry.step.gizli) continue;
          var c = entry.byHandle[h];
          if (!c || !c.available) continue;
          var variants = c.variants || [];
          var v = null;
          if (variantId && /^\d+$/.test(String(variantId))) {
            v =
              variants.find(function (x) {
                return String(x.id) === String(variantId) && x.available;
              }) || null;
          }
          if (!v && variants.length === 1 && variants[0].available) v = variants[0];
          if (!v && variants.length > 1) {
            if (!variants.some(function (x) { return x.available; })) continue;
            return { key: order[i], c: c, v: null };
          }
          if (!v) continue;
          return { key: order[i], c: c, v: v };
        }
        return null;
      }

      /* Lays resolved picks onto boxes, under the set limits of plan §3.2:
         a default box of the category first, then a new box of it while the
         category has room. At most MAX_LINES in all, MAX_PER_PRODUCT of one
         product, categoryMax of one step (2 of a tray step) and MAX_EXTRAS
         off the tray together. `{limits: false}` lays them as they are —
         for a set read back out of the cart, which is not to be trimmed.
         Returns the record and how many picks did not fit. */
      placePicks(picks, setId, opts) {
        var self = this;
        var limits = !(opts && opts.limits === false);
        var slots = this.defaultSlots().map(function (s) {
          return { uid: s.uid, categoryKey: s.categoryKey, label: s.label, isExtra: false, pick: null };
        });
        var perCategory = {};
        var perProduct = {};
        var extras = 0;
        var extraSeq = 0;
        var placed = 0;
        var overflow = 0;
        var overLines = 0;

        picks.forEach(function (p) {
          var pid = self.colourCap ? self.groupOf(p.c.productId) : String(p.c.productId);
          // A link names a step; when its box is taken and a twin box (same
          // pool) is free, the pick goes there rather than over the limit.
          var key = p.key;
          var freeBox = function (k) {
            return slots.some(function (s) {
              return !s.isExtra && s.categoryKey === k && !s.pick;
            });
          };
          if (!freeBox(key)) {
            var twin = self.twinKeys(key).find(function (k) {
              return k !== key && freeBox(k);
            });
            if (twin) key = twin;
          }
          var onTray = self.isTrayStep(key);
          if (limits) {
            if (placed >= MAX_LINES) {
              overflow += 1;
              overLines += 1;
              return;
            }
            if (
              (perProduct[pid] || 0) >= MAX_PER_PRODUCT ||
              (perCategory[key] || 0) >= self.categoryMax(key) ||
              (!onTray && extras >= MAX_EXTRAS)
            ) {
              overflow += 1;
              return;
            }
          }
          var pick = {
            uid: p.c.ana || p.c.handle,
            handle: p.c.handle,
            variantId: String(p.v.id),
            kaynak: p.kaynak || 'secim',
          };
          var box = slots.find(function (s) {
            return !s.isExtra && s.categoryKey === key && !s.pick;
          });
          if (!box) {
            extraSeq += 1;
            var step = self.stepInfo(key);
            var panel = self.panelFor(key);
            box = {
              uid: key + '-ekstra-' + extraSeq,
              categoryKey: key,
              label: (panel && panel.dataset.categoryTitle) || (step && step.baslik) || key,
              isExtra: true,
              pick: null,
            };
            slots.push(box);
          }
          box.pick = pick;
          perProduct[pid] = (perProduct[pid] || 0) + 1;
          perCategory[key] = (perCategory[key] || 0) + 1;
          if (!onTray) extras += 1;
          placed += 1;
        });

        return {
          snapshot: { setId: setId || this.makeSetId(), slots: slots },
          placed: placed,
          overflow: overflow,
          overLines: overLines,
        };
      }

      /* ?set=tuvalet.<vid>~mama.<vid>~su.<vid> — the share format of plan
         §2.2, also what the ready-set carousel links with. */
      snapshotFromLink(raw) {
        var self = this;
        var tokens = String(raw)
          .split('~')
          .map(function (t) {
            return t.trim();
          })
          .filter(Boolean);
        var tooMany = Math.max(0, tokens.length - MAX_LINK_TOKENS);
        tokens = tokens.slice(0, MAX_LINK_TOKENS);

        var picks = [];
        var unknown = tooMany;
        // A token that is not "<adım>.<digits>" (or bare digits) never named a
        // product at all: a mangled or hand-typed link. Calling it "artık
        // satışta değil" would claim a product existed and sold out.
        var unreadable = 0;
        tokens.forEach(function (t) {
          var dot = t.lastIndexOf('.');
          var key = dot > 0 ? t.slice(0, dot) : '';
          var vid = dot > 0 ? t.slice(dot + 1) : t;
          if (!/^\d+$/.test(vid)) {
            unreadable += 1;
            return;
          }
          var hit = self.resolveVariant(key, vid);
          if (!hit) {
            unknown += 1;
            return;
          }
          picks.push({ key: hit.key, c: hit.c, v: hit.v, kaynak: 'secim' });
        });

        var placed = this.placePicks(picks);
        var notes = [];
        if (unreadable && !picks.length && !unknown) {
          notes.push('Bu link okunamadı. Setini kutucuklardan kurabilirsin.');
        } else if (unreadable) {
          notes.push('Linkteki ' + unreadable + ' öğe okunamadı.');
        }
        if (unknown) notes.push('Linkteki ' + unknown + ' ürün artık satışta değil.');
        if (placed.overflow) {
          notes.push(
            placed.overLines
              ? 'Linkte set sınırını aşan ' + placed.overflow + ' ürün vardı; ilk ' + MAX_LINES + ' ürün alındı.'
              : 'Linkte set sınırını aşan ' + placed.overflow + ' ürün vardı; fazlası alınmadı.'
          );
        }
        return { source: 'set', snapshot: placed.snapshot, notes: notes };
      }

      /* ------------------------------------------------------------ presets */

      /* Named presets ("En çok tercih edilen", "Premium", "Kameralı" — plan
         §7) are read from #xset-oneriler-<section id>:

           {"oneriler": [
             {"id": "en-cok-tercih", "baslik": "En çok tercih edilen",
              "kalemler": [{"adim": "tuvalet", "varyant": 123},
                           {"adim": "mama", "handle": "…"}]}
           ]}

         `varyant` wins over `handle`; `adim` is a hint, a variant is found in
         any drawn step. The section emits the element from its "oneri"
         blocks (product id, handle and first available variant per item,
         only builder candidates; `eksik` counts the out-of-stock ones).
         Without it there are no presets: ?oneri= is ignored (plan §2.2),
         ?bas= fills only its own box, Otomatik rolls on an empty tray. */
      loadPresets() {
        var el = document.getElementById('xset-oneriler-' + this.dataset.sectionId);
        var data = null;
        try {
          data = el ? JSON.parse(el.textContent) : null;
        } catch (e) {
          data = null;
        }
        var list = data && Array.isArray(data.oneriler) ? data.oneriler : [];
        this.presets = list.filter(function (p) {
          return p && p.id && Array.isArray(p.kalemler) && p.kalemler.length;
        });
      }

      presetById(id) {
        var want = String(id || '').trim().toLowerCase();
        if (!want) return null;
        return (
          (this.presets || []).find(function (p) {
            return String(p.id).toLowerCase() === want;
          }) || null
        );
      }

      /* The family's default suggestion ("En çok tercih edilen"): the first
         "oneri" block, in the merchant's order. */
      defaultPreset() {
        return (this.presets && this.presets[0]) || null;
      }

      presetPicks(preset, kaynak) {
        var self = this;
        var picks = [];
        var missing = 0;
        preset.kalemler.forEach(function (item) {
          var hit = item.varyant ? self.resolveVariant(item.adim, item.varyant) : self.resolveHandle(item.adim, item.handle);
          // A handle-only item of a multi-variant product names no variant:
          // not something a suggestion may choose for the shopper.
          if (!hit || !hit.v) {
            missing += 1;
            return;
          }
          picks.push({ key: hit.key, c: hit.c, v: hit.v, kaynak: kaynak });
        });
        return { picks: picks, missing: missing };
      }

      snapshotFromPreset(id) {
        var preset = this.presetById(id);
        if (!preset) return null;
        var res = this.presetPicks(preset, 'oneri');
        if (!res.picks.length) return null;
        var placed = this.placePicks(res.picks);
        var notes = [];
        // `eksik`: listed products Liquid found out of stock; `missing`: ones
        // that no longer resolve here (sold out since the page was cached).
        var gone = res.missing + (parseInt(preset.eksik, 10) || 0);
        if (gone) notes.push('Önerideki ' + gone + ' ürün şu an stokta yok.');
        var name = preset.baslik || 'Önerdiğimiz set';
        var self = this;
        return {
          source: 'oneri',
          snapshot: placed.snapshot,
          notes: notes,
          announce: function () {
            return name + ' setini önerdik: ' + self.pieceCount() + ' ürün. İstemediğini × ile çıkar.';
          },
        };
      }

      /* ?bas=<handle>: the product goes into its own box as "Senin seçimin";
         the other default boxes get the family's default preset as
         "Önerimiz" (plan §2.2, §4.2 B). Nothing is in the cart until the
         shopper presses the button. */
      snapshotFromBas(handle, variantId) {
        var hit = this.resolveHandle(null, handle, variantId);
        if (!hit) {
          var first = this.nextAdvanceTarget() || this.defaultSlots()[0];
          return {
            source: 'bas',
            snapshot: null,
            notes: ['Bu ürün bu sette yok; benzerini seçebilirsin.'],
            actions: first ? [{ label: phrase(first.label) + ' seç', act: 'adim-sec', uid: first.uid }] : [],
          };
        }

        // A product with several variants and none chosen on its page: its
        // box stays empty for the shopper to choose in (the notice opens it),
        // the other boxes are still suggested.
        var choose = !hit.v;
        var picks = choose ? [] : [{ key: hit.key, c: hit.c, v: hit.v, kaynak: 'bas' }];
        var suggested = [];
        var preset = this.defaultPreset();
        if (preset) {
          var self = this;
          var taken = {};
          taken[hit.key] = true;
          this.presetPicks(preset, 'oneri').picks.forEach(function (p) {
            // Only into the OTHER default boxes, one each.
            var box = self.defaultSlots().find(function (s) {
              return s.categoryKey === p.key;
            });
            if (!box || taken[p.key]) return;
            taken[p.key] = true;
            picks.push(p);
            suggested.push(box.label);
          });
        }
        var placed = this.placePicks(picks);
        var name = hit.c.kisaAd || shortTitle(hit.c.title);
        if (choose) {
          var box = placed.snapshot.slots.find(function (s) {
            return !s.isExtra && s.categoryKey === hit.key && !s.pick;
          });
          return {
            source: 'bas',
            hit: null,
            name: name,
            suggested: suggested.length,
            snapshot: placed.snapshot,
            notes: [name + ' için ' + this.lowerTr(hit.c.secenek || 'Seçenek') + ' seç.'],
            actions: box ? [{ label: 'Seç', act: 'adim-sec', uid: box.uid }] : [],
          };
        }
        return {
          source: 'bas',
          hit: hit,
          name: name,
          suggested: suggested.length,
          snapshot: placed.snapshot,
          notes: [],
          announce: function () {
            if (!suggested.length) return name + ' setinde. Kalan ürünleri kutucuklardan seç.';
            // Box titles inside a sentence: "Mama Kabı" → "mama kabı"
            // (Turkish-aware, so "I" becomes "ı").
            var lower = suggested.map(function (label) {
              var s = String(label || '').trim();
              try {
                return s.toLocaleLowerCase('tr-TR');
              } catch (e) {
                return s.toLowerCase();
              }
            });
            var list =
              lower.length === 1 ? lower[0] : lower.slice(0, -1).join(', ') + ' ve ' + lower[lower.length - 1];
            return name + ' setinde. Yanına en çok tercih edilenleri önerdik: ' + list + '. İstemediğini × ile çıkar.';
          },
        };
      }

      /* The saved record with the ?bas= product in it: unchanged when the
         tray already holds that product (any colour), else the product goes
         into its category's empty default box, or takes the place of what
         that box held. A product of an off-tray step (a dryer, a camera) has
         no default box: it gets an extra box of its own while the set limits
         of plan §3.2 leave room for one. Null when there is no room. */
      mergeBas(saved, hit) {
        var uid = hit.c.ana || hit.c.handle;
        // The very product (or variant) the shopper came from is already
        // there: nothing to do. Another COLOUR of it is not the same thing —
        // coming from the pink camera's page means the pink one — so that box
        // is the one replaced below.
        var has = (saved.slots || []).some(function (s) {
          return s && s.pick && (s.pick.handle === hit.c.handle || String(s.pick.variantId) === String(hit.v.id));
        });
        if (has) return { snapshot: saved, changed: false };
        var copy;
        try {
          copy = JSON.parse(JSON.stringify(saved));
        } catch (e) {
          return null;
        }
        // A product of twin boxes (one pool, two steps) belongs in either:
        // the box already holding its colour group, else an empty one of
        // them — never a second colour beside the first.
        var keys = this.twinKeys(hit.key);
        var boxes = (copy.slots || []).filter(function (s) {
          return s && !s.isExtra && keys.indexOf(s.categoryKey) !== -1;
        });
        var sameGroup = (copy.slots || []).find(function (s) {
          return s && s.pick && s.pick.uid === uid && keys.indexOf(s.categoryKey) !== -1;
        });
        var box =
          sameGroup ||
          boxes.find(function (s) {
            return !s.pick;
          }) ||
          boxes[0];
        if (!box) {
          box = this.extraBoxFor(copy, hit);
          if (!box) return null;
          copy.slots.push(box);
        }
        var replaced = '';
        if (box.pick) {
          var old = this.resolveVariant(box.categoryKey || hit.key, box.pick.variantId);
          replaced = old ? old.c.kisaAd || shortTitle(old.c.title) : phrase(box.label || '');
        }
        box.pick = { uid: uid, handle: hit.c.handle, variantId: String(hit.v.id), kaynak: 'bas' };
        return { snapshot: copy, changed: true, replaced: replaced };
      }

      /* A new extra box for `hit` in a saved record, or null when the record
         is already at a limit that box would break. */
      extraBoxFor(snap, hit) {
        var self = this;
        var step = this.stepInfo(hit.key);
        if (!step || step.gizli) return null;
        var picks = (snap.slots || []).filter(function (s) {
          return s && s.pick;
        });
        var inStep = picks.filter(function (s) {
          return s.categoryKey === hit.key;
        }).length;
        var extras = picks.filter(function (s) {
          return !self.isTrayStep(s.categoryKey);
        }).length;
        if (picks.length >= MAX_LINES || inStep >= this.categoryMax(hit.key)) return null;
        if (!this.isTrayStep(hit.key) && extras >= MAX_EXTRAS) return null;
        var seq = 0;
        (snap.slots || []).forEach(function (s) {
          var n = parseInt(String((s && s.uid) || '').split('-ekstra-')[1], 10);
          if (n > seq) seq = n;
        });
        var panel = this.panelFor(hit.key);
        return {
          uid: hit.key + '-ekstra-' + (seq + 1),
          categoryKey: hit.key,
          label: (panel && panel.dataset.categoryTitle) || step.baslik || hit.key,
          isExtra: true,
          pick: null,
        };
      }

      /* Empties the tray and lays a record onto it. The manifest is the
         authority, not the comparison cells: a pick comes back when it is
         still an in-stock candidate of its step — with or without a column on
         screen — and is priced from the manifest. A product that has since
         sold out, left the collection or become a set is simply not there,
         and resurrecting it would put an unbuyable line in the batch (the
         card-must-exist rule of complementary-look.js:143, applied to data). */
      applySnapshot(snap) {
        var self = this;
        this.clearTray();
        var applied = 0;
        var dropped = 0;
        if (snap.setId && String(snap.setId).indexOf(this.setIdPrefix) === 0) this.state.setId = snap.setId;

        (snap.slots || []).forEach(function (savedSlot) {
          if (!savedSlot || !savedSlot.pick) return;
          var pick = savedSlot.pick;

          var sel = self.selectionFor(savedSlot.categoryKey, pick.uid, pick.handle || pick.uid, pick.variantId);
          if (!sel) {
            dropped += 1;
            return;
          }
          sel.kaynak = pick.kaynak === 'bas' || pick.kaynak === 'oneri' ? pick.kaynak : 'secim';

          // Keep the column's controls in step with the restored pick when it
          // has one: the colour it shows and the variant it would add.
          var panel = self.panelFor(savedSlot.categoryKey);
          var card = panel && panel.querySelector('[data-xset-card][data-product-handle="' + pick.uid + '"]');
          if (card) {
            if (sel.handle !== pick.uid) {
              var colorBtn = card.querySelector('[data-xset-color][data-product-handle="' + sel.handle + '"]');
              if (colorBtn) self.pickColor(colorBtn);
            }
            card.dataset.activeVariantId = sel.variantId;
            card.dataset.price = String(sel.price);
          }

          var slot = self.slotByUid(savedSlot.uid);
          if (!slot && savedSlot.isExtra) {
            // An extra box only comes back for a category the page still offers.
            var step = self.stepInfo(savedSlot.categoryKey);
            if (!step || step.gizli) {
              dropped += 1;
              return;
            }
            slot = self.createExtraSlot(savedSlot.categoryKey, savedSlot.label || step.baslik);
            slot.uid = savedSlot.uid;
            slot.el.dataset.slotUid = savedSlot.uid;
            // createExtraSlot minted a uid and we just overwrote it, so the
            // counter no longer tracks what is on the tray. Push it past the
            // restored suffix or the next "+ Yeni ürün" re-mints a uid that is
            // already live — and slotByUid() is a find(), so the × on the new
            // box would empty the old one and a restore would collapse the two
            // picks into one.
            var restoredSeq = parseInt(String(savedSlot.uid).split('-ekstra-')[1], 10);
            if (restoredSeq > self.extraSeq) self.extraSeq = restoredSeq;
          }
          // A default box whose category is no longer drawn (0 candidates) has
          // no slot to land in — and one already holding something cannot
          // take a second pick.
          if (!slot || slot.selection) {
            dropped += 1;
            return;
          }
          slot.selection = sel;
          applied += 1;
        });

        return { applied: applied, dropped: dropped };
      }

      /* Every box empty, extra boxes gone. The set id is left alone: whoever
         calls this decides whether the tray is still the same set. */
      clearTray() {
        var self = this;
        this.state.slots.slice().forEach(function (slot) {
          slot.selection = null;
          slot.el.classList.remove('is-stale');
          if (slot.isExtra) {
            slot.el.remove();
            self.state.slots = self.state.slots.filter(function (s) {
              return s !== slot;
            });
          }
        });
        this.extraSeq = 0;
      }

      /* ---------------------------------------------------------- url, history */

      /* The tray as ?set=: each filled box as <adım>.<varyant>, in tray order. */
      setParam() {
        return this.filledSlots()
          .filter(function (s) {
            return s.selection.variantId;
          })
          .map(function (s) {
            return encodeURIComponent(s.categoryKey) + '.' + s.selection.variantId;
          })
          .join('~');
      }

      /* Writes the tray onto the BASE history entry. Never while the picker's
         own entry is on top (plan §2.2 step 4) — the write is then owed, and
         paid by the popstate that lands back on the base. Other query
         parameters (utm_*, variant) are kept as they were; ?bas= and ?oneri=
         have done their job once the tray exists and give way to ?set=.
         Built by hand rather than through URLSearchParams, which would write
         every "~" as %7E. */
      syncUrl() {
        if (!this.historyOn) return;
        if (this.modalEntry || this.closing) {
          this.urlDirty = true;
          return;
        }
        this.urlDirty = false;

        var loc = window.location;
        // `v` only ever rides along with ?bas= (the variant chosen on the
        // product page) and goes with it.
        var hadBas = /(^|[?&])bas=/.test(loc.search || '');
        var kept = (loc.search || '')
          .replace(/^\?/, '')
          .split('&')
          .filter(function (part) {
            if (!part) return false;
            var k = decodeURIComponent(part.split('=')[0].replace(/\+/g, ' '));
            return k !== 'set' && k !== 'bas' && k !== 'oneri' && !(hadBas && k === 'v');
          });
        var value = this.setParam();
        if (value) kept.push('set=' + value);
        var next = loc.pathname + (kept.length ? '?' + kept.join('&') : '') + (loc.hash || '');
        var current = loc.pathname + (loc.search || '') + (loc.hash || '');
        var state = history.state;
        var isBase = state && state.xset === 'base' && state.sid === this.dataset.sectionId;
        if (next === current && isBase) return;
        try {
          history.replaceState({ xset: 'base', sid: this.dataset.sectionId }, '', next);
        } catch (e) {
          /* a sandboxed frame may refuse; the tray is still in storage */
        }
      }

      /* The picker opened or closed — by any route. */
      onPickerToggle(event) {
        if (event.target !== this.picker || !this.historyOn) return;

        if (this.picker.open) {
          this.pushModalEntry();
          return;
        }

        // Closed by the shopper's own back: the entry is already gone.
        if (this.closingByPop) {
          this.closingByPop = false;
          return;
        }
        // Opened and closed again while the previous close was still going
        // back: nothing was pushed for it, so there is nothing to undo.
        if (this.closing) {
          this.pushQueued = false;
          return;
        }
        if (!this.modalEntry) return;

        // A pick, the ✕, Esc or the overlay: step back over the picker's
        // entry. The pick itself is already on the tray and in storage (so a
        // navigation inside this window loses nothing); only the address waits
        // for the popstate. If the browser never answers — some in-app
        // browsers swallow history.back() on a page they consider idle — the
        // timer stops waiting.
        this.closing = true;
        var self = this;
        clearTimeout(this.closingTimer);
        this.closingTimer = setTimeout(function () {
          if (self.closing) self.finishClosing();
        }, 1500);
        history.back();
      }

      pushModalEntry() {
        if (this.closing) {
          // history.back() is asynchronous; a push now would be undone by it.
          this.pushQueued = true;
          return;
        }
        if (this.modalEntry) return;
        try {
          history.pushState({ xset: 'modal', sid: this.dataset.sectionId }, '', window.location.href);
          this.modalEntry = true;
        } catch (e) {
          this.modalEntry = false;
        }
      }

      handlePopState(event) {
        if (this.closing) {
          this.finishClosing();
          return;
        }
        if (this.modalEntry) {
          // The shopper went back (button, swipe, Android back) with the
          // picker open: it closes, and nothing is picked.
          this.modalEntry = false;
          if (this.picker && this.picker.open) {
            this.closingByPop = true;
            this.picker.hide();
          }
          this.syncUrl();
          return;
        }
        // Forward onto a picker entry whose picker is closed: there is
        // nothing here to show, so step back over it.
        var state = event.state;
        if (state && state.xset === 'modal' && state.sid === this.dataset.sectionId && !(this.picker && this.picker.open)) {
          history.back();
        }
      }

      finishClosing() {
        clearTimeout(this.closingTimer);
        this.closing = false;
        this.modalEntry = false;
        if (this.pushQueued) {
          this.pushQueued = false;
          if (this.picker && this.picker.open) {
            this.pushModalEntry();
            return;
          }
        }
        this.syncUrl();
      }

      /* bfcache. The flags in memory describe the moment the page was left,
         not where history is now. */
      resumeFromCache() {
        clearTimeout(this.closingTimer);
        this.closing = false;
        this.pushQueued = false;
        if (this.picker && this.picker.open) {
          var onEntry = history.state && history.state.xset === 'modal';
          this.modalEntry = !!onEntry;
          // On its own entry the normal close goes back over it; on the base
          // it just closes.
          if (!onEntry) this.closingByPop = true;
          this.picker.hide();
        } else {
          this.modalEntry = false;
        }

        this.hydrating = true;
        var saved = this.readSaved();
        if (saved) this.applySnapshot(saved);
        else this.clearTray();
        this.hydrating = false;
        this.render();
        this.syncUrl();
        this.refreshCart();
      }

      /* ---------------------------------------------------------------- notice */

      /* The band under the status line (plan §2.2 "gelen link", §4.2 J/K):
         one sentence about the tray, up to three actions, an optional ✕.
         Spans and buttons only — theme.css hides an empty div. */
      showNotice(opts) {
        if (!this.noticeEl) return;
        var el = this.noticeEl;
        el.innerHTML = '';
        el.className = 'xset__notice xset__notice--' + (opts.tone || 'info');
        this.noticeKind = opts.kind || '';

        var text = document.createElement('span');
        text.className = 'xset__notice-text';
        text.textContent = opts.text || '';
        el.appendChild(text);

        (opts.actions || []).forEach(function (a) {
          if (a.note) {
            var note = document.createElement('span');
            note.className = 'xset__notice-muted';
            note.textContent = a.note;
            el.appendChild(note);
            return;
          }
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'xset__notice-action';
          b.setAttribute('data-xset-notice-action', a.act);
          if (a.uid) b.dataset.uid = a.uid;
          if (a.setId) b.dataset.setId = a.setId;
          b.textContent = a.label;
          el.appendChild(b);
        });

        if (opts.closable) {
          var close = document.createElement('button');
          close.type = 'button';
          close.className = 'xset__notice-close';
          close.setAttribute('data-xset-notice-action', 'kapat');
          close.setAttribute('aria-label', 'Bildirimi kapat');
          close.textContent = '×';
          el.appendChild(close);
        }
        this.placeNotice();
        el.hidden = false;
        if (!opts.silent) this.speak(opts.text, 3, true);
      }

      /* Where the band goes. Under the status line, except on a phone with
         something on the tray: there it goes UNDER the tray, beside the
         total it talks about, so a returning shopper's first screen is still
         the boxes and the bar (plan §10: at least 1.5 boxes above the bar at
         360×640 — a two-line band with its 44px buttons above the tray left
         none). An empty tray keeps it on top, where it explains the empty
         boxes. Decided per band, when it is shown. */
      placeNotice() {
        var el = this.noticeEl;
        if (!el || !this.noticeHomeParent) return;
        var phone = window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches;
        var below = !!(phone && this.trayWrapEl && this.pieceCount() > 0);
        el.classList.toggle('xset__notice--below', below);
        if (below) {
          if (el.previousElementSibling !== this.trayWrapEl) this.trayWrapEl.insertAdjacentElement('afterend', el);
        } else if (el.parentNode !== this.noticeHomeParent) {
          this.noticeHomeParent.insertBefore(el, this.noticeHomeNext && this.noticeHomeNext.parentNode === this.noticeHomeParent ? this.noticeHomeNext : null);
        }
      }

      clearNotice() {
        if (!this.noticeEl || this.noticeEl.hidden) return;
        this.noticeEl.hidden = true;
        this.noticeEl.innerHTML = '';
        this.noticeKind = '';
      }

      onNoticeAction(act, btn) {
        if (act === 'kapat') return this.clearNotice();
        if (act === 'temizle') {
          this.clearNotice();
          return this.resetAll();
        }
        if (act === 'kendi-setim') return this.restoreAltSaved();
        if (act === 'sepete-git') return this.openCart(btn);
        if (act === 'yeni-set') return this.startNewSet();
        if (act === 'sepet-seti') return this.loadCartSet(btn.dataset.setId);
        if (act === 'adim-sec') {
          var slot = this.slotByUid(btn.dataset.uid) || this.nextAdvanceTarget();
          this.clearNotice();
          if (slot) this.openPickerForSlot(slot, btn);
        }
      }

      restoreAltSaved() {
        var saved = this.altSaved;
        if (!saved) return this.clearNotice();
        this.altSaved = null;
        this.cancelAdvance();
        this.hydrating = true;
        var result = this.applySnapshot(saved);
        this.hydrating = false;
        this.hideError();
        this.writeStorage();
        this.clearNotice();
        this.announce(
          result.dropped
            ? 'Kayıtlı setin geri geldi; ' + result.dropped + ' ürünü artık satışta değil.'
            : 'Kayıtlı setin geri geldi.'
        );
        this.render();
        this.syncUrl();
      }

      /* "Yeni set kur": an empty tray under a NEW set id, so the set already
         in the cart stays exactly as it is. Refused at the family cap. */
      startNewSet() {
        if (this.cart.order.length >= MAX_SETS) {
          this.announce(this.capText());
          return;
        }
        this.cancelAdvance();
        this.reset();
        this.writeStorage();
        this.clearNotice();
        this.hideError();
        this.announce('Yeni set için kutucuklar boşaltıldı.');
        this.render();
        this.syncUrl();
      }

      /* ----------------------------------------------------------------- cart */

      buildItems(setId, only) {
        // Keyed on variant id and summed, not mapped. Shopify merges identical
        // id+properties lines server-side, so a naive map would let the same
        // product chosen in two boxes become one line of quantity 2 while the
        // tray still showed two filled boxes.
        var map = new Map();
        var setLabel = this.setLabel;
        setId = setId || this.state.setId;

        // `only`: just these boxes — the ones beside a ready set.
        (only || this.state.slots).forEach(function (slot) {
          var sel = slot.selection;
          if (!sel || !sel.variantId) return;
          var existing = map.get(sel.variantId);
          if (existing) {
            existing.quantity += 1;
            return;
          }
          map.set(sel.variantId, {
            id: parseInt(sel.variantId, 10),
            quantity: 1,
            properties: {
              // Underscore-prefixed: the machine key stays out of the shopper's
              // face in the cart and on the order. This is also THE discount
              // seam — a Function can group lines by it with no change here.
              _set_id: setId,
              // The slot, not just the category: an extra box carries
              // "<kategori>-ekstra-N", so a report can tell a skeleton choice
              // from something the shopper added on top.
              _set_adim: slot.uid,
              // Where the line came from, for analytics and a later "Seti
              // düzenle" link. Never read for pricing (plan §5.1 step 2).
              _set_kaynak: 'builder',
              // No underscore, so it shows in the cart, at checkout and on the
              // order. That visibility is the requirement.
              Set: setLabel,
            },
          });
        });

        return Array.from(map.values());
      }

      /* ------------------------------------------------------------ cart state */

      /* "Sepette en fazla 2 Kedi seti olabilir." (plan §4.1). Without a family
         name the sentence simply has none. */
      capText() {
        return 'Sepette en fazla ' + MAX_SETS + ' ' + (this.familyName ? this.familyName + ' seti' : 'set') + ' olabilir.';
      }

      cartRoot() {
        var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        return root.replace(/\/?$/, '/');
      }

      /* Reads /cart.js and applies it. A newer request supersedes an older
         one; a failed read changes nothing (the CTA then simply offers to
         add, and the add itself is guarded against a double). */
      refreshCart() {
        var self = this;
        var token = (this.cartToken = (this.cartToken || 0) + 1);
        return fetch(this.cartRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function (r) {
            return r.ok ? r.json() : null;
          })
          .then(function (cart) {
            if (cart && token === self.cartToken) self.applyCart(cart);
            return cart;
          })
          .catch(function () {
            return null;
          });
      }

      /* The sets THIS page put in the cart, grouped by _set_id: the variant
         ids each one holds (with quantity) as a sorted signature, its line
         keys for a replacement, and how many PRODUCTS it is. A ready set is
         one line standing for several products, so a line count would call a
         two-product ready set "1 ürün" (plan §4.2 K counts products). */
      readCartSets(cart) {
        var prefix = this.setIdPrefix;
        var readySets = this.readySets || [];
        var sets = {};
        var order = [];
        ((cart && cart.items) || []).forEach(function (item) {
          var p = item.properties || {};
          var id = p._set_id ? String(p._set_id) : '';
          if (!id || id.indexOf(prefix) !== 0) return;
          if (!sets[id]) {
            sets[id] = { id: id, lines: [], vids: [], count: 0, products: 0, price: 0 };
            order.push(id);
          }
          var s = sets[id];
          var q = item.quantity || 0;
          s.lines.push(item);
          for (var i = 0; i < q; i += 1) s.vids.push(String(item.variant_id));
          s.count += q;
          var per = 1;
          if (p._set_hazir) {
            var ready = readySets.find(function (r) {
              return r.handle === p._set_hazir || String(r.variantId) === String(item.variant_id);
            });
            if (ready && ready.ids && ready.ids.length) per = ready.ids.length;
          }
          s.products += q * per;
          s.price += item.final_line_price || 0;
        });
        order.forEach(function (id) {
          sets[id].sig = sets[id].vids.slice().sort().join(',');
        });
        return { loaded: true, sets: sets, order: order };
      }

      applyCart(cart) {
        this.cart = this.readCartSets(cart);
        var sets = this.cart.sets;

        // The tray IS a set that is already in the cart under another id —
        // storage was cleared, or another tab added it: adopt that id, so the
        // CTA says "Sepette ✓" instead of offering a second copy (plan §4.1).
        if (!sets[this.state.setId] && this.pieceCount() >= this.setFloor()) {
          var sig = this.traySig();
          var same = this.cart.order.find(function (id) {
            return sets[id].sig === sig;
          });
          if (same) {
            this.state.setId = same;
            this.writeStorage();
          }
        }

        // Mid-add the cart is between two truths (the old copy of a replaced
        // set is still being taken out); submit() speaks once it is done.
        if (!this.submitting) {
          this.syncAddedNotice();
          this.syncCartBand();
        }

        this.render();
      }

      /* İ6: the green "Setin sepette" band and the "Setin sepete eklendi."
         line describe the cart at the moment of the add. The cart can change
         under them — a line removed in the drawer, a quantity changed, the
         set taken out on another tab — while the tray does not, so neither
         the tray-bound status rule nor the CTA (which is re-derived anyway)
         would ever retire them. Checked against every cart read instead. */
      syncAddedNotice() {
        var ref = this.addedRef;
        if (!ref) return;
        var set = this.cart.sets[ref.id];
        if (set && set.sig === ref.sig) return;
        // Kept on what is there now, so a second change is reported too.
        this.addedRef = set ? { id: ref.id, sig: set.sig } : null;
        if (this.noticeKind === 'sepette') this.clearNotice();
        this.announce(
          set
            ? 'Sepetteki setin değişti: sepette bu setten ' + set.products + ' ürün var.'
            : 'Setin sepetten çıkarıldı.'
        );
      }

      /* Plan §4.2 K: sets from this page are in the cart and the tray is
         none of them. Opened once per page view, never over a band that is
         already talking; once open it follows the cart — counts, how many
         sets, gone when there is nothing left to point at. */
      syncCartBand() {
        var sets = this.cart.sets;
        var others = sets[this.state.setId] ? [] : this.cart.order;
        var showing = this.noticeKind === 'sepet' && this.noticeEl && !this.noticeEl.hidden;
        if (!showing) {
          if (this.cartBandShown || !others.length || (this.noticeEl && !this.noticeEl.hidden)) return;
          this.cartBandShown = true;
        } else if (!others.length) {
          this.clearNotice();
          return;
        }

        var family = this.familyName;
        var text;
        var actions = [];
        if (others.length === 1) {
          text = 'Sepetinde bir ' + (family ? family + ' setin' : 'setin') + ' var (' + sets[others[0]].products + ' ürün).';
          actions.push({ label: 'Düzenle', act: 'sepet-seti', setId: others[0] });
        } else {
          var counts = others.map(function (id) {
            return sets[id].products;
          });
          text =
            'Sepetinde ' + others.length + ' ' + (family ? family + ' setin' : 'setin') + ' var (' +
            counts.slice(0, -1).join(', ') + ' ve ' + counts[counts.length - 1] + ' ürün).';
          others.forEach(function (id, i) {
            actions.push({ label: i + 1 + '. seti düzenle', act: 'sepet-seti', setId: id });
          });
        }
        actions.push(
          this.cart.order.length >= MAX_SETS ? { note: this.capText() } : { label: 'Yeni set kur', act: 'yeni-set' }
        );
        this.showNotice({ tone: 'info', kind: 'sepet', text: text, actions: actions, closable: true });
      }

      /* What the tray would put in the cart, as the same signature: the
         ready set's one variant when the tray sells as one (Sade mod), else
         every box's variant. */
      traySig() {
        var st = this.readyState();
        if (st.match) {
          return [String(st.match.variantId)]
            .concat(
              st.rest.map(function (x) {
                return String(x.variantId);
              })
            )
            .sort()
            .join(',');
        }
        return this.filledSlots()
          .map(function (s) {
            return String(s.selection.variantId);
          })
          .sort()
          .join(',');
      }

      /* "Düzenle" on the cart band: the cart's set comes back onto the tray
         under its own id, so the CTA reads "Sepette ✓" and any change offers
         to update it in place. */
      loadCartSet(id) {
        var set = this.cart.sets[id];
        if (!set) return this.clearNotice();
        var self = this;
        var picks = [];
        set.lines.forEach(function (line) {
          var p = line.properties || {};
          var q = line.quantity || 1;
          if (p._set_hazir) {
            var ready = (self.readySets || []).find(function (s) {
              return s.handle === p._set_hazir || String(s.variantId) === String(line.variant_id);
            });
            if (!ready) return;
            ready.ids.forEach(function (pid) {
              var t = self.linkTarget(pid);
              var hit = t && self.resolveVariant(t.key, t.variantId);
              if (hit) picks.push({ key: hit.key, c: hit.c, v: hit.v, kaynak: 'secim' });
            });
            return;
          }
          var key = String(p._set_adim || '').split('-ekstra-')[0];
          var hit = self.resolveVariant(key, line.variant_id);
          for (var i = 0; hit && i < q; i += 1) picks.push({ key: hit.key, c: hit.c, v: hit.v, kaynak: 'secim' });
        });
        // The cart's set as it is: set limits are for what comes IN through
        // a link, and trimming a set that is already in the cart would make
        // the tray a different set than the one "Sepette ✓" vouches for.
        var placed = this.placePicks(picks, id, { limits: false });
        this.cancelAdvance();
        this.hydrating = true;
        this.applySnapshot(placed.snapshot);
        this.state.setId = id;
        this.addedRef = null;
        this.cartBandShown = true;
        this.hydrating = false;
        this.hideError();
        this.writeStorage();
        this.clearNotice();
        this.announce('Sepetteki setin tepside. Değiştirirsen sepetteki seti güncelleyebilirsin.');
        this.render();
        this.syncUrl();
      }

      openCart(trigger) {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.show === 'function') {
          try {
            drawer.show(trigger);
            return;
          } catch (e) {
            /* fall through to the cart page */
          }
        }
        var url = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_url) || this.cartRoot() + 'cart';
        window.location.href = url;
      }

      /* -------------------------------------------------------------- the CTA */

      hasStale() {
        return this.state.slots.some(function (s) {
          return s.selection && s.el.classList.contains('is-stale');
        });
      }

      /* Products that count toward the ladder: everything except a
         campaign-priced (set-disi) one. Only meaningful with the ladder on. */
      eligibleCount() {
        return this.filledSlots().filter(function (s) {
          return !s.selection.setDisi;
        }).length;
      }

      /* Not yet a set: too few pieces, or — for a family whose set is its
         boxes (requireAll) — a default box still empty. */
      incomplete() {
        return this.pieceCount() < this.setFloor() || (this.requireAll && !this.isComplete());
      }

      /* The piece count a set needs. Under requireAll the boxes ARE the set:
         never more than the boxes that are filled or can still be — a step
         whose models all sold out draws no box, and min_pieces must not
         then ask for a piece nobody can add. */
      setFloor() {
        if (!this.requireAll) return this.minPieces;
        var self = this;
        var boxes = this.defaultSlots().filter(function (s) {
          return s.selection || self.fillable(s);
        }).length;
        return Math.min(this.minPieces, boxes);
      }

      /* How many more the CTA asks for: pieces, or empty boxes when every
         box is required — whichever is more. */
      piecesMissing() {
        var short = this.setFloor() - this.pieceCount();
        if (this.requireAll) {
          var self = this;
          var empty = this.defaultSlots().filter(function (s) {
            return !s.selection && self.fillable(s);
          }).length;
          if (empty > short) short = empty;
        }
        return short;
      }

      /* Plan §4.1. Derived on every render, never stored. */
      ctaState() {
        if (this.submitting) return 'ekleniyor';
        var n = this.pieceCount();
        if (!n) return 'bos';
        if (this.hasStale()) return 'sorunlu';
        var inCart = this.cart.sets[this.state.setId];
        if (inCart && inCart.sig === this.traySig()) return 'sepette';
        if (this.incomplete()) return 'eksik';
        if (this.ladderOn && this.eligibleCount() < this.minPieces) return 'eksik-uygun';
        return inCart ? 'guncelle' : 'hazir';
      }

      /* What the cart will charge for this tray: the ready set's own price
         when it sells as one, else the boxes less any ladder saving. */
      displayTotal() {
        var st = this.readyState();
        if (st.match) return st.match.price + st.restSum;
        return this.total() - this.savings();
      }

      renderCta() {
        if (!this.submitEl) return;
        var state = this.ctaState();
        var n = this.pieceCount();
        var label = '';
        var priceInline = false;

        if (state === 'bos') {
          var first = this.nextAdvanceTarget();
          label = first ? phrase(first.label) + ' seç →' : 'Ürün seç →';
        } else if (state === 'eksik') {
          label = this.piecesMissing() + ' ürün daha seç →';
        } else if (state === 'eksik-uygun') {
          label = 'Set indirimi için ' + (this.minPieces - this.eligibleCount()) + ' uygun ürün daha seç →';
        } else if (state === 'sorunlu') {
          var stale = this.state.slots.filter(function (s) {
            return s.selection && s.el.classList.contains('is-stale');
          }).length;
          label = stale + ' ürünü değiştir';
        } else if (state === 'ekleniyor') {
          label = 'Ekleniyor…';
        } else if (state === 'sepette') {
          label = 'Sepette ✓ · Sepete git';
        } else if (state === 'guncelle') {
          label = 'Sepetteki seti güncelle';
          priceInline = true;
        } else {
          label = this.readyState().match ? 'Seti sepete ekle' : this.ctaText || 'Sepete ekle';
          priceInline = true;
        }

        // The price rides in its own span: inline after the label where the
        // plan's label carries it (HAZIR, GÜNCELLE), and on mobile — where
        // this button IS the sticky bar — as a second line in every state
        // with something on the tray, so the bar always shows the total
        // (plan İ7). CSS decides which; the text is the same.
        var price = n ? money(this.displayTotal()) : '';
        if (this.ctaLabelEl) {
          this.ctaLabelEl.textContent = label;
          if (this.ctaPriceEl) this.ctaPriceEl.textContent = price;
        } else {
          var text = this.submitEl.querySelector('.btn__text');
          if (text) text.textContent = priceInline && price ? label + ' · ' + price : label;
        }

        // The desktop bar's copy is the same button in every respect but its
        // place (plan §4.1: "Yapışkan barlar … her zaman sayfadaki CTA'nın
        // etiketini ve durumunu birebir yansıtır").
        var self = this;
        [this.submitEl, this.dbarBtn].forEach(function (btn) {
          if (!btn) return;
          btn.dataset.state = state;
          btn.classList.toggle('is-price-inline', priceInline && !!price);
          btn.classList.toggle('is-done', state === 'sepette');
          btn.classList.toggle('btn--loading', !!self.submitting);
          btn.classList.remove('is-disabled');
          btn.setAttribute('aria-disabled', state === 'ekleniyor' ? 'true' : 'false');
          if (state === 'ekleniyor') btn.setAttribute('aria-busy', 'true');
          else btn.removeAttribute('aria-busy');
        });
        setText(this.dbarLabelEl, label);
        setText(this.dbarPriceEl, price);

        // On a narrow phone a longer label can take the fixed bar to a second
        // line; the page's bottom padding follows it, or the bar would cover
        // the last of the content. Only when the height actually moved.
        if (this.summaryEl && this.summaryEl.offsetHeight !== this.barHeight) {
          this.barHeight = this.summaryEl.offsetHeight;
          this.measureBar();
        }
      }

      /* The CTA never refuses: it does the next useful thing. */
      onCta(trigger) {
        var state = this.ctaState();
        if (state === 'ekleniyor') return;

        if (state === 'bos' || state === 'eksik' || state === 'eksik-uygun') {
          var target = this.nextAdvanceTarget();
          if (target) return this.openPickerForSlot(target, trigger);
          return this.openPickerForNew(trigger);
        }

        if (state === 'sorunlu') {
          var slot = this.state.slots.find(function (s) {
            return s.selection && s.el.classList.contains('is-stale');
          });
          if (!slot) return;
          slot.el.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
          var t = slot.el.querySelector('[data-xset-slot-trigger]');
          if (t) t.focus({ preventScroll: true });
          return;
        }

        if (state === 'sepette') return this.openCart(trigger);

        return this.submit(trigger);
      }

      async preflight(options) {
        // The only way to name WHICH product died before the cart refuses the
        // whole batch. Shopify's multi-line /cart/add failure returns one human
        // sentence and no per-line map, and /cart.js is no help either — it
        // reports what is in the cart, not what is still buyable.
        var slots = ((options && options.slots) || this.filledSlots()).filter(function (s) {
          return s.selection && s.selection.variantId;
        });
        if (!slots.length) return [];

        var results = await Promise.all(
          slots.map(function (slot) {
            var root = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.root_url) || '/';
            var url = root.replace(/\/$/, '') + '/products/' + slot.selection.handle + '.js';
            return fetch(url)
              .then(function (r) {
                return r.ok ? r.json() : null;
              })
              .then(function (data) {
                if (!data) return null;
                var v = (data.variants || []).find(function (variant) {
                  return String(variant.id) === String(slot.selection.variantId);
                });
                return v && v.available ? null : slot;
              })
              .catch(function () {
                // A failed check is not evidence of a sold-out product. Staying
                // silent and letting the add proceed beats blocking a valid set
                // because one JSON request timed out.
                return null;
              });
          })
        );

        var offenders = results.filter(Boolean);
        this.markOffenders(offenders, options);
        return offenders;
      }

      markOffenders(offenders, options) {
        if (!this.errorEl) return;
        this.state.slots.forEach(function (s) {
          s.el.classList.remove('is-stale');
        });
        if (!offenders.length) {
          // Caller keeps its own message when preflight had nothing to add.
          if (!options || !options.preserveError) this.hideError();
          return;
        }

        offenders.forEach(function (slot) {
          slot.el.classList.add('is-stale');
        });

        this.errorEl.innerHTML = '';
        var p = document.createElement('p');
        p.className = 'xset__error-text';
        p.textContent =
          'Şu ürün' + (offenders.length > 1 ? 'ler' : '') + ' tükendi: ' +
          offenders.map(function (s) { return s.selection.title; }).join(', ') +
          '. Değiştirin veya çıkarın.';
        this.errorEl.appendChild(p);

        var actions = document.createElement('p');
        actions.className = 'xset__error-actions';
        offenders.forEach(function (slot) {
          var fix = document.createElement('button');
          fix.type = 'button';
          fix.className = 'btn btn--secondary xset__error-fix';
          fix.setAttribute('data-xset-fix', slot.uid);
          var span = document.createElement('span');
          span.className = 'btn__text';
          span.textContent = slot.selection.title + ' — Değiştir';
          fix.appendChild(span);
          actions.appendChild(fix);
        });
        this.errorEl.appendChild(actions);
        this.errorEl.hidden = false;
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      /* Plan §4.2 I: the tray is untouched, the sentence says so, and the way
         forward is one button with focus already on it. `cartState` is what
         undoPartialAdd found — the sentence only claims what was checked:
           clean   nothing of this set is in the cart (nothing was added)
           rolled  part of it went in and was taken back out
           partial part of it is in the cart and could not be taken out
           unknown the cart could not be read */
      showAddError(detail, cartState) {
        if (!this.errorEl) return;
        var state = cartState || 'clean';
        this.errorEl.innerHTML = '';
        var p = document.createElement('p');
        p.className = 'xset__error-text';
        var reason = detail ? ': ' + String(detail).replace(/[.\s]+$/, '') : '';
        var tail = {
          clean: 'Hiçbir ürün eklenmedi, setin olduğu gibi duruyor.',
          rolled: 'Sepete geçen ürünleri geri aldık; setin olduğu gibi duruyor.',
          partial: 'Setin bir kısmı sepette kaldı; sepeti kontrol et. Tekrar denersen sepetteki eksik set bununla değiştirilir.',
          unknown: 'Sepetin şu an okunamadı; sepeti kontrol et. Setin tepside olduğu gibi duruyor.',
        }[state] || '';
        p.textContent = (state === 'partial' ? 'Setin sepete tam eklenemedi' : 'Setin sepete eklenemedi') + reason + '. ' + tail;
        this.errorEl.appendChild(p);
        var actions = document.createElement('p');
        actions.className = 'xset__error-actions';
        var retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'btn btn--secondary xset__error-fix';
        retry.setAttribute('data-xset-retry', '');
        var span = document.createElement('span');
        span.className = 'btn__text';
        span.textContent = 'Tekrar dene';
        retry.appendChild(span);
        actions.appendChild(retry);
        if (state === 'partial' || state === 'unknown') {
          var go = document.createElement('button');
          go.type = 'button';
          go.className = 'btn btn--secondary xset__error-fix';
          go.setAttribute('data-xset-notice-action', 'sepete-git');
          var goText = document.createElement('span');
          goText.className = 'btn__text';
          goText.textContent = 'Sepete git';
          go.appendChild(goText);
          actions.appendChild(go);
        }
        this.errorEl.appendChild(actions);
        this.errorEl.hidden = false;
        retry.focus({ preventScroll: false });
        this.track('xset_add_error', { neden: detail ? String(detail).slice(0, 80) : 'baglanti', sepet: state });
      }

      hideError() {
        if (!this.errorEl) return;
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      sectionsToBundle() {
        // CartDrawer (cart.js:22) and MainCart (cart.js:915) both answer this
        // by pushing their own section ids. Bubbling from documentElement is
        // how every add-to-cart call site in this theme does it.
        var sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
        );
        return sections;
      }

      /* One /cart/add.js. Resolves to { ok, parsed } or { ok:false, parsed }
         for a refusal; throws only on a network failure. */
      async postAdd(items) {
        var body = JSON.stringify({
          items: items,
          sections: this.sectionsToBundle(),
          sections_url: window.location.pathname,
        });
        var response = await fetch(
          FoxTheme.routes.cart_add_url,
          Object.assign({}, FoxTheme.utils.fetchConfig('javascript'), { body: body })
        );
        var parsed = await response.json();
        return { ok: !parsed.status, parsed: parsed };
      }

      /* After a failed add: takes back whatever went into the cart under
         `setId` (a 422 can still have added most of the batch — see submit)
         and says what it found: 'clean' (nothing there), 'rolled' (something
         was, and is gone now), 'partial' (something is, and could not be
         taken out) or 'unknown' (the cart could not be read). Read straight
         from the response, not this.cart: a concurrent read (reconcile, the
         drawer's own) may supersede refreshCart's. */
      async undoPartialAdd(setId) {
        if (!setId) return 'clean';
        var cart = await this.refreshCart();
        if (!cart) return 'unknown';
        if (!this.readCartSets(cart).sets[setId]) return 'clean';
        var removed = await this.removeSet(setId);
        if (removed && removed.cart) {
          this.publishCart(removed.cart);
          return 'rolled';
        }
        var after = await this.refreshCart();
        if (!after) return 'unknown';
        return this.readCartSets(after).sets[setId] ? 'partial' : 'rolled';
      }

      /* Whether the cart holds exactly `items` under `setId` — a lost
         response that hid a partial add is not a set that went in. */
      setComplete(setId, items) {
        var set = this.cart.sets[setId];
        if (!set) return false;
        var want = [];
        items.forEach(function (item) {
          for (var i = 0; i < (item.quantity || 1); i += 1) want.push(String(item.id));
        });
        return set.sig === want.sort().join(',');
      }

      /* Tells the drawer and the header count about a cart this page changed
         outside an add. Guarded: a subscriber that throws is not ours to
         report (see submit). */
      publishCart(cart) {
        if (!cart) return;
        if (!cart.sections) cart.sections = {};
        try {
          if (window.FoxTheme && FoxTheme.pubsub) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
          }
        } catch (e) {
          /* the cart is right even when a listener is not */
        }
        this.applyCart(cart);
      }

      /* Whether lines with this set id are in the cart right now — the one
         question that makes retrying a network failure safe: an add whose
         response was lost may well have gone through. */
      async setInCart(setId) {
        var cart = await this.refreshCart();
        return !!(cart && this.cart.sets[setId]);
      }

      async submit(trigger) {
        if (this.submitting) return;
        if (this.incomplete()) return this.onCta(trigger);

        // Claimed BEFORE the cart read below, so a second press during that
        // read is the first `return` above.
        this.submitting = true;
        this.setSubmitting(true);
        this.hideError();
        var added = false;
        var attemptedId = null;

        try {
          // Everything below is decided on a cart read made NOW, never on the
          // one connectedCallback started: on a reload whose tray is already
          // in the cart that read may not have answered yet (this.cart is
          // still the empty placeholder), and adding then put every line in a
          // second time under the same _set_id — Shopify merged them into
          // quantity 2 and doubled the total. A cart that cannot be read is
          // not added to at all (plan §4.1, İ1).
          var cartNow = await this.refreshCart();
          if (!cartNow) cartNow = await this.refreshCart();
          if (!cartNow) {
            this.showAddError('sepetin şu an okunamadı');
            return;
          }
          // Applied here even when a newer read superseded this one: the
          // decisions below need a cart, and this one is seconds old at most.
          // applyCart also adopts the cart's id when the tray IS a set there.
          this.applyCart(cartNow);

          // The same unchanged set is never added twice (plan §4.1).
          var inCart = this.cart.sets[this.state.setId];
          if (inCart && inCart.sig === this.traySig()) {
            this.openCart(trigger);
            return;
          }

          // A NEW set while the cart already holds the family's limit (plan
          // §3.2, §4.1): refused here, with the way out, rather than by a
          // Function the shopper would only meet at checkout.
          if (!inCart && this.cart.order.length >= MAX_SETS) {
            this.showNotice({
              tone: 'warn',
              kind: 'sinir',
              text: this.capText() + ' Sepetteki bir seti çıkarınca bu seti ekleyebilirsin.',
              actions: [{ label: 'Sepete git', act: 'sepete-git' }],
              closable: true,
            });
            return;
          }

          this.clearNotice();

          // Sade mode, tray == an in-stock ready set: the cart gets that set,
          // one line at its own price (plan İ2). The components' own stock is
          // beside the point then; the set's is checked instead.
          var ready = this.readyState().match;
          var pageTotal = this.displayTotal();
          // GÜNCELLE: the set in the cart is replaced, not added to. The new
          // lines go in under a fresh id — under the old one Shopify would
          // merge any unchanged line into the line that is about to be
          // removed — and only then do the old lines go (plan §4.1, §9).
          var replacing = inCart || null;
          var setId = replacing ? this.makeSetId() : this.state.setId;
          var items;
          if (ready) {
            if (!(await this.readyStillAvailable(ready))) {
              ready.available = false;
              this.readyVersion += 1;
              this.announce('Bu set az önce tükendi. Ürünler kendi fiyatlarıyla eklenecek; tekrar sepete ekleyebilirsin.');
              return;
            }
            // What sits beside the set (an extra, a care product) goes as its
            // own lines under the same set id, checked like any box.
            var restSlots = this.readyState().restSlots || [];
            if (restSlots.length) {
              var restOffenders = await this.preflight({ slots: restSlots });
              if (restOffenders.length) {
                this.announce('Seçiminde tükenen ürün var. Değiştirip tekrar dene.');
                return;
              }
            }
            items = this.buildReadyItems(ready, setId).concat(restSlots.length ? this.buildItems(setId, restSlots) : []);
          } else {
            var offenders = await this.preflight();
            if (offenders.length) {
              this.announce('Seçiminde tükenen ürün var. Değiştirip tekrar dene.');
              return;
            }
            items = this.buildItems(setId);
          }
          if (!items.length) return;

          // Plan §4.2 I: one automatic retry, for a network failure or a
          // refusal (422) alike — never more than one in total.
          //
          // A REFUSAL IS NOT ALL-OR-NOTHING. When a line asks for more than
          // is in stock, /cart/add.js adds the most it can of that line AND
          // every other line of the batch, then still answers 422 ("Stok
          // durumu nedeniyle sepetinize yalnızca 35 ürün eklendi."). So after
          // any failed add the cart is read before anything else is decided,
          // and whatever went in under this set id is taken back out
          // (undoPartialAdd) — a retry then starts from a clean cart, and
          // "Hiçbir ürün eklenmedi" is only ever said when it is true.
          var result;
          var retried = false;
          var leftover = 'clean';
          // From here on something may be in the cart under this id.
          attemptedId = setId;
          try {
            result = await this.postAdd(items);
          } catch (networkError) {
            // Lost response: look before retrying — an add whose response was
            // lost may well have gone through.
            retried = true;
            if (await this.setInCart(setId)) {
              result = { ok: true, parsed: {}, recovered: true };
            } else {
              try {
                result = await this.postAdd(items);
              } catch (secondNetworkError) {
                result = { ok: false, parsed: {}, network: true };
              }
            }
          }
          if (result.recovered && !this.setComplete(setId, items)) {
            // The lost response hid a partial add: not a set to report as added.
            result = { ok: false, parsed: {} };
          }
          if (!result.ok) leftover = await this.undoPartialAdd(setId);
          if (!result.ok && !retried && leftover !== 'partial' && leftover !== 'unknown') {
            // The transient refusals — a stock reservation that just freed
            // up, a Function or app timing out — after a short pause, from a
            // cart that holds nothing of this set.
            retried = true;
            await new Promise(function (done) {
              setTimeout(done, 600);
            });
            var firstLeftover = leftover;
            try {
              var second = await this.postAdd(items);
              if (second.ok) result = second;
              else result = second.parsed && second.parsed.description ? second : result;
            } catch (retryNetworkError) {
              if ((await this.setInCart(setId)) && this.setComplete(setId, items)) {
                result = { ok: true, parsed: {}, recovered: true };
              }
            }
            if (!result.ok) {
              leftover = await this.undoPartialAdd(setId);
              // What the shopper is told is about the whole attempt: a part
              // that went in and came back out in either round is news.
              if (leftover === 'clean' && firstLeftover === 'rolled') leftover = 'rolled';
            }
          }

          if (!result.ok) {
            // The boxes STAY filled so the shopper can swap the offender
            // rather than rebuild the set — complementary-look.js:497
            // records the same reasoning.
            var parsed = result.parsed || {};
            this.showAddError(parsed.description, leftover);
            if (window.FoxTheme && FoxTheme.pubsub) {
              FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, { errors: parsed.description, message: parsed.message });
            }
            document.dispatchEvent(new CustomEvent('product-ajax:error', { detail: { error: parsed.description } }));
            // preserveError: preflight marks the offending boxes, but when it
            // cannot reproduce Shopify's objection (a cart-level quantity cap,
            // an app or Function rejection, a variant still reporting itself
            // available) it finds nothing — and clearing the banner there wiped
            // the only explanation the shopper ever gets.
            if (!ready) await this.preflight({ preserveError: true });
            return;
          }

          added = true;
          // PAST THE POINT OF NO RETURN. The items are in the cart; from here
          // nothing may report failure.
          this.state.setId = setId;
          this.writeStorage();
          var sections = result.parsed && result.parsed.sections;
          var removedOld = true;

          if (replacing) {
            removedOld = await this.removeSet(replacing.id);
          }

          var cartJson = removedOld && removedOld.cart ? removedOld.cart : null;
          if (!cartJson) {
            var cartResponse = await fetch(FoxTheme.routes.cart_url, FoxTheme.utils.fetchConfig());
            cartJson = await cartResponse.json();
            // MANDATORY. cart.js:212 reads event.cart.sections[sectionId] with
            // no guard, so publishing cartUpdate without this line throws and
            // the drawer stays broken for the rest of the session.
            cartJson.sections = sections || {};
          }

          // pubsub.publish (theme.js:296) calls every subscriber synchronously
          // with no try/catch of its own, and cart.js:212 reads
          // event.cart.sections[sectionId] unguarded — so one unhappy drawer
          // must not throw into the catch below and look like a failed add.
          try {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
            document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: result.parsed } }));
            this.dispatchEvent(
              new CustomEvent('xenon-set:added', {
                bubbles: true,
                detail: { setId: setId, items: items, hazirSet: ready ? ready.handle : null, guncellendi: !!replacing },
              })
            );
          } catch (notifyError) {
            // A broken listener is not the shopper's problem.
          }
          // The subscriber above already applied it; this covers a page with
          // no pubsub at all.
          this.applyCart(cartJson);
          // What the green band below vouches for. Every later cart read is
          // checked against it (syncAddedNotice) and the band goes the moment
          // the cart stops holding exactly this. The shopper has just seen
          // the set go in, so the "a set is in your cart" band (K) has
          // nothing to tell them for the rest of this page view.
          var mine = this.cart.sets[setId];
          this.addedRef = mine ? { id: setId, sig: mine.sig } : null;
          this.cartBandShown = true;
          // Adding to the cart is "the next action" that retires "Geri al"
          // (plan §7). Left up, it read as undoing the add — it only ever
          // reverted the tray, emptying it while the cart kept the whole set.
          this.autoUndo = null;

          // The tray STAYS (plan §4.1): the CTA now reads "Sepette ✓".
          this.announce(
            replacing && removedOld
              ? 'Sepetteki setin güncellendi.'
              : replacing
                ? 'Yeni setin sepete eklendi, eskisi sepetten çıkarılamadı. Sepeti kontrol et.'
                : (ready ? 'Set sepete eklendi: ' : 'Setin sepete eklendi: ') +
                  this.pieceCount() +
                  ' ürün, ' +
                  money(pageTotal) +
                  '.'
          );
          this.track('xset_add_to_cart', {
            set_id: setId,
            toplam: lira(pageTotal),
            eslesen_hazir_set: ready ? ready.handle : null,
            guncellendi: !!replacing,
            urunler: this.filledSlots().map(function (s) {
              return { adim: s.categoryKey, urun: s.selection.handle, varyant: s.selection.variantId, fiyat: lira(s.selection.price) };
            }),
          });
          this.showAddedNotice(ready, pageTotal);
          this.reconcile(setId, pageTotal);

          try {
            var drawer = document.querySelector('cart-drawer');
            if (drawer) drawer.show(trigger);
          } catch (drawerError) {
            /* the cart is correct even when the drawer will not open */
          }
        } catch (error) {
          // Only reachable before `added`; past that every step has its own
          // guard. Reporting a failure after a successful add is the one
          // outcome this method must never produce — and the cart is looked
          // at before the sentence claims anything about it.
          if (!added) {
            // Nothing was posted yet: nothing can be in the cart. Otherwise
            // ONLY this attempt's id is looked at — never the id of a set that
            // was already in the cart before (GÜNCELLE adds under a fresh one).
            var state = 'clean';
            if (attemptedId) {
              try {
                state = await this.undoPartialAdd(attemptedId);
              } catch (e) {
                state = 'unknown';
              }
            }
            this.showAddError('', state);
          }
        } finally {
          this.submitting = false;
          this.setSubmitting(false);
          this.render();
        }
      }

      /* The old copy of a replaced set, taken out line by line. Resolves to
         { cart } (with sections for the drawer) or false.

         NOT by the line keys read before the add: a line's key is a hash that
         includes its discount allocations, so the moment the new lines make
         an automatic discount (a campaign threshold) apply, the old lines'
         keys change — and /cart/update.js, handed a key it no longer knows,
         falls back to the variant id and zeroes the FIRST line of that
         variant, which is the new set's. Measured: the new Mama Kabı went and
         the old one stayed. So each removal goes by position in a cart read a
         moment ago, and the position is re-found in every response, checking
         the line's own _set_id each time. */
      async removeSet(setId) {
        var root = this.cartRoot();
        var sections = this.sectionsToBundle();
        var find = function (cart) {
          return ((cart && cart.items) || []).findIndex(function (item) {
            return item.properties && String(item.properties._set_id) === setId;
          });
        };
        try {
          var response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
          if (!response.ok) return false;
          var cart = await response.json();
          // A set is at most a handful of lines; the bound only stops a
          // misbehaving cart from looping.
          for (var guard = 0; guard < 12; guard += 1) {
            var idx = find(cart);
            if (idx < 0) break;
            response = await fetch(
              root + 'cart/change.js',
              Object.assign({}, FoxTheme.utils.fetchConfig('javascript'), {
                body: JSON.stringify({ line: idx + 1, quantity: 0, sections: sections, sections_url: window.location.pathname }),
              })
            );
            if (!response.ok) return false;
            cart = await response.json();
            if (!cart || cart.status) return false;
          }
          if (find(cart) >= 0) return false;
          if (!cart.sections) cart.sections = {};
          return { cart: cart };
        } catch (e) {
          return false;
        }
      }

      /* Plan §4.2 J. Green, near the CTA's own words: what went in, at what
         price, and the two ways on. */
      showAddedNotice(ready, pageTotal) {
        var n = this.pieceCount();
        var actions = [{ label: 'Sepete git', act: 'sepete-git' }];
        actions.push(
          this.cart.order.length >= MAX_SETS
            ? { note: this.capText() }
            : { label: 'Yeni set kur', act: 'yeni-set' }
        );
        this.showNotice({
          // Silent: the add's own sentence already says this, with the total.
          silent: true,
          tone: 'success',
          kind: 'sepette',
          text:
            'Setin sepette: ' +
            (ready && !this.readyState().rest.length ? 'tek set olarak, ' : '') +
            n +
            ' ürün · ' +
            money(pageTotal) +
            '.',
          actions: actions,
          closable: true,
        });
      }

      /* Plan §4.2 J, "mutabakat". The cart can apply a discount after the add
         (a campaign, an automatic discount), and the first read is not to be
         trusted while it recalculates: read now, at 300 ms and at 1000 ms, and
         speak only after the last read — up when the cart came out dearer
         than the page said, never silent when it came out cheaper. */
      reconcile(setId, pageTotal) {
        var self = this;
        var delays = [0, 300, 1000];
        var i = 0;
        clearTimeout(this.reconcileTimer);
        var step = function () {
          self.refreshCart().then(function () {
            i += 1;
            if (i < delays.length) {
              self.reconcileTimer = setTimeout(step, delays[i] - delays[i - 1]);
              return;
            }
            var set = self.cart.sets[setId];
            if (!set || self.noticeKind !== 'sepette' || !self.noticeEl) return;
            var diff = set.price - pageTotal;
            if (!diff) return;
            var text = self.noticeEl.querySelector('.xset__notice-text');
            if (!text) return;
            var extra =
              diff > 0
                ? 'Sepette tutar farklı hesaplandı: ' + money(set.price) + '. Sepetteki tutar geçerlidir.'
                : 'Sepette ek ' + money(-diff) + ' kampanya indirimi geldi.';
            text.textContent += ' ' + extra;
            // The notice band is not a live region (speak() is the one); a
            // price that moved is the one thing here a listener must hear.
            self.speak(extra, 3);
            self.track('xset_fiyat_farki', { set_id: setId, sayfa: lira(pageTotal), sepet: lira(set.price) });
          });
        };
        step();
      }

      setSubmitting(on) {
        if (!this.submitEl) return;
        // The width is held while the label reads "Ekleniyor…": a shorter
        // label would shrink the button under the shopper's thumb (CLS 0).
        // Both copies — the desktop bar's is the one pressed half the time.
        [this.submitEl, this.dbarBtn].forEach(function (btn) {
          if (!btn) return;
          if (on) {
            var w = btn.getBoundingClientRect().width;
            if (w) btn.style.minWidth = Math.ceil(w) + 'px';
          } else {
            btn.style.minWidth = '';
          }
          btn.classList.toggle('btn--loading', on);
        });
        this.renderCta();
      }

      reset() {
        this.clearTray();
        this.clearState();
        // The set that went in is not this tray's any more.
        this.addedRef = null;
        // A fresh id, so a second set built in the same session is a second set
        // in the cart rather than lines quietly joining the first one.
        this.state.setId = this.makeSetId();
        // Starting over is a new intent: the advance gets another chance.
        this.cancelAdvance();
        this.advanceDismissed = false;
        this.autoOpened = false;
        this.pendingFill = null;
        // …and Otomatik starts over: the first press loads the family's
        // suggestion again, nothing counts as shown, nothing to undo.
        this.autoUsed = false;
        this.autoShown = [];
        this.autoUndo = null;
        this.lastPresetId = null;
        this.autoManualSig = '';
        this.freeHint = null;
      }
    }
  );
})();
